/* eslint-disable max-len */
import { Close } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import {
  Box,
  Button,
  CircularProgress,
  createSvgIcon,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Link,
  Rating,
  Stack,
  styled,
  SwipeableDrawer,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

import LinearProgress, { linearProgressClasses } from "@mui/material/LinearProgress";
import Popover from "@mui/material/Popover";
import { useTheme } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  areaElementClasses,
  chartsGridClasses,
  ChartsReferenceLine,
  LineChart,
  lineElementClasses,
  markElementClasses,
} from "@mui/x-charts";
import moment, { duration } from "moment";
import React, { useCallback, useContext, useEffect, useId, useLayoutEffect, useState, useRef } from "react";
import { Map as MapboxMap, MapRef } from "react-map-gl";
import { generatePath, useLocation, useNavigate, useParams } from "react-router";
import { AddressLink, CountdownTimer, CurrencyLabel, EcoscoreIcon, PulseCircle, Text } from "../../components";
import { AreaGradient } from "../../components/LineChart";
import { AppRoutes } from "../../config";
import ApiRoutes from "../../config/api.routes";
import { MAPBOX_STYLE, MAPBOX_TOKEN } from "../../config/mapbox";
import { AppContext } from "../../contexts";
import useAuthorization from "../../contexts/useAuthorization";
import useMetaMask from "../../contexts/useMetaMask";
import {
  ercContractAddress,
  getContract,
  getMetaMaskSigner,
  getReadOnlyContract,
  ecoFuturesTokenContractAddress,
} from "../../services/contract.service";
import { useCountdown } from "../../services/CountDown";
import { useAxios } from "../../services/http.service";
import { describeChainError } from "../../services/challenge.service";
import { isUserRejection } from "../../services/relay.service";
import { AdvanceType, LandToken as LandTokenType, TokenMeta } from "../../types/LandToken";
import { UserRoles } from "../../types/User";
import {
  APP_TITLE,
  CURRENCY_NAME,
  EcoTokenStatus,
  TERM_UNIT,
  TIME_DISPLAY_UNIT,
  VERIFICATION_INTERVAL_UNIT,
  VERIFICATION_INTERVAL_VALUE,
  TREE_CURRENCY,
} from "../../utils/constants";
import { formatValue, toHectares } from "../../utils/formatValue";
import { getEffectiveSizeFromMeta, landStatusStates } from "../../utils/landMeta";
import {
  auctionCurrentPrice,
  checkTransactionConfirmationReceipt,
  consoleError,
  isSecondaryMarketLocked,
  numberWithCommas,
  sixDigits,
  sizing,
  toReadable,
} from "../../utils/methods";
import { resolveStandardStatus } from "../../utils/standard-status";
import { Attention } from "../BecomeTrust";
import { BackgroundDiv } from "../EcoBank";
import Auction from "./Auction";
import { ecoSystemTypes } from "./ecoSystemTypes";
import { ecoSystemSubTypeName } from "../Scan/constants";
import { BackButton } from "../EcoBank/components";
import ArrowForwardSharpIcon from "@mui/icons-material/ArrowForwardSharp";

import { AxisId } from "@mui/x-charts/models/axis";
import TreeClaimModalNew from "../Profile/TreeClaimModalNew";
import OfferCreationModal from "../Profile/OfferCreationModal";
import EFTActionButton from "./EFTActionButton";
import SettlementPanel from "../../components/Settlement/SettlementPanel";
import CovenantChallengePanel from "../../components/Settlement/CovenantChallengePanel";
import { fetchSettlement, Settlement } from "../../hooks/useSettlement";
import EditionBadgeCanvas from "../../components/EditionBadge";
import { IconContainer, CircularIcon } from "../Profile/Portal";
import { LandSummaryCardRoot, LandSummaryHeader, LandSummaryField, ContractIcon, SurveyIcon } from "./LandSummaryCard";
import LandDescriptionTiersCard from "./LandDescriptionTiersCard";
import LandDescriptionCard from "./LandDescriptionCard";
import RoleBanner from "./RoleBanner";
import MediaGalleryModal, { MediaFile } from "./MediaGalleryModal";
import LandActionPopup, { PopupCTA, PopupFootnote, PopupValuePill } from "./LandActionPopup";
import DigitFlip from "../../components/DigitFlip";
import StatusCycle from "../../components/StatusCycle";
import PdfPreview from "../../components/DigitalSignature/PdfPreview";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { ClickableContainer } from "../../utils/table-schemas";
import { MORPH_TO_DETAIL_KEY, playMorphFromRect, takeMorphRect } from "../../utils/flip-morph";
import { landImageSrc } from "../../utils/landImage";
import {
  ANIMATED_LAND_PLAYING_CLASS,
  AnimatedLandCanvas,
  AnimatedLandLayer,
  useAnimatedLand,
} from "../../components/AnimatedLand";
import { useCardTilt } from "../../hooks/useCardTilt";
import HoloProgressBar from "../../components/HoloProgressBar";
const landContentEmerge = keyframes`
  0%   { opacity: 0; transform: scale(0); }
  60%  { opacity: 1; }
  100% { opacity: 1; transform: scale(1); }
`;

// Entering from the bottom of the viewport (scrolling down): card rises into
// place from below (tucked under the fold).
const parallaxCardReveal = keyframes`
  0%   { opacity: 0; transform: translate3d(0, 48px, 0) scale(0.98); }
  100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
`;

// Entering from the top of the viewport (scrolling up): card stays hidden above
// the fold and drops down into place instead of rising from below.
const parallaxCardRevealFromTop = keyframes`
  0%   { opacity: 0; transform: translate3d(0, -48px, 0) scale(0.98); }
  100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
`;

// Embedded (Explore inline) desktop reveal: the hero/left column slides in from
// the right into its sticky position while the right-column sections stagger up.
const heroSlideIn = keyframes`
  0%   { opacity: 0; transform: translate3d(40px, 0, 0); }
  100% { opacity: 1; transform: translate3d(0, 0, 0); }
`;

// Hero card copy (name, location, stat labels, stat values): each line rises
// out of a soft blur one --stagger-stagger behind the line above — the
// transitions.dev "Texts reveal" recipe (#18); tunables are the `--stagger-*`
// vars on `:root` in App.tsx. The recipe toggles a class to run transitions;
// here it's a one-shot keyframe so it can start from mount (and restart on a
// key remount after the gallery flip). heroTextLineSx fills `backwards`, not
// `both`: the 100% frames equal the natural style, so once the run ends no
// translate3d layer or filter is left behind.
const heroTextRise = keyframes`
  0%   { opacity: 0; transform: translate3d(0, var(--stagger-distance), 0); }
  100% { opacity: 1; transform: translate3d(0, 0, 0); }
`;

// The recipe's blur lane, run as a separate animation on the same lines. It's a
// deliberate exception to the compositor-only rule (as in DigitFlip), kept small:
// 3px over a few short lines of text, once. Chrome can't composite a blur (it
// "may move pixels"), and in the same keyframe it would drag the rise onto the
// main thread with it — which is busy exactly then, committing the embedded
// payload. Split, the rise stays on the compositor and only the blur can stall.
const heroTextUnblur = keyframes`
  0%   { filter: blur(var(--stagger-blur)); }
  100% { filter: blur(0); }
`;

// ——— Embedded loading state (popup→detail morph + geo-ping skeleton panel) ———

// The right loading panel rises into place shortly after the hero morph starts.
const panelIn = keyframes`
  0%   { opacity: 0; transform: translate3d(0, 14px, 0); }
  100% { opacity: 1; transform: translate3d(0, 0, 0); }
`;

// Keyframes for the (currently disabled) sonar rings / orbiting arc / breathing
// core inside GeoPing — kept alongside their commented-out JSX for easy re-enable.
// const pingSonar = keyframes`
//   0%   { transform: scale(0.18); opacity: 0.9; }
//   80%  { opacity: 0; }
//   100% { transform: scale(1.15); opacity: 0; }
// `;

// const pingOrbit = keyframes`
//   to { transform: rotate(360deg); }
// `;

// const pingCoreBreathe = keyframes`
//   0%, 100% { transform: scale(1); }
//   50%      { transform: scale(1.35); }
// `;

const pingLabelFade = keyframes`
  0%, 100% { opacity: 0.45; }
  50%      { opacity: 0.9; }
`;

// Light band sweeping across a skeleton block (translate-only; the gradient
// itself is a static paint). Linear, like any shimmer: an eased loop surges
// mid-block and stalls at each edge, which reads as a stutter on repeat.
const skeletonShimmer = keyframes`
  to { transform: translateX(100%); }
`;

// Loader exit once the fetch resolves: the skeleton layer dissolves while the
// real sections stagger in underneath (parallaxCardReveal). transitions.dev
// skeleton-reveal timing (400ms ease-in-out) without the recipe's blur lane:
// this layer is the height of the whole column, and a blur there can't be
// composited — it would repaint that surface every frame, and drag the opacity
// onto the main thread with it, in exactly the frames that commit the payload.
const loadLayerOut = keyframes`
  to { opacity: 0; }
`;

// Single light sweep across the purple price box after the embedded reveal.
const priceSweep = keyframes`
  to { transform: translateX(110%); }
`;

// ——— Desktop left-column sizing ———
// The hero is a square whose side is width-derived (75% of the 50% column), so
// on wide-but-short laptops (MacBook Air: ~1440×740 usable viewport) the sticky
// left column (hero + purple price box) outgrows the viewport — and because a
// sticky element taller than its scrollport pins at the top and never travels,
// the price box's action button ends up cropped below the fold, unreachable by
// scrolling. Cap the square's side against the viewport height too: the 380px
// reserve covers the Explore header / overlay topOffset (~120px), the hero's
// clamped top margin (≤40px), and a worst-case price box with action buttons
// (~200px) plus slack. On tall screens the 75% term wins and nothing changes;
// the 280px floor keeps the hero from degenerating in very short windows.
// Applied identically to the hero card, the deep-link skeleton hero, and the
// purple price box so all three stay edge-aligned in every state.
const LEFT_COL_CARD_WIDTH_MD = "min(75%, max(280px, calc(100dvh - 380px)))";
// The hero's 8.5% top margin resolves against column *width*, so it grows
// exactly when vertical space is scarcest — clamp it on desktop. Shared with
// the right column's first card, which must stay top-aligned with the hero.
const HERO_TOP_MARGIN_MD = "min(8.5%, 40px)";

// Refresh cadence while an expired auction is being settled by the backend
// workers (see the auction-settling effect). They run every minute, so 15s
// picks the new status up within a tick or two; the timeout is several times
// the expected wait — past it something is wrong server-side and there is no
// point hammering the API from an idle tab.
const AUCTION_SETTLE_POLL_MS = 15_000;
const AUCTION_SETTLE_POLL_TIMEOUT_MS = 5 * 60 * 1000;

// ——— Hero card gallery flip ———
const HERO_FLIP_MS = 850;
// The hero's animated image starts after heroSlideIn (900ms) has played, so its
// download/decode never competes with the entrance.
const HERO_ANIMATION_START_MS = 900;
const HERO_FLIP_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
// ——— Hero card copy reveal (heroTextRise) ———
// Wait before the first line rises. Embedded: the card is mid-morph / slide-in
// when it mounts, so the copy lands just after it. Standalone: lets
// landContentEmerge settle first (the same 750ms the parallax observer waits).
// Flip back: HeroFlipFrontOverlays' fade-in delay, so the lines rise as the
// overlay returns.
const HERO_TEXT_REVEAL_DELAY_MS = 350;
const HERO_TEXT_STANDALONE_DELAY_MS = 750;
const HERO_OVERLAY_RETURN_DELAY_MS = 200;

// sx for one line of hero copy; `line` is its row, top to bottom. A null delay
// holds the copy at rest with no animation (standalone, until the emerge starts
// — the whole column is invisible before that anyway).
const heroTextLineSx = (delayMs: number | null, line: number) =>
  delayMs === null
    ? {}
    : {
        animation: [
          `${heroTextRise} var(--stagger-dur) var(--stagger-ease) backwards`,
          `${heroTextUnblur} var(--stagger-dur) var(--stagger-ease) backwards`,
        ].join(", "),
        // One delay, repeated across both animations in the list.
        animationDelay: `calc(${delayMs}ms + var(--stagger-stagger) * ${line})`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      };
// ——— Hero card pointer tilt ———
// Peak lean, in degrees, with the pointer on a card edge. transitions.dev keeps
// this in JS rather than CSS; 10–16° reads as subtle (its demo goes to ~40°),
// and the hero is big enough that the low end already sells the depth. The
// timing tunables are `--tilt-*` on `:root` in App.tsx.
const HERO_TILT_MAX_DEG = 10;
// Media-stack tiles stagger in after the detail stack fades out.
// Compositor-only: opacity + translate3d, no filter/blur to avoid painting
// four simultaneous blur layers on mobile.

export const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 14,
  borderRadius: 20,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "black",
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    // This transition makes the bar grow smoothly
    transition: "transform .4s linear",
    background: "linear-gradient(90deg, #CCC 0%, #D1EFFF 100%)",
    ...theme.applyStyles("dark", {
      backgroundColor: "#308fe8",
    }),
  },
}));

type LandPageProps = {
  details?: boolean;
  trust?: boolean;
  link?: string;
  // Embedded (inline-on-Explore) mode. When set, the detail body is rendered
  // over a host-owned live map instead of mounting its own LandMapBackground,
  // and the token is supplied/identified by props rather than the route param.
  embedded?: boolean;
  tokenId?: string;
  // Lightweight token from the map endpoint — used for the instant preview/
  // skeleton in embedded mode before the full detail payload is fetched.
  previewToken?: any;
  onClose?: () => void;
  // Called when the user clicks a land-feature tag in embedded mode so the
  // host Explore page can run the search without a full navigation.
  onSearch?: (term: string) => void;
  revealOrigin?: { x: number; y: number } | null;
  // Pixels from the top of the viewport the embedded scroll overlay starts at
  // (so it sits below the Explore header).
  topOffset?: number;
};

type ZoneScoreMarker = {
  zoneName: string;
  score: number;
};

type IndicatorParameterDescription = {
  value: number;
  description: string;
};

const SCORE_MIN = 0;
const SCORE_MAX = 5;

const clampZoneScore = (score: number): number => Math.min(SCORE_MAX, Math.max(SCORE_MIN, score));

const normalizeZoneScores = (zoneScores: unknown): ZoneScoreMarker[] => {
  if (!Array.isArray(zoneScores)) {
    return [];
  }

  return zoneScores
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const zoneName = String((item as { zoneName?: unknown }).zoneName || "").trim();
      const score = Number((item as { score?: unknown }).score);

      if (!zoneName || !Number.isFinite(score)) {
        return null;
      }

      return {
        zoneName,
        score: clampZoneScore(score),
      };
    })
    .filter((item): item is ZoneScoreMarker => item !== null);
};

type ZoneScoreChip = ZoneScoreMarker & {
  index: number;
  markerTag: string;
};

type ZoneScoreMarkerGroup = {
  score: number;
  markerTags: string[];
  zoneIndexes: number[];
};

// Extracted components for Fast Refresh compatibility
const PopupContent = ({
  type,
  value,
  level,
  description,
  parameterDescriptions,
  zoneScores,
  onClose,
  isMobileView,
}: {
  type: string;
  value: number;
  level: string;
  description: string;
  parameterDescriptions?: IndicatorParameterDescription[];
  zoneScores?: ZoneScoreMarker[];
  onClose: () => void;
  isMobileView: boolean;
  navigator: any;
}) => {
  const safeZoneScores = Array.isArray(zoneScores) ? zoneScores : [];
  const zoneScoreChips: ZoneScoreChip[] = safeZoneScores.map((zoneScore, index) => ({
    ...zoneScore,
    index,
    markerTag: String.fromCharCode(65 + index),
  }));

  const defaultActiveZoneIndex =
    zoneScoreChips.length > 0
      ? zoneScoreChips.reduce(
          (bestIndex, current, index, items) => (current.score > items[bestIndex].score ? index : bestIndex),
          0
        )
      : -1;

  const [selectedZoneIndex, setSelectedZoneIndex] = useState<number | null>(null);

  useEffect(() => {
    setSelectedZoneIndex(null);
  }, [zoneScores]);

  const activeZoneIndex =
    selectedZoneIndex !== null && selectedZoneIndex >= 0 && selectedZoneIndex < zoneScoreChips.length
      ? selectedZoneIndex
      : defaultActiveZoneIndex;

  const safeParameterDescriptions = Array.isArray(parameterDescriptions)
    ? parameterDescriptions.filter(
        (parameter): parameter is IndicatorParameterDescription =>
          Number.isFinite(Number(parameter?.value)) && typeof parameter?.description === "string"
      )
    : [];

  const getDescriptionByScore = (score: number): string => {
    if (safeParameterDescriptions.length === 0 || !Number.isFinite(score)) {
      return "";
    }

    const exactMatch = safeParameterDescriptions.find((parameter) => Number(parameter.value) === score);
    if (exactMatch?.description) {
      return exactMatch.description;
    }

    const nearestParameter = safeParameterDescriptions.reduce((closest, current) => {
      const closestDistance = Math.abs(Number(closest.value) - score);
      const currentDistance = Math.abs(Number(current.value) - score);
      return currentDistance < closestDistance ? current : closest;
    }, safeParameterDescriptions[0]);

    return nearestParameter?.description || "";
  };

  const selectedZoneScore =
    selectedZoneIndex !== null && selectedZoneIndex >= 0 && selectedZoneIndex < zoneScoreChips.length
      ? zoneScoreChips[selectedZoneIndex].score
      : null;

  const activeDescription =
    selectedZoneScore !== null ? getDescriptionByScore(selectedZoneScore) || description : description;

  const markerGroupsMap = new Map<number, ZoneScoreMarkerGroup>();

  zoneScoreChips.forEach((zoneScoreChip) => {
    const normalizedScore = clampZoneScore(zoneScoreChip.score);
    const existingMarkerGroup = markerGroupsMap.get(normalizedScore);

    if (existingMarkerGroup) {
      existingMarkerGroup.markerTags.push(zoneScoreChip.markerTag);
      existingMarkerGroup.zoneIndexes.push(zoneScoreChip.index);
      return;
    }

    markerGroupsMap.set(normalizedScore, {
      score: normalizedScore,
      markerTags: [zoneScoreChip.markerTag],
      zoneIndexes: [zoneScoreChip.index],
    });
  });

  const markerGroups = Array.from(markerGroupsMap.values());

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: isMobileView ? "24px 16px 32px 16px" : "32px 24px",
        position: "relative",
        minHeight: isMobileView ? "40vh" : "auto",
      }}
    >
      {/* Swipe indicator - only for mobile */}
      {isMobileView && (
        <Box
          sx={{
            width: "40px",
            height: "4px",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            borderRadius: "2px",
            alignSelf: "center",
            marginBottom: "16px",
          }}
        />
      )}

      {/* Close button */}
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          position: "absolute",
          top: isMobileView ? "20px" : "16px",
          right: "16px",
          zIndex: 1,
        }}
      >
        <CloseIcon sx={{ fontSize: 24 }} />
      </IconButton>

      {/* Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          paddingRight: "40px",
        }}
      >
        <IconContainer>
          <CircularIcon>
            <InfoIcon fontSize="large" color="primary" />
          </CircularIcon>
        </IconContainer>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }}
        >
          <Stack spacing={2}>
            <Typography
              variant="subtitle2"
              sx={{
                paddingBottom: { xs: "16px", md: "24px" },
                fontWeight: 700,
                textTransform: "capitalize",
                fontSize: 14,
                lineHeight: 1.5,
                color: "#999",
              }}
            >
              {type}
            </Typography>
            <Box
              sx={{
                paddingBottom: { xs: "16px", md: "24px" },
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: 20,
                  lineHeight: 1.5,
                  color: "#000",
                  textTransform: "capitalize",
                }}
              >
                {level}
              </Typography>
              <Rating value={value} readOnly max={5} size="small" />
            </Box>
            {safeZoneScores.length > 0 && (
              <Box sx={{ paddingBottom: { xs: "16px", md: "24px" } }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingBottom: "6px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      lineHeight: 1.2,
                      color: "#8E9279",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.14em",
                    }}
                  >
                    Pioneer
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      lineHeight: 1.2,
                      color: "#8E9279",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.14em",
                    }}
                  >
                    Climax
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "relative",
                    pt: "8px",
                    overflow: "visible",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      height: "6px",
                      borderRadius: "999px",
                      background: "linear-gradient(90deg, #A7C767 0%, #355F0F 100%)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage:
                          "repeating-linear-gradient(to right, transparent, transparent calc(20% - 1px), rgba(255,255,255,0.42) calc(20% - 1px), rgba(255,255,255,0.42) 20%)",
                        pointerEvents: "none",
                      }}
                    />
                  </Box>

                  {markerGroups.map((markerGroup) => {
                    const markerLeftPercent = (markerGroup.score / SCORE_MAX) * 100;
                    const isActive = markerGroup.zoneIndexes.includes(activeZoneIndex);

                    return (
                      <Box
                        key={`marker-group-${markerGroup.score}-${markerGroup.markerTags.join("-")}`}
                        sx={{
                          position: "absolute",
                          left: `${markerLeftPercent}%`,
                          top: "5px",
                          transform: "translateX(-50%)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            border: isActive ? "2px solid #2D4D07" : "2px solid #C8C8C8",
                            backgroundColor: isActive ? "#FDF9ED" : "#D9D9D9",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            mt: "8px",
                            color: isActive ? "#2D4D07" : "#B3B3B3",
                            fontSize: 11,
                            lineHeight: 1,
                            fontWeight: 700,
                          }}
                        >
                          {markerGroup.markerTags.join(",")}
                        </Typography>
                      </Box>
                    );
                  })}

                  <Box
                    sx={{
                      display: "flex",
                      gap: "8px",
                      mt: "30px",
                      flexWrap: "wrap",
                    }}
                  >
                    {zoneScoreChips.map((zoneScoreChip) => {
                      const isActive = zoneScoreChip.index === activeZoneIndex;
                      const formattedPercentage = Math.round((clampZoneScore(zoneScoreChip.score) / SCORE_MAX) * 100);

                      return (
                        <Box
                          key={`zone-chip-${zoneScoreChip.zoneName}-${zoneScoreChip.score}-${zoneScoreChip.index}`}
                          component="button"
                          type="button"
                          onClick={() => setSelectedZoneIndex(zoneScoreChip.index)}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "7px",
                            borderRadius: "13px",
                            px: "10px",
                            py: "6px",
                            backgroundColor: isActive ? "#355F0F" : "#EFEFEF",
                            color: isActive ? "#FDF9ED" : "#666",
                            border: "none",
                            cursor: "pointer",
                            font: "inherit",
                            textAlign: "left",
                            transition: "background-color 0.2s ease, color 0.2s ease",
                          }}
                        >
                          <Box
                            sx={{
                              width: "12px",
                              height: "12px",
                              borderRadius: "4px",
                              backgroundColor: isActive ? "rgba(255,255,255,0.18)" : "#DCDCDC",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              sx={{
                                width: "4px",
                                height: "4px",
                                borderRadius: "2px",
                                backgroundColor: isActive ? "#FDF9ED" : "#A8A8A8",
                              }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            {zoneScoreChip.markerTag}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              lineHeight: 1,
                              textTransform: "uppercase",
                              maxWidth: "130px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {zoneScoreChip.zoneName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            {formattedPercentage}%
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            )}
            <Box sx={{ paddingBottom: { xs: "16px", md: "24px" } }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: "#666",
                }}
              >
                {activeDescription.split("\n").map((line, index) => (
                  <span key={index}>
                    {line}
                    {index < activeDescription.split("\n").length - 1 && <br />}
                  </span>
                ))}
              </Typography>
            </Box>
            <Box sx={{ paddingTop: { xs: "0px", md: "0px" } }}>
              <Typography variant="body2" sx={{ fontSize: 14, lineHeight: 1.5, color: "#959A80" }}>
                To learn about the Ecoscore matrix, read our{" "}
                <Link
                  sx={{
                    fontWeight: 700,
                    color: "#959A80",
                    textDecorationColor: "#959A80",
                    "&:hover": {
                      textDecorationColor: "#959A80",
                      color: "#959A80",
                      cursor: "pointer",
                    },
                  }}
                  onClick={() => window.open(AppRoutes.About.path + "?blog=blog-article-2", "_blank")}
                >
                  Decoding the EcoScore
                </Link>{" "}
                article.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

const InfoPopup = ({
  type,
  value,
  level,
  description,
  parameterDescriptions,
  zoneScores,
  open,
  onClose,
  navigator,
}: {
  type: string;
  value: number;
  level: string;
  description: string;
  parameterDescriptions?: IndicatorParameterDescription[];
  zoneScores?: ZoneScoreMarker[];
  open: boolean;
  onClose: () => void;
  navigator: any;
}) => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));

  // Render SwipeableDrawer for mobile, Dialog for desktop
  return isMobileView ? (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      sx={{
        "& .MuiDrawer-paper": {
          borderTopLeftRadius: "20px",
          borderTopRightRadius: "20px",
          backgroundColor: "#fff",
          maxHeight: "80vh",
          overflow: "visible",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      }}
    >
      <PopupContent
        type={type}
        value={value}
        level={level}
        description={description}
        parameterDescriptions={parameterDescriptions}
        zoneScores={zoneScores}
        onClose={onClose}
        isMobileView={isMobileView}
        navigator={navigator}
      />
    </SwipeableDrawer>
  ) : (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "20px",
          backgroundColor: "#fff",
          maxWidth: "600px",
          overflow: "visible",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      }}
    >
      <PopupContent
        type={type}
        value={value}
        level={level}
        description={description}
        parameterDescriptions={parameterDescriptions}
        zoneScores={zoneScores}
        onClose={onClose}
        isMobileView={isMobileView}
        navigator={navigator}
      />
    </Dialog>
  );
};

// The background map's own mapbox-gl: the leading `!` bypasses CRA's loaders, which makes it a
// second, untranspiled ~1.8MB copy next to the one src/index.tsx configures. It's loaded on
// demand, when a LandMapBackground first mounts. As a static import it sat in the main bundle,
// which every page downloads and evaluates before its first paint (≈500KB gzip of the LCP
// critical path on `/` and `/explore`, neither of which shows this map).
let landMapLib: Promise<any> | null = null;
// The loaded module, for the identity check in handleLoad (always set before a map exists).
let mapboxgl: any = null;
const loadLandMapLib = () => {
  if (!landMapLib) {
    // eslint-disable-next-line import/no-webpack-loader-syntax
    // @ts-expect-error - the leading `!` is a webpack loader-bypass prefix; TS can't resolve it
    landMapLib = import("!mapbox-gl").then((module) => {
      mapboxgl = module.default;
      // Production-only workaround for the Mapbox WebWorker bundle parse error.
      // CRA's webpack runs Babel over mapbox-gl's prebuilt worker, breaking it.
      // Loading the worker from the official CDN bypasses our build pipeline entirely.
      // See: https://docs.mapbox.com/mapbox-gl-js/guides/install/#transpiling
      if (mapboxgl && !mapboxgl.workerUrl) {
        mapboxgl.workerUrl = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl-csp-worker.js";
        mapboxgl.workerCount = 1;
        console.log("[LandMap] workerUrl override applied", {
          workerUrl: mapboxgl.workerUrl,
          workerCount: mapboxgl.workerCount,
          mapboxglVersion: mapboxgl.version,
        });
      } else {
        console.log("[LandMap] workerUrl override skipped", {
          hasMapboxgl: !!mapboxgl,
          existingWorkerUrl: mapboxgl?.workerUrl,
        });
      }
      return mapboxgl;
    });
  }
  return landMapLib;
};

// Zoom level the map settles at — used both as the flyTo target and, when
// arriving from Explore, as the initial zoom so no animation is needed.
const LAND_MAP_TARGET_ZOOM = 14;

const LandMapBackground: React.FC<{
  lon: number;
  lat: number;
  onArrive?: (origin: { x: number; y: number }) => void;
  skipFlyTo?: boolean;
}> = ({ lon, lat, onArrive, skipFlyTo = false }) => {
  const mapRef = useRef<MapRef>();
  const hasAnimatedRef = useRef(false);

  const loadStartRef = useRef<number>(Date.now());

  const handleLoad = () => {
    console.log("[LandMap] map onLoad fired", { elapsedMs: Date.now() - loadStartRef.current });
    if (hasAnimatedRef.current) return;
    hasAnimatedRef.current = true;
    const map = mapRef.current;
    if (!map) {
      console.warn("[LandMap] handleLoad: mapRef.current is null");
      return;
    }

    // Identity check: confirm react-map-gl is using the mapboxgl instance we configured.
    try {
      const gl = map.getMap();
      const usingConfiguredInstance = gl instanceof mapboxgl.Map;
      console.log("[LandMap] mapLib identity check", {
        usingConfiguredInstance,
        glConstructorName: gl.constructor?.name,
        mapboxglVersion: mapboxgl.version,
      });
    } catch (e) {
      console.warn("[LandMap] identity check threw", e);
    }

    // Hide all label layers (text/icon) in the Mapbox style.
    try {
      const gl = map.getMap();
      const style = gl.getStyle();
      style?.layers?.forEach((layer) => {
        if (layer.type !== "symbol") return;
        gl.setLayoutProperty(layer.id, "visibility", "none");
      });
    } catch {
      // Style not ready or layer missing — non-fatal, just skip.
    }
    // Notify the parent of the projected pixel for the lon/lat. Guarded by `arrived`
    // so we only fire once even if both `moveend` and the safety timeout trip.
    let arrived = false;
    const notifyArrive = (source: "moveend" | "timeout") => {
      if (arrived || !onArrive) {
        console.log("[LandMap] notifyArrive skipped", { source, arrived, hasOnArrive: !!onArrive });
        return;
      }
      arrived = true;
      console.log("[LandMap] notifyArrive fired", { source, elapsedMs: Date.now() - loadStartRef.current });
      try {
        const point = map.project([lon, lat]);
        onArrive({ x: point.x, y: point.y });
      } catch (e) {
        console.warn("[LandMap] project threw, using viewport center", e);
        onArrive({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
      }
    };

    // Arriving from Explore: the map already initializes at the target zoom on
    // the location, so there's no fly-to. Just notify the parent immediately.
    if (skipFlyTo) {
      console.log("[LandMap] skipFlyTo — revealing content without animation");
      notifyArrive("moveend");
      return;
    }

    // Attach the moveend listener BEFORE flyTo so we never miss the event
    // (on slow networks / production, flyTo can settle before a deferred listener attaches).
    map.once("moveend", () => notifyArrive("moveend"));

    // Safety net: if the map errors, tiles fail to load, or moveend simply never fires
    // (e.g. token restricted on the deployed domain), reveal content anyway.
    // 350ms pre-delay + 1000ms flyTo ≈ 1350ms; give it 2500ms total grace.
    window.setTimeout(() => notifyArrive("timeout"), 2500);

    // Small delay so the wide-out frame paints before we ease in.
    window.setTimeout(() => {
      console.log("[LandMap] dispatching flyTo", { center: [lon, lat], zoom: LAND_MAP_TARGET_ZOOM });
      map.flyTo({
        center: [lon, lat],
        zoom: LAND_MAP_TARGET_ZOOM,
        duration: 3000,
        essential: true,
        curve: 1.4,
        speed: 0.9,
      });
    }, 350);
  };

  const handleError = (event: any) => {
    console.error("[LandMap] map error event", {
      message: event?.error?.message || event?.message,
      sourceId: event?.sourceId,
      error: event?.error,
    });
  };

  // Not aria-hidden: the map is decorative, but mapbox-gl's logo and attribution links inside it
  // are required (Mapbox Product Terms §1.4) and focusable, so they must stay reachable — and
  // clickable, hence pointer events back on for the control layer only.
  return (
    <Box
      sx={{
        display: "block",
        position: "fixed",
        inset: 0,
        zIndex: 1,
        pointerEvents: "none",
        overflow: "hidden",
        "& .mapboxgl-control-container": { pointerEvents: "auto" },
      }}
    >
      <MapboxMap
        ref={mapRef as any}
        // A promise is fine here: react-map-gl awaits it once, on mount, before creating the map.
        mapLib={loadLandMapLib()}
        projection={{ name: "globe" }}
        maxZoom={17}
        interactive={false}
        interactiveLayerIds={["map-data-fill"]}
        initialViewState={{
          longitude: lon,
          latitude: lat,
          // From Explore: start already zoomed in (no fly-to). Otherwise start
          // wide so the flyTo animation has somewhere to travel from.
          zoom: skipFlyTo ? LAND_MAP_TARGET_ZOOM : 5,
          bearing: 0,
          pitch: 0,
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle={MAPBOX_STYLE}
        mapboxAccessToken={MAPBOX_TOKEN}
        onLoad={handleLoad}
        onError={handleError}
      />
      {/* <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.35)",
          pointerEvents: "none",
        }}
      /> */}
    </Box>
  );
};

// In embedded mode the detail body is layered over the host page's live,
// interactive map as its own fixed, self-scrolling overlay (so the host's body
// scroll-lock stays intact and the map keeps rendering behind any gaps). In the
// standalone route it's a transparent fragment and the page flows normally.
const EmbeddedBoundary: React.FC<{
  embedded?: boolean;
  topOffset?: number;
  onEngage?: () => void;
  onBackdropClick?: () => void;
  onScrollChange?: (scrollTop: number) => void;
  scrollRef?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
}> = ({ embedded, topOffset = 0, onEngage, onBackdropClick, onScrollChange, scrollRef, children }) => {
  if (!embedded) return <>{children}</>;
  // First meaningful scroll = the user has engaged with this land → load its
  // full detail. Lets the user flick through lands via the thumbnail strip
  // (preview + skeleton only) without firing an API call for each one.
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const top = e.currentTarget.scrollTop;
    onScrollChange?.(top);
    if (onEngage && top > 4) onEngage();
  };
  return (
    <Box
      ref={scrollRef}
      onScroll={handleScroll}
      // Dismiss when the click landed on the transparent backdrop — the visible
      // map or the empty padding around the cards — rather than on actual
      // content (the hero or a section card). This makes the big surrounding
      // gaps behave as click-to-close instead of swallowing the click.
      onClick={(e) => {
        if (!onBackdropClick) return;
        // Ignore clicks that bubbled in from a portalled child — MUI Modal,
        // Dialog, SwipeableDrawer, Popover, Select menu, etc. render their DOM
        // under <body>, outside this overlay, yet React still routes their
        // synthetic click events through here (their React-tree parent). Such a
        // click happened visually *over* the detail (on the popup's own backdrop),
        // not on this overlay, so it must not dismiss the detail. Only a click that
        // actually lands in this overlay's own DOM subtree counts as a backdrop tap.
        if (!e.currentTarget.contains(e.target as Node)) return;
        const el = e.target as HTMLElement | null;
        if (el?.closest?.("[data-parallax-card], #image-block, [data-detail-keep]")) return;
        onBackdropClick();
      }}
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        top: `${topOffset}px`,
        zIndex: 1305,
        overflowX: "hidden",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        // Transparent — the host (Explore) keeps its live map fully visible
        // behind the detail; the only layer under this one is its invisible
        // click-to-dismiss backdrop.
        backgroundColor: "transparent",
        // Pin the sticky left column to the top of this overlay (which already
        // sits below the host header) rather than the standalone 100px default.
        "--layout-header-height": "0px",
      }}
    >
      {/* Cap the content to the same 1560px the header/ContentWrapper use so the
          detail aligns with the page chrome instead of spanning the full width.
          On mobile the host renders a fixed back button in the top-left corner
          (Explore's MobileBackButton, ~58px tall incl. its top offset); pad the
          content down so the hero card clears it and the button gets its own
          tappable strip. Desktop sits below the header, so no inset needed. */}
      <Box sx={{ width: "100%", maxWidth: "1560px", margin: "0 auto", minHeight: "100%", pt: { xs: "44px", sm: 0 } }}>
        {children}
      </Box>
    </Box>
  );
};

// Shimmering placeholder block, sized by the caller to roughly match the
// section card it stands in for so the layout doesn't jump when data arrives.
const SkeletonBlock = styled("div")({
  position: "relative",
  overflow: "hidden",
  borderRadius: "20px",
  backgroundColor: "rgba(245, 245, 243, 0.08)",
  border: "1px solid rgba(235, 225, 221, 0.1)",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(100deg, transparent 30%, rgba(235, 225, 221, 0.12) 50%, transparent 70%)",
    transform: "translateX(-100%)",
    animation: `${skeletonShimmer} 1.6s linear infinite`,
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&::after": { animation: "none" },
  },
});

// "Locating asset data" geo-ping: a breathing leaf-green core inside three
// expanding purple sonar rings and an orbiting arc. Transform/opacity only,
// so the loop stays on the compositor.
const GeoPing: React.FC = () => (
  <Box sx={{ position: "relative", width: 110, height: 110, display: "grid", placeItems: "center" }}>
    {/* {[0, 0.7, 1.4].map((delay) => (
      <Box
        key={delay}
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: "1.5px solid #BC92F8",
          animation: `${pingSonar} 2.1s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0.35 },
        }}
      />
    ))} */}
    {/* <Box
      sx={{
        position: "absolute",
        inset: "13px",
        borderRadius: "50%",
        border: "2px solid transparent",
        borderTopColor: "#BC92F8",
        borderRightColor: "rgba(188, 146, 248, 0.25)",
        animation: `${pingOrbit} 1.1s linear infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    /> */}
    {/* <Box
      sx={{
        width: 13,
        height: 13,
        borderRadius: "50%",
        backgroundColor: "#ccff99",
        boxShadow: "0 0 18px 4px rgba(204, 255, 153, 0.65)",
        animation: `${pingCoreBreathe} 1.6s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    /> */}
    <Typography
      sx={{
        position: "absolute",
        top: "calc(100% + 10px)",
        left: "50%",
        transform: "translateX(-50%)",
        color: "#ebe1dd",
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 9,
        letterSpacing: "0.28em",
        whiteSpace: "nowrap",
        animation: `${pingLabelFade} 2.2s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0.8 },
      }}
    >
      LOCATING ASSET DATA
    </Typography>
  </Box>
);

// Loading layer for the embedded right panel: the geo-ping centered over
// shimmer skeletons whose heights approximate the real sections (summary card,
// purple price box, tiers card, provenance) so nothing jumps when data lands.
// `exiting` re-renders it as a non-interactive overlay on top of the real,
// already-mounted sections for the 400ms dissolve (see loadLayerOut).
// data-detail-keep: clicks on the skeletons must not dismiss the detail —
// they stand in for cards, which don't dismiss either.
const EmbeddedLoadLayer: React.FC<{ exiting?: boolean }> = ({ exiting }) => (
  <Box
    data-detail-keep
    aria-hidden={exiting ? true : undefined}
    sx={{
      // Same top offset as the right column's first real card, so the skeletons
      // dissolve in place over the sections instead of sitting ~50px below them.
      pt: { xs: 0, md: HERO_TOP_MARGIN_MD },
      ...(exiting
        ? {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 3,
            pointerEvents: "none",
            animation: `${loadLayerOut} 400ms ease-in-out both`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0 },
          }
        : {}),
    }}
  >
    <Box sx={{ position: "relative" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <SkeletonBlock sx={{ height: 320 }} />
        <SkeletonBlock
          sx={{ height: 60, backgroundColor: "rgba(188, 146, 248, 0.16)", borderColor: "rgba(188, 146, 248, 0.3)" }}
        />
        <SkeletonBlock sx={{ height: 150 }} />
        <SkeletonBlock sx={{ height: 220 }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2,
        }}
      >
        <GeoPing />
      </Box>
    </Box>
  </Box>
);

// The hero image card, shared between the embedded loading pass (lightweight
// preview token) and the loaded detail (rich token). It must stay ONE component
// rendered at the SAME tree position in both states: React then reconciles the
// swap instead of remounting, so the <img> DOM node survives when the payload
// lands and the gif keeps playing without a visible refresh/restart. Don't fork
// this back into two inline copies — that's what caused the hero flash.
//
// Gallery flip: clicking the card rotates it 180° on the Y axis (front face →
// back face = clean photo + pager dots). A `flipped` prop drives the transform;
// all motion is on a plain styled div so MUI Box sx transforms don't interfere.
const EmbeddedHeroCard: React.FC<{
  heroToken: any;
  isMobile: boolean;
  remainingTrees: number | null;
  // Opens the media gallery — absent during the loading pass (cursor stays default).
  onImageClick?: () => void;
  heroRef?: React.Ref<HTMLDivElement>;
  // Gallery flip props (only used in the loaded/embedded pass)
  flipped?: boolean;
  // Called when the user clicks the back-face image to flip back.
  onFlipClose?: () => void;
  // a11y: ref for the flip trigger button — focus returned here on gallery close.
  flipButtonRef?: React.Ref<HTMLDivElement>;
  // If provided, a Mint button is shown in the EditionBadge tooltip.
  onMint?: () => void;
  // Wait before the name/location/stats copy rises in on mount (heroTextRise);
  // null holds it until the caller is ready to show the card.
  textRevealDelayMs?: number | null;
}> = ({
  heroToken,
  isMobile,
  remainingTrees,
  onImageClick,
  heroRef,
  flipped,
  onFlipClose,
  flipButtonRef,
  onMint,
  textRevealDelayMs = HERO_TEXT_REVEAL_DELAY_MS,
}) => {
  const flipInnerRef = useRef<HTMLDivElement>(null);
  // Pointer tilt: the handlers go on BackgroundDiv (the flat hit area); the
  // rotation is written onto HeroTiltCard. See hooks/useCardTilt.
  const tiltCardRef = useRef<HTMLDivElement>(null);
  const tiltGlareRef = useRef<HTMLDivElement>(null);
  const tiltHandlers = useCardTilt({ cardRef: tiltCardRef, glareRef: tiltGlareRef, maxDeg: HERO_TILT_MAX_DEG });
  // Front (detail) face: optimized animated WebP rendition, falling back to the
  // raw content when absent. The back face deliberately uses raw `content`.
  // Both are 30–80MB animated files, so neither is ever painted directly (that
  // froze the main thread ~0.5s on open): the static poster is painted and the
  // animation plays over it from a worker (see components/AnimatedLand). The
  // front's blurred backdrop copy is fed from the same decode.
  const heroImageUrl = landImageSrc(heroToken, "full");
  const heroPosterUrl = landImageSrc(heroToken, "poster");
  const heroAnimation = useAnimatedLand(heroImageUrl, {
    startDelayMs: HERO_ANIMATION_START_MS,
    blurPx: 30,
    active: !flipped,
  });
  const heroAnimationClass = heroAnimation.playing ? ANIMATED_LAND_PLAYING_CLASS : undefined;
  // The back face is invisible until the first flip — don't start its 80MB
  // download/decode until then, and pause it whenever the front is showing.
  const [backFaceOpened, setBackFaceOpened] = useState(false);
  useEffect(() => {
    if (flipped) {
      setBackFaceOpened(true);
    }
  }, [flipped]);
  const badgeTooltipOpenRef = useRef(false);
  // Latched at mousedown so the click handler can check if the tooltip was open
  // when the user started the click (it will already be closed by click time).
  const tooltipWasOpenOnMouseDownRef = useRef(false);

  // Imperatively hint will-change on the flip inner for the exact duration of
  // the transition; cleared immediately after so the layer is never kept alive.
  useEffect(() => {
    const el = flipInnerRef.current;
    if (!el) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    el.style.willChange = "transform";
    const t = setTimeout(() => {
      el.style.willChange = "";
    }, HERO_FLIP_MS + 50);
    return () => clearTimeout(t);
  }, [flipped]);

  // Copy reveal replay: bumped when the card flips back to its front. The copy
  // wrappers are keyed on it, so the remount restarts the one-shot heroTextRise.
  // Layout effect so the remount commits before the front face's first paint.
  const [textRevealGen, setTextRevealGen] = useState(0);
  const wasFlippedRef = useRef(flipped);
  useLayoutEffect(() => {
    const wasFlipped = wasFlippedRef.current;
    wasFlippedRef.current = flipped;
    if (!wasFlipped || flipped) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    setTextRevealGen((gen) => gen + 1);
  }, [flipped]);
  const textDelayMs = textRevealGen > 0 ? HERO_OVERLAY_RETURN_DELAY_MS : textRevealDelayMs;

  return (
    <BackgroundDiv
      id="image-block"
      ref={heroRef}
      {...tiltHandlers}
      sx={{
        margin: 0,
        width: { xs: "85%", md: LEFT_COL_CARD_WIDTH_MD },
        aspectRatio: { xs: "1 / 1", md: "1 / 1" },
        mr: { xs: "7.5%", md: "8.5%" },
        ml: { xs: "7.5%", md: "16.5%" },
        mt: { xs: "7.5%", md: HERO_TOP_MARGIN_MD },
      }}
    >
      {/* Tilt layer wraps the flip so the whole card leans toward the pointer. */}
      <HeroTiltCard ref={tiltCardRef}>
        <HeroFlipInner ref={flipInnerRef} flipped={!!flipped}>
          {/* FRONT face — the existing overlaid hero card */}
          <HeroFlipFront flipped={!!flipped}>
            <Background style={{ height: "100%" }}>
              <Box
                ref={flipButtonRef}
                role="button"
                tabIndex={flipped ? -1 : 0}
                onMouseDown={() => {
                  tooltipWasOpenOnMouseDownRef.current = badgeTooltipOpenRef.current;
                }}
                onClick={() => {
                  if (tooltipWasOpenOnMouseDownRef.current) return;
                  if (!flipped) onImageClick?.();
                }}
                onKeyDown={(event) => {
                  if (!flipped && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    onImageClick?.();
                  }
                }}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  cursor: !flipped && onImageClick && heroImageUrl ? "pointer" : "default",
                }}
              >
                {/* Photo — the poster, same as the back face's base layer so the
                  flip feels like one continuous element morphing. */}
                <Image
                  sx={{
                    borderRadius: "20px",
                    backgroundColor: "white",
                    display: "block",
                    position: "absolute",
                    inset: 0,
                    height: "100% !important",
                    width: "100% !important",
                    objectFit: "cover",
                  }}
                  src={heroPosterUrl}
                />
                <AnimatedLandCanvas
                  key={heroImageUrl}
                  ref={heroAnimation.canvasRef}
                  aria-hidden="true"
                  className={heroAnimationClass}
                  style={{ borderRadius: 20 }}
                />
                {/* Overlays fade out mid-rotation (transition driven by flipped prop)
                  so text/badges are gone before they'd appear mirrored. */}
                <HeroFlipFrontOverlays flipped={!!flipped}>
                  {heroToken?.etNft?.editionId && (
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: "absolute",
                        top: { xs: "18px", md: "24px" },
                        left: { xs: "18px", md: "24px" },
                        zIndex: 1000,
                      }}
                    >
                      <EditionBadgeCanvas
                        size={isMobile ? 48 : 64}
                        editionId={Number(heroToken.etNft.editionId)}
                        active={remainingTrees !== null && remainingTrees > 0}
                        onMint={onMint}
                        onTooltipChange={(open) => {
                          badgeTooltipOpenRef.current = open;
                        }}
                      />
                    </Box>
                  )}
                  {(() => {
                    const publicStatus = resolveStandardStatus(
                      {
                        status: heroToken?.status,
                        etNft: heroToken?.etNft,
                        offerStatus: heroToken?.offerStatus,
                        meta: heroToken?.meta,
                      },
                      { role: "public" }
                    );
                    if (!publicStatus) return null;
                    const isGreenBadge = publicStatus.color === "action.pulse";
                    const badgeTextColor = isGreenBadge ? "#ccff99" : "rgba(255,255,255,0.6)";
                    const badgeBorderColor = isGreenBadge ? "#ccff99" : "rgba(255,255,255,0.35)";

                    return (
                      <Box
                        sx={{
                          position: "absolute",
                          top: { xs: "18px", md: "24px" },
                          right: { xs: "18px", md: "24px" },
                          zIndex: 2,
                          height: 36,
                          px: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                          backgroundColor: "rgba(43,53,1,0.75)",
                          border: `1px solid ${badgeBorderColor}`,
                          borderRadius: "20px",
                          color: badgeTextColor,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {publicStatus.label === "Active" && <PulseCircle sold={true} sx={{ mr: 0 }} />}
                        {publicStatus.label === "Pending" && <PulseCircle warning sx={{ mr: 0 }} />}
                        {(publicStatus.label === "Offer" || publicStatus.label === "Auction") && (
                          <ListAltOutlinedIcon sx={{ fontSize: 14 }} />
                        )}
                        {publicStatus.label === "Idle" && <IdleIcon sx={{ fontSize: 14 }} />}
                        <Typography
                          sx={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontWeight: 700,
                            fontSize: 12,
                            color: badgeTextColor,
                            textTransform: "uppercase",
                            lineHeight: 1,
                          }}
                        >
                          {publicStatus.label}
                        </Typography>
                      </Box>
                    );
                  })()}
                  {(() => {
                    // Location line states (StatusCycle) — shared with FeaturedLand. The
                    // preview token's list just grows when the rich token lands.
                    const locationStates = landStatusStates(heroToken?.meta);
                    const ecoScoreDisplay = heroToken?.ecoScore != null ? (heroToken.ecoScore * 100).toFixed(0) : "N/A";
                    const formattedTrees = heroToken?.etNft?.totalTreeForEasement
                      ? sixDigits(formatValue(heroToken.etNft.totalTreeForEasement as any, true, 0))
                      : "N/A";
                    const tokenNumber = heroToken?.etNft?.id != null ? String(heroToken.etNft.id) : "N/A";
                    const formattedRemainingTrees =
                      remainingTrees !== null ? String(numberWithCommas(sixDigits(remainingTrees))) : null;
                    const stats = [
                      { label: "SCORE", value: ecoScoreDisplay },
                      {
                        label: "TR3",
                        value: formattedRemainingTrees ?? formattedTrees,
                      },
                      { label: "No", value: tokenNumber },
                    ];

                    return (
                      <>
                        {/* Frosted-glass bottom. `backdrop-filter` is dead here: the flip
                          card's 3D transform context (HeroFlipInner's preserve-3d +
                          perspective) zeroes out the backdrop, so a backdrop blur renders
                          nothing. Instead we blur a duplicate of the hero image with
                          `filter` (unaffected by the 3D context) and fade it in from the
                          bottom with a mask. The blurred copy is oversized and clipped by
                          the wrapper so `filter: blur`'s faded edges never show as a rim. */}
                        <Box
                          aria-hidden
                          sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "20px",
                            overflow: "hidden",
                            maskImage: "linear-gradient(to bottom, transparent 0%, black 85%)",
                            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 85%)",
                            pointerEvents: "none",
                            zIndex: 1,
                          }}
                        >
                          <Box
                            component="img"
                            src={heroPosterUrl}
                            alt=""
                            sx={{
                              position: "absolute",
                              inset: "-8%",
                              width: "116%",
                              height: "116%",
                              objectFit: "cover",
                              objectPosition: "center",
                              filter: "blur(30px)",
                            }}
                          />
                          {/* The animated frames, blurred by the worker (same oversize). */}
                          <AnimatedLandCanvas
                            key={heroImageUrl}
                            ref={heroAnimation.blurCanvasRef}
                            className={heroAnimationClass}
                            style={{ inset: "-8%", width: "116%", height: "116%" }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              backgroundColor: isMobile ? "rgba(43, 53, 1, 0.25)" : "rgba(43, 53, 1, 0.15)",
                            }}
                          />
                        </Box>
                        {/* Copy rises in line by line (heroTextRise): name, location,
                          stat labels, stat values. Keyed on textRevealGen so it replays
                          when the card flips back to this face. */}
                        <Box
                          key={`copy-${textRevealGen}`}
                          sx={{
                            position: "absolute",
                            bottom: { xs: 70, md: 78 },
                            left: { xs: 18, md: 24 },
                            right: { xs: 18, md: 24 },
                            zIndex: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontWeight: 700,
                              fontSize: 26,
                              color: "#fff",
                              lineHeight: 1.21,
                              ...heroTextLineSx(textDelayMs, 0),
                            }}
                          >
                            {heroToken?.meta?.name ?? ""}
                          </Typography>
                          {/* Status line: shimmers while a state holds, then swaps to the
                            next (StatusCycle). Frozen while the card shows its back face. */}
                          <Typography
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontWeight: 700,
                              fontSize: 16,
                              color: "#fff",
                              lineHeight: 1.21,
                              letterSpacing: "0.08em",
                              "--think-base": "rgba(255, 255, 255, 0.7)",
                              "--think-highlight": "#fff",
                              ...heroTextLineSx(textDelayMs, 1),
                            }}
                          >
                            <StatusCycle states={locationStates} paused={!!flipped} />
                          </Typography>
                        </Box>
                        <Box
                          key={`stats-${textRevealGen}`}
                          sx={{
                            position: "absolute",
                            left: { xs: 18, md: 24 },
                            right: { xs: 18, md: 24 },
                            bottom: { xs: 18, md: 24 },
                            display: "flex",
                            justifyContent: "space-between",
                            zIndex: 2,
                          }}
                        >
                          {stats.map((stat) => (
                            <Box key={stat.label}>
                              <Typography
                                sx={{
                                  fontFamily: "'IBM Plex Mono', monospace",
                                  fontWeight: 700,
                                  fontSize: 16,
                                  color: "rgba(255,255,255,0.5)",
                                  lineHeight: 1.3,
                                  ...heroTextLineSx(textDelayMs, 2),
                                }}
                              >
                                {stat.label}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "'IBM Plex Mono', monospace",
                                  fontWeight: 700,
                                  fontSize: 16,
                                  color: "#fff",
                                  lineHeight: 1.3,
                                  ...heroTextLineSx(textDelayMs, 3),
                                }}
                              >
                                {stat.value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </>
                    );
                  })()}
                </HeroFlipFrontOverlays>
              </Box>
            </Background>
          </HeroFlipFront>

          {/* BACK face — always the raw `content` (over the poster while it
            streams in), no overlays. Clicking flips back to the front (detail) view. */}
          <HeroFlipBack
            flipped={!!flipped}
            onClick={onFlipClose}
            style={{ cursor: onFlipClose ? "pointer" : "default" }}
          >
            <span
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: heroPosterUrl ? `url(${heroPosterUrl})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            {backFaceOpened && (
              <AnimatedLandLayer key={heroToken?.content} src={heroToken?.content} active={!!flipped} />
            )}
          </HeroFlipBack>
        </HeroFlipInner>
        {/* Cursor glare — after the flip inner so it paints above both faces. */}
        <HeroTiltGlareClip aria-hidden>
          <HeroTiltGlare ref={tiltGlareRef} />
        </HeroTiltGlareClip>
      </HeroTiltCard>
    </BackgroundDiv>
  );
};

/**
 * The auction bar's countdown, with the label that introduces it.
 *
 * The label follows the clock rather than the row's status. An auction whose end date has
 * passed still reports `status: "started"` for the couple of minutes the settling workers
 * take — `auctionEnded` is still false, or this block would not be mounted at all — and in
 * that window `CountdownTimer` paints "Expired", which read as a contradiction under an
 * "Ends in" label. Past the end date the label says "Ended" and the timer goes: the state is
 * the label's to carry, and "Ended Expired" only says it twice. The tooltip still gives the
 * exact end timestamp.
 *
 * `useCountdown` is what makes the flip live: the timer ticks in state of its own, which
 * never re-renders a label sitting outside it, so the label borrows the same 1s tick.
 */
const AuctionCountdown = ({ endDate }: { endDate: Date | null }) => {
  useCountdown(endDate);
  const expired = !!endDate && endDate.getTime() <= Date.now();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <Tooltip title={endDate ? moment(endDate).format("LLL") : ""} arrow placement="top">
        <ClickableContainer>
          <Text
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#000",
              fontFamily: "'IBM Plex Mono', monospace",
              lineHeight: "18px",
            }}
          >
            {expired ? "Ended" : "Ends in"}
          </Text>

          {!expired && (
            <CountdownTimer
              sx={{
                "& .timerItem_over span": {
                  fontFamily: "'IBM Plex Mono', monospace !important",
                  fontSize: "14px !important",
                  color: "black !important",
                  fontWeight: "700 !important",
                  lineHeight: "18px !important",
                  textTransform: "uppercase",
                },
              }}
              targetDate={endDate}
            />
          )}
        </ClickableContainer>
      </Tooltip>
    </Box>
  );
};

const LandPage = ({
  details,
  embedded,
  tokenId: propTokenId,
  previewToken,
  onClose,
  onSearch,
  revealOrigin,
  topOffset,
}: LandPageProps) => {
  const axios = useAxios();
  const navigator = useNavigate();
  const routerLocation = useLocation();
  // Set by LandCardPopup when navigating here from the Explore map — when true,
  // the background map loads already zoomed in instead of playing the fly-to.
  const cameFromExplore = Boolean((routerLocation.state as { fromExplore?: boolean } | null)?.fromExplore);
  const { tokenId: routeTokenId } = useParams();
  // Embedded callers pass the id directly; the standalone route reads it from
  // the URL. Everything downstream (fetchAllData, the [tokenId] effect) is
  // unchanged, so the embedded page fetches the same rich payload → full parity.
  const tokenId = propTokenId ?? routeTokenId;
  const { user, isTrust, isAdmin } = useAuthorization();
  const { library } = useMetaMask();
  const [token, setToken] = useState<LandTokenType | undefined>(undefined);
  // Post-auction settlement, resolved in sortData() as part of the page payload rather than
  // by the panel after it mounts — see the fetch there for why. Always written in the same
  // pass that writes `token`, so the two can never describe different lands.
  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // const [isOrgDataLoading, setIsOrgDataLoading] = useState<boolean>(true);
  // const [isChartsLoading, setIsChartsLoading] = useState<boolean>(true);
  const [isAuctionLoading, setIsAuctionLoading] = useState<boolean>(true);
  const [remainingTrees, setRemainingTrees] = useState<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [charts, setCharts] = useState<
    {
      date: string;
      ecoScore: number | null;
      ecoScoreVersion?: string;
      createdAt?: string;
      updatedAt?: string;
      nextVerificationEcoScore?: number | null;
      contractStartEcoScore?: number | null;
    }[]
  >([]);
  // const [searchParams] = useSearchParams();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryInitialFileId, setGalleryInitialFileId] = useState<string | number | undefined>(undefined);
  // In-place gallery flip on the hero card (embedded only). Separate from the
  // full-screen MediaGalleryModal which the survey/contract/media buttons open.
  const [heroFlipOpen, setHeroFlipOpen] = useState(false);
  // Active tile index for the vertical thumb rail in the flip media stack.
  const [activeFlipIdx, setActiveFlipIdx] = useState<number>(0);
  const flipTileObserverRef = useRef<IntersectionObserver | null>(null);
  const parallaxObserverRef = useRef<IntersectionObserver | null>(null);
  // a11y: focus returns to the hero card when the gallery closes.
  const heroCardRef = useRef<HTMLDivElement>(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>(null);
  const [contractMax, setContractMax] = useState<number>(0);
  // const [parcelMax, setParcelMax] = useState<number>(3333);
  const [selectedView, setSelectedView] = useState<string>("ecoscore"); // Removed - no longer used with half-and-half layout
  const [landFeaturesTab, setLandFeaturesTab] = useState<"features" | "elevation">("features");

  const uniqueId = useId();
  const gradientId = `chart-gradient-${uniqueId.replace(/:/g, "")}`;

  // offer states
  const [openToOffers, setOpenToOffers] = useState<boolean>(false); // checks offerStatus
  const [canOffer, setCanOffer] = useState<boolean>(true); // checks if user is owner or minter
  const [canAcceptOffer, setCanAcceptOffer] = useState<boolean>(false); // checks if user is owner
  const [offerModal, setOfferModal] = useState<boolean>(false);
  const [biddingTiers, setBiddingTiers] = useState<any[]>([]);
  const [balance, setBalance] = useState<string>("0");
  const [nextOffer, setNextOffer] = useState<string>("");
  const [nextOfferValid, setNextOfferValid] = useState<boolean>(false);
  const [offerLoading, setOfferLoading] = useState<boolean>(false);
  const [cancelOfferLoading, setCancelOfferLoading] = useState<boolean>(false);
  const [cancelOfferModal, setCancelOfferModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  // Still set by the offer flow, but strict Figma leaves the popup no place to print it.
  const [, setInfoMessage] = useState<string>("");
  const [highestOffer, setHighestOffer] = useState<number | null>(null);
  const [highestOfferBuyer, setHighestOfferBuyer] = useState<string | null>(null);
  const [landGuardian, setLandGuardian] = useState<boolean>(false);
  const [landOwner, setLandOwner] = useState<boolean>(false);
  const [landTrust, setLandTrust] = useState<boolean>(false);
  const [landActive, setLandActive] = useState<boolean>(false);
  const [hasRemainingTrees, setHasRemainingTrees] = useState<boolean>(false);
  const [offerCreationModalOpen, setOfferCreationModalOpen] = useState<boolean>(false);
  const [hoveredEcoScore, setHoveredEcoScore] = useState<number | null>(null);
  const [rewardData, setRewardData] = useState<RewardData | null>(null);
  // Tree claim modal states
  const [claimMint, setClaimMint] = useState<string | number | boolean | null>(null);
  const [claimMintData, setClaimMintData] = useState<{
    name: string;
    content: string;
    targetDate: Date;
  } | null>(null);
  const appContext = useContext(AppContext);
  const parentRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const tiersRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const [tokenData, setTokenData] = useState<any>(null);
  const [auctionData, setAuctionData] = useState<any>(null);
  // Deadline for the auction-settling poll below — see that effect for why it
  // cannot live in the effect closure.
  const settleGiveUpAtRef = useRef<number | null>(null);
  const [offersData, setOffersData] = useState<any>(null);
  const [transactionsData, setTransactionsData] = useState<any>(null);
  const [transactionsCountData, setTransactionsCountData] = useState<number>(0);
  const [indicatorsData, setIndicatorsData] = useState<any[]>([]);

  // Auction display state (compact view in LandSummaryCard)
  const [auctionCurrentBid, setAuctionCurrentBid] = useState<string | number>("0");
  const [auctionHasBids, setAuctionHasBids] = useState<boolean>(false);
  const [auctionHighestBidderAddress, setAuctionHighestBidderAddress] = useState<string>("");
  const [auctionWinnerAddress, setAuctionWinnerAddress] = useState<string | null>(null);
  const [auctionRemainingTrees, setAuctionRemainingTrees] = useState<any>(0);
  const [auctionTreesValue, setAuctionTreesValue] = useState<any>(0);
  const [auctionEnded, setAuctionEnded] = useState<boolean>(false);
  const [auctionEndDate, setAuctionEndDate] = useState<Date | null>(null);
  const [hasAuction, setHasAuction] = useState<boolean>(false);
  const [emergeOrigin, setEmergeOrigin] = useState<{ x: number; y: number } | null>(null);
  const [emergePlaying, setEmergePlaying] = useState<boolean>(false);
  // Tracks whether the embedded desktop "open" stagger has run for the current land.
  const didDesktopIntroRef = useRef(false);
  // The embedded scroll overlay, so we can reset it to the top on a land switch.
  const embeddedScrollRef = useRef<HTMLDivElement>(null);
  // Distinguishes a real land switch (reset to preview) from a user/library
  // change on the same land (refetch in place) in the embedded fetch effect.
  const prevTokenIdRef = useRef<string | undefined>(undefined);
  // The embedded preview hero (#image-block while loading) — target of the
  // popup→detail FLIP morph and source rect for the reverse morph on close.
  const previewHeroRef = useRef<HTMLDivElement>(null);
  // True once the embedded preview pass has rendered for this land: the loaded
  // hero then mounts already in place, so its slide-in entrance must not replay.
  const heroIntroPlayedRef = useRef(false);
  // Latches while the skeleton layer is showing so the exit effect below knows
  // a dissolve is owed when the full payload lands.
  const showedLoadLayerRef = useRef(false);
  // Keeps the load layer mounted as a dissolving overlay (loadLayerOut) over the
  // real sections for one beat after the fetch resolves.
  const [loadLayerExiting, setLoadLayerExiting] = useState(false);

  // Hard fallback: if the map never reports "arrived" (token blocked on this domain,
  // tiles failed, mapbox-gl errored on slow mobile network, etc.), the content would
  // stay invisible forever because it's gated behind emergePlaying. Force-reveal
  // after a generous grace period so the page always shows up.
  useEffect(() => {
    if (emergePlaying) return;
    const timeoutId = window.setTimeout(() => {
      console.log("[emerge] 5s hard-fallback firing setEmergePlaying(true)");
      setEmergePlaying(true);
    }, 5000);
    return () => window.clearTimeout(timeoutId);
  }, [emergePlaying]);

  // Embedded mode has no LandMapBackground to report "arrived", so kick off the
  // same emerge/parallax reveal as soon as a token is available. This lights up
  // the IntersectionObserver parallax (mobile scroll reveal) and the per-card CSS.
  useEffect(() => {
    console.log("[emerge] embedded effect run", { embedded, hasToken: !!token, emergePlaying });
    if (!embedded || !token || emergePlaying) return;
    console.log("[emerge] embedded effect -> setEmergePlaying(true)");
    setEmergeOrigin(revealOrigin ?? null);
    setEmergePlaying(true);
  }, [embedded, token, revealOrigin, emergePlaying]);

  // Shared-element morph: FLIP the preview hero from the popup card's stashed
  // rect to its natural position (the popup unmounts and this mounts in the
  // same commit, so it reads as one element expanding). Falls back to the
  // regular hero slide-in when there's nothing to morph from (deep link /
  // direct ?asset= visit). Reduced motion bails inside playMorphFromRect and
  // again here — content just appears in place.
  useLayoutEffect(() => {
    if (!embedded) {
      return;
    }
    const el = previewHeroRef.current;
    if (!el) {
      return;
    }
    heroIntroPlayedRef.current = true;
    showedLoadLayerRef.current = true;
    const from = takeMorphRect(MORPH_TO_DETAIL_KEY, tokenId);
    if (from && playMorphFromRect(el, from)) {
      return;
    }
    if (isMobile) {
      return;
    }
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    // Same motion as the heroSlideIn keyframe, via WAAPI so it shares the
    // morph's code path (the CSS variant still serves the loaded column).
    el.animate(
      [
        { opacity: 0, transform: "translate3d(40px, 0, 0)" },
        { opacity: 1, transform: "translate3d(0, 0, 0)" },
      ],
      // `backwards`, not `both`: the last frame equals the natural style, so the
      // run ends without leaving a translate3d layer pinned on the hero.
      { duration: 900, easing: "cubic-bezier(0.16, 1, 0.3, 1)", fill: "backwards" }
    );
    // isMobile is a render-time media query; re-running the entrance on a
    // viewport flip mid-load would replay it, so deliberately not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, tokenId]);

  // When the full payload lands after the skeleton pass, keep the load layer
  // mounted as an overlay for one beat so it dissolves (opacity) over
  // the real sections staggering in underneath. Must be a LAYOUT effect: the
  // skeletons unmount in the same commit the (still-invisible) real cards
  // mount, so a passive effect would let the browser paint an empty right
  // column for a frame before the overlay appears — a visible flicker.
  useLayoutEffect(() => {
    if (!embedded || !token || !showedLoadLayerRef.current) {
      return;
    }
    showedLoadLayerRef.current = false;
    setLoadLayerExiting(true);
    const id = window.setTimeout(() => setLoadLayerExiting(false), 500);
    return () => window.clearTimeout(id);
  }, [embedded, token]);

  // Disable iOS rubber-band overscroll while LandPage is mounted. Without this,
  // pulling down on mobile bounces the page and exposes the fixed Mapbox
  // background above the page header — looks like a layout glitch.
  // Also pin scroll to top: content renders at full layout height behind a
  // scale(0) transform, so any browser scroll-restoration after the DOM
  // commits would land the user partway down the page on mobile.
  useEffect(() => {
    const prevHtml = document.documentElement.style.overscrollBehaviorY;
    const prevBody = document.body.style.overscrollBehaviorY;
    document.documentElement.style.overscrollBehaviorY = "none";
    document.body.style.overscrollBehaviorY = "none";

    const history = window.history;
    const prevRestoration = "scrollRestoration" in history ? history.scrollRestoration : undefined;
    if (prevRestoration !== undefined) history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
    // Re-pin after the next frame in case restoration fires after our initial call.
    const rafId = window.requestAnimationFrame(() => window.scrollTo(0, 0));

    return () => {
      document.documentElement.style.overscrollBehaviorY = prevHtml;
      document.body.style.overscrollBehaviorY = prevBody;
      if (prevRestoration !== undefined) history.scrollRestoration = prevRestoration;
      window.cancelAnimationFrame(rafId);
    };
  }, []);

  // Parallax: IntersectionObserver-driven entrance animation for right-column
  // cards. Adds .is-revealed when the card crosses ~15% into the scroll root.
  //
  // ROOT SELECTION — this is the key correctness decision:
  //   Standalone: scroll happens on the document, so root=null (viewport) is
  //   correct and the observer fires on every page scroll event.
  //   Embedded: scroll happens inside EmbeddedBoundary (position:fixed,
  //   overflow-y:auto). With root=null the observer fires against the full
  //   viewport and can't see the container's overflow clip — cards that have
  //   scrolled outside the panel boundary still appear "intersecting" the
  //   viewport, so exits are missed and re-entries are unreliable. Passing
  //   root=embeddedScrollRef.current fixes this: the observer measures
  //   intersection against the panel's actual visible rectangle, matching
  //   exactly what the user sees.
  //
  // ROOTMARGIN — not used in embedded mode. The EmbeddedBoundary's own clip
  //   boundary is already a tight fit; adding a negative margin would cause
  //   cards to go opacity:0 while still partially visible at the panel edge.
  //   Standalone keeps a small negative bottom margin so cards don't pop
  //   immediately at the raw viewport edge.
  //
  // DELAY — standalone waits 750ms for landContentEmerge to settle so the
  //   two transforms don't visibly compose on first paint. Embedded has no
  //   whole-page scale animation, so it can start the observer sooner; however
  //   we still wait until the desktop-intro stagger timers have all fired
  //   (longest stagger ~6 cards × 80ms = 480ms) so the observer's initial
  //   sweep sees those cards already carrying is-revealed and early-returns
  //   instead of double-triggering the animation.
  useEffect(() => {
    if (isLoading || !emergePlaying) return;
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const rightCol = rightColumnRef.current;
    if (!rightCol) return;

    // Capture at effect-run time; the ref is stable for the component lifetime.
    const observerRoot: Element | null = embedded ? embeddedScrollRef.current ?? null : null;

    let observer: IntersectionObserver | null = null;
    const startTimeout = window.setTimeout(() => {
      const targets = Array.from(rightCol.querySelectorAll<HTMLElement>("[data-parallax-card]"));
      if (targets.length === 0) return;
      const REVEAL_RATIO = 0.15;
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const target = entry.target as HTMLElement;
            if (entry.isIntersecting && entry.intersectionRatio >= REVEAL_RATIO) {
              // Already shown: don't restart while scrubbing within view. The
              // reveal only replays after a full exit (reset below) + re-entry,
              // so jitter near the edge doesn't re-trigger it.
              if (target.classList.contains("is-revealed")) return;
              // Decide which edge the card entered from using the entry's own
              // geometry: if its top sits in the upper half of the root rect it
              // came from above (user scrolled down past it then back) → drop
              // into place from above; otherwise it rose from the bottom.
              const rootTop = entry.rootBounds?.top ?? 0;
              const rootHeight = entry.rootBounds?.height ?? window.innerHeight;
              const fromTop = entry.boundingClientRect.top < rootTop + rootHeight / 2;
              target.classList.toggle("reveal-from-top", fromTop);
              // Promote only for the duration of this reveal, then release the
              // layer so we don't keep every card promoted permanently.
              target.style.willChange = "transform, opacity";
              target.addEventListener(
                "animationend",
                () => {
                  target.style.willChange = "";
                },
                { once: true }
              );
              target.classList.add("is-revealed");
            } else if (!entry.isIntersecting) {
              // With the correct root (EmbeddedBoundary or viewport), this fires
              // only when the card is fully outside the scroll container's visible
              // area — safe to clear is-revealed so the reveal replays on re-entry.
              target.classList.remove("is-revealed");
              target.style.willChange = "";
            }
          });
        },
        {
          root: observerRoot,
          threshold: [0, REVEAL_RATIO],
          // Standalone only: delay reveal trigger slightly below the fold so
          // cards don't pop at the raw viewport edge. Not needed in embedded
          // mode because the container boundary already provides a clean edge.
          rootMargin: embedded ? "0px" : "0px 0px -10% 0px",
        }
      );
      targets.forEach((el) => observer!.observe(el));
      parallaxObserverRef.current = observer;
    }, 750);

    return () => {
      window.clearTimeout(startTimeout);
      observer?.disconnect();
      parallaxObserverRef.current = null;
    };
    // `token` (not just tokenData) is in the deps because the right column — and
    // thus rightColumnRef — only mounts once `token` is set (a tick after tokenData
    // via sortData). Without it this effect runs while the ref is still null and
    // never re-runs to attach the observer.
  }, [isLoading, emergePlaying, tokenData, indicatorsData, token, embedded]);

  // Embedded desktop "open" reveal: stagger the right-column sections that are
  // initially in view so they slide up one after another (the gif slide-in is a
  // CSS entrance on the left column). Cards below the fold stay hidden and are
  // handled by the IntersectionObserver above on scroll. Runs once per open.
  useEffect(() => {
    if (!embedded || isMobile) return;
    if (isLoading || !emergePlaying || didDesktopIntroRef.current) return;
    if (typeof window === "undefined") return;
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const root = rightColumnRef.current;
    if (!root) return;

    const cards = Array.from(root.querySelectorAll<HTMLElement>("[data-parallax-card]"));
    // Cards aren't painted yet (token/tokenData just landed) — don't latch the
    // ref, so this effect re-runs once they render (tokenData is in the deps).
    if (cards.length === 0) return;
    didDesktopIntroRef.current = true;

    if (prefersReducedMotion) {
      // No motion: just make everything visible immediately.
      cards.forEach((el) => el.classList.add("is-revealed"));
      return;
    }

    // Use the EmbeddedBoundary's actual rect in embedded mode so the "in view"
    // check matches what the user can actually see through the panel, not the
    // raw viewport (which would incorrectly include content above topOffset).
    const containerEl = embedded ? embeddedScrollRef.current : null;
    const containerRect = containerEl?.getBoundingClientRect();
    const visibleTop = containerRect?.top ?? 0;
    const visibleBottom = containerRect?.bottom ?? window.innerHeight;
    const timers: number[] = [];
    let order = 0;
    cards.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const inView = rect.bottom > visibleTop && rect.top < visibleBottom * 0.9;
      if (!inView) return; // below-fold cards reveal on scroll via the observer
      // 80ms per card (large items): the 2–4 cards in view land within ~300ms.
      const delay = order * 80;
      order += 1;
      timers.push(
        window.setTimeout(() => {
          el.style.willChange = "transform, opacity";
          el.addEventListener("animationend", () => (el.style.willChange = ""), { once: true });
          el.classList.add("is-revealed");
        }, delay)
      );
    });

    return () => timers.forEach((t) => window.clearTimeout(t));
    // `token` in deps: the right column / rightColumnRef only mounts once token is
    // set (after tokenData), so re-run then to reveal the now-present cards.
  }, [embedded, isMobile, isLoading, emergePlaying, tokenData, token]);

  // Reveal failsafe. The right-column cards default to opacity:0 and rely entirely
  // on the IntersectionObserver / desktop-intro effects adding `.is-revealed`. If
  // that reveal is missed — observer setup starved by wallet-driven refetch churn
  // (notably MetaMask on Firefox toggling isLoading), a latched one-shot, or any
  // timing race — the cards stay invisible forever even though the data loaded.
  // After a grace period past emerge, force-reveal any still-hidden card that is in
  // view (below-fold cards stay hidden so the scroll reveal still works). Re-runs on
  // isLoading/tokenData so it fires once after the last refetch settles.
  useEffect(() => {
    console.log("[reveal failsafe] effect run", {
      emergePlaying,
      isLoading,
      hasRoot: !!rightColumnRef.current,
      embedded,
      isMobile,
    });
    if (!emergePlaying || isLoading) return;
    if (typeof window === "undefined") return;
    const id = window.setTimeout(() => {
      // Query the ref at fire time (not effect-run time) so a ref that attached
      // after this effect ran is still picked up.
      const root = rightColumnRef.current;
      if (!root) {
        console.log("[LandPage reveal failsafe] no rightColumnRef at fire time");
        return;
      }
      // Use the EmbeddedBoundary rect in embedded mode so we only force-reveal
      // cards that are actually visible through the panel, not all cards that
      // happen to fall within the raw viewport rectangle.
      const containerEl = embedded ? embeddedScrollRef.current : null;
      const containerRect = containerEl?.getBoundingClientRect();
      const visibleTop = containerRect?.top ?? 0;
      const visibleBottom = containerRect?.bottom ?? window.innerHeight;
      const all = root.querySelectorAll<HTMLElement>("[data-parallax-card]");
      let forced = 0;
      root.querySelectorAll<HTMLElement>("[data-parallax-card]:not(.is-revealed)").forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom > visibleTop && rect.top < visibleBottom) {
          el.style.willChange = "transform, opacity";
          el.addEventListener("animationend", () => (el.style.willChange = ""), { once: true });
          el.classList.add("is-revealed");
          forced += 1;
        }
      });
      // Diagnostic: how many cards existed, how many were already revealed by the
      // normal path, and how many this failsafe had to force. forced > 0 means the
      // primary reveal (IntersectionObserver / desktop-intro) was missing them.
      const revealed = root.querySelectorAll("[data-parallax-card].is-revealed").length;
      console.log("[LandPage reveal failsafe]", {
        embedded,
        isMobile,
        totalCards: all.length,
        revealedAfterFailsafe: revealed,
        forcedByFailsafe: forced,
      });
    }, 1600);
    return () => window.clearTimeout(id);
    // `token` in deps: re-run once the right column (and its ref) mounts on token.
  }, [emergePlaying, isLoading, tokenData, indicatorsData, embedded, isMobile, token]);

  const effectiveTokenSize = React.useMemo(() => getEffectiveSizeFromMeta(token?.meta), [token?.meta]);

  useEffect(() => {
    if (!isMobile || hoveredEcoScore === null) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setHoveredEcoScore(null);
    }, 1200);

    return () => clearTimeout(timeoutId);
  }, [hoveredEcoScore, isMobile]);

  // Calculate dynamic heights based on tier count for Figma popup
  // const calculateHeights = (tierCount: number) => {
  //   // Base heights for 3 tiers: forest 209px, content 356px, total 565px
  //   const totalHeight = 565;
  //   const baseContentHeight = 356;

  //   // Calculate reduced height based on missing tiers
  //   const missingTiers = 3 - tierCount;
  //   const reducedTierHeight = missingTiers * (17 + 7); // Each tier is 17px height + 7px gap

  //   // Redistribute the saved space to the forest section
  //   const forestHeight = 209 + reducedTierHeight;
  //   const contentHeight = baseContentHeight - reducedTierHeight;

  //   return { forestHeight, contentHeight, totalHeight };
  // };

  // Handle offer amount change with currency suffix
  const handleOfferAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Strip the currency suffix, the "$" prefix and the thousands separators the field now
    // displays — validateNextOffer runs Number() on this, and Number("7,000") is NaN.
    const cleanedValue = value.replace(new RegExp(` ${CURRENCY_NAME}$`), "").replace(/[^0-9.]/g, "");
    validateNextOffer(cleanedValue);
  };

  // Validate next offer function (updated to use original logic)
  const validateNextOffer = (value: string) => {
    setNextOffer(value);
    const num = Number(value);
    if (num <= 0) {
      setNextOfferValid(false);
      setErrorMessage("Offer must be greater than 0");
    } else if (value.includes("e") || value.includes("-") || value.includes(".")) {
      setNextOfferValid(false);
      setErrorMessage("Offer must be a valid number");
    } else if (num > 0 && value.startsWith("0") && !value.includes(".")) {
      setNextOfferValid(false);
      setErrorMessage("Offer must not start with 0");
    } else if (num > Number(balance)) {
      setNextOfferValid(false);
      setErrorMessage("Offer must be less than your balance");
    } else {
      setNextOfferValid(true);
      setErrorMessage("");
    }
  };

  // Standalone fetches on any tokenId/user/library change (unchanged). Embedded
  // distinguishes a land switch from a user/library change:
  //  - land switch → reset to the preview/skeleton; desktop fetches now, mobile
  //    defers until the user scrolls (engage) — see the effect below.
  //  - user/library change on the same land → refetch in place only if the full
  //    detail is already showing, so a wallet connect never drops back to skeleton.
  useEffect(() => {
    if (!tokenId) return;
    const tokenChanged = prevTokenIdRef.current !== tokenId;
    prevTokenIdRef.current = tokenId;

    if (!embedded) {
      console.log("TokenId available, fetching data:", tokenId, contractMax);
      // Same split as the embedded branch below: only a real land switch gets a
      // loud fetch. `user`/`library` resolve a beat after mount, and a loud
      // refetch there unmounts the content subtree — the detail flashes back to
      // the skeleton every time the wallet connects.
      if (tokenChanged) {
        fetchAllData();
      } else if (token) {
        fetchAllData(true);
      }
      return;
    }

    if (tokenChanged) {
      setToken(undefined);
      setTokenData(null);
      setEmergePlaying(false);
      didDesktopIntroRef.current = false;
      // NOTE: deliberately NOT resetting heroIntroPlayedRef / showedLoadLayerRef
      // here. The initial mount also takes this branch (prevTokenIdRef starts
      // undefined), and this passive effect runs AFTER the morph layout effect
      // that just latched them — resetting would make the loaded hero replay
      // its slide-in over the already-in-place preview and skip the skeleton
      // dissolve. The latches re-arm on a real land switch via the morph
      // layout effect (tokenId is in its deps); Explore keys this component by
      // land id anyway, so a switch is a fresh mount with fresh refs.
      embeddedScrollRef.current?.scrollTo({ top: 0 });
      fetchAllData();
      return;
    }

    // Same land, user/library changed: only refresh if the detail is loaded.
    // Pass silent=true so the columns stay visible (no display:none) during the
    // background re-fetch — otherwise the CSS animations on the cards reset and
    // the content appears invisible for ~1 second after the user's wallet connects.
    if (token) fetchAllData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenId, user, library]);

  // Close the gallery flip with Escape; restore focus to the hero card on close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && heroFlipOpen) setHeroFlipOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [heroFlipOpen]);

  useEffect(() => {
    if (!heroFlipOpen) heroCardRef.current?.focus({ preventScroll: true });
  }, [heroFlipOpen]);

  useEffect(() => {
    embeddedScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [heroFlipOpen]);

  // Scrollspy: track which flip media tile is most visible to drive the thumb rail.
  useEffect(() => {
    if (!heroFlipOpen) {
      setActiveFlipIdx(0);
      flipTileObserverRef.current?.disconnect();
      flipTileObserverRef.current = null;
      return;
    }
    // Wait for tiles to animate in (they have entrance delays up to ~600ms).
    const timerId = setTimeout(() => {
      const container = embeddedScrollRef.current;
      if (!container) return;
      const tiles = Array.from(container.querySelectorAll<HTMLElement>("[data-flip-tile]"));
      if (!tiles.length) return;

      const ratios = new Map<Element, number>();
      tiles.forEach((el) => ratios.set(el, 0));

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => ratios.set(entry.target, entry.intersectionRatio));
          let maxRatio = -1;
          let maxIdx = 0;
          ratios.forEach((ratio, el) => {
            if (ratio > maxRatio) {
              maxRatio = ratio;
              maxIdx = Number((el as HTMLElement).dataset.flipTile ?? 0);
            }
          });
          setActiveFlipIdx(maxIdx);
        },
        { root: container, rootMargin: "-20% 0px -20% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
      );

      tiles.forEach((el) => observer.observe(el));
      flipTileObserverRef.current = observer;
    }, 600);

    return () => {
      clearTimeout(timerId);
      flipTileObserverRef.current?.disconnect();
      flipTileObserverRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroFlipOpen]);

  // When the hero card flips open, the GalleryMediaTiles mount with data-parallax-card
  // but the parallax IntersectionObserver was set up before they existed. Register the
  // newly mounted tiles with the existing observer so they get the scroll-reveal treatment.
  useEffect(() => {
    if (!heroFlipOpen) return;
    const rightCol = rightColumnRef.current;
    if (!rightCol) return;
    // Small delay to let React flush the remounted tiles into the DOM.
    const timerId = window.setTimeout(() => {
      const observer = parallaxObserverRef.current;
      if (!observer) return;
      const tiles = Array.from(rightCol.querySelectorAll<HTMLElement>("[data-flip-tile]"));
      tiles.forEach((el) => observer.observe(el));
    }, 50);
    return () => window.clearTimeout(timerId);
  }, [heroFlipOpen]);

  // Scroll the embedded overlay to a specific flip media tile and mark it active.
  const scrollToFlipTile = useCallback((idx: number) => {
    const container = embeddedScrollRef.current;
    if (!container) return;
    const tile = container.querySelector<HTMLElement>(`[data-flip-tile="${idx}"]`);
    if (tile) {
      const containerRect = container.getBoundingClientRect();
      const tileRect = tile.getBoundingClientRect();
      const targetTop =
        container.scrollTop + tileRect.top - containerRect.top - (containerRect.height - tileRect.height) / 2;
      container.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
    }
    setActiveFlipIdx(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async (silent = false) => {
    console.log("Starting fetchAllData");
    if (!silent) setIsLoading(true);
    try {
      console.log("Fetching data for tokenId:", tokenId);
      const allData = await axios.get(ApiRoutes.token.getFullData(tokenId as string)).then((res) => res.data.data);
      console.log("Received data:", allData);

      // Check if allData is undefined or missing essential data
      if (!allData || !allData.token) {
        console.warn("allData is undefined or incomplete, falling back to user.getTokens");
        throw new Error("Incomplete data received");
      }

      setTokenData(allData.token);
      setAuctionData(allData.auction);
      setOffersData(allData.offers);
      setTransactionsData(allData.transactions);
      setTransactionsCountData(allData.transactionCount);
      setIndicatorsData(allData.indicators || []);
      setIsLoading(false);
      console.log("All data fetched and set:", {
        tokenData: allData.token,
        auctionData: allData.auction,
        offersData: allData.offers,
        transactionsData: allData.transactions,
        transactionsCountData: allData.transactionCount,
      });
      console.log("Data set successfully");
    } catch (error) {
      console.error("Failed to fetch all data:", error);
      console.log("Attempting fallback to user.getTokens endpoint");

      try {
        const fallbackData = await axios.get(ApiRoutes.user.getToken(tokenId as string)).then((res) => res.data);
        console.log("Fallback data received:", fallbackData);
        console.log({ tokenId, fallbackData });

        // Find the specific token from the user's tokens
        const token = fallbackData.data;

        if (token) {
          // setTokenData(token);
          // setTokenData({
          //   ...token,
          //   meta: {
          //     ...token?.meta,
          //     location: JSON.parse(token?.meta?.location),
          //   },
          // });
          // // Set other data to null/empty since user.getTokens might not have this data
          // setAuctionData(null);
          // setOffersData([]);
          // setTransactionsData([]);
          // setTransactionsCountData(0);
          // console.log("Fallback token data set successfully");
          setTokenData({
            ...token.token,
            meta: {
              ...token.token?.meta,
              location: JSON.parse(token.token?.meta?.location),
            },
          });
          // A land without an NFT has no on-chain auction or transactions.
          // token.auction from this endpoint is { bidTransactions: [] } — truthy but empty.
          // token.transactions contains the user's global history, not this land's.
          const hasNft = token.token?.etNft != null;
          setAuctionData(hasNft ? token.auction : null);
          setOffersData(token.offers ?? []);
          setTransactionsData(hasNft ? token.transactions ?? [] : []);
          setTransactionsCountData(hasNft ? token.transactionCount ?? 0 : 0);
          setIndicatorsData(token.indicators || []);
          setIsLoading(false);
          // Log what we actually store (not the raw fallback): for a land with no NFT,
          // the endpoint still returns the user's global auction/transaction history,
          // which we intentionally drop so it isn't shown as this land's activity.
          console.log("All data fetched and set (fallback):", {
            hasNft,
            tokenId: token.token?.id,
            auctionData: hasNft ? token.auction : null,
            offersData: token.offers ?? [],
            transactionsCountData: hasNft ? token.transactionCount ?? 0 : 0,
            droppedGlobalTransactions: hasNft ? 0 : token.transactions?.length ?? 0,
          });
          console.log("Data set successfully");
        } else {
          console.error("Token not found in user tokens");
          setTokenData(null);
        }

        setIsLoading(false);
      } catch (fallbackError) {
        console.error("Fallback request also failed:", fallbackError);
        setIsLoading(false);
      }
    } finally {
      if (user?.address) {
        try {
          console.log("Fetching balance for user:", user.address);
          // Read via read-only RPC — balanceOf is a view, no signer needed
          const ercContract = getReadOnlyContract("erc20");
          if (ercContract) {
            const ercBalance = await ercContract.balanceOf(user?.address);
            setBalance(toReadable(ercBalance.toString()).toString());
          }
        } catch (error) {
          console.error("Error getting balance:", error);
        }
      }
    }
  };

  useEffect(() => {
    if (tokenData) {
      sortData();
    }
  }, [tokenData]);

  const sortData = async () => {
    if (tokenData) {
      console.log("Sorting data, current state:", { isLoading, tokenData });

      // Settlement is part of the page payload, not something SettlementPanel fetches for
      // itself once mounted. Left to the panel, the right column painted first and the panel
      // dropped in a round-trip later, shoving every card below it down — so the read is
      // awaited *before* setToken (which is what gates the whole loaded return) and the
      // resolved record is handed to the panel as a prop.
      //
      // Only lands that actually have a settlement pay for this: no winner and no
      // backend-tracked settlementState means no request and no added latency. Same gate as
      // the panel's render slot below.
      const settlementNftId = tokenData?.etNft?.etAuction?.nftId;
      const expectsSettlement =
        settlementNftId !== undefined &&
        settlementNftId !== null &&
        (!!tokenData?.etNft?.etAuction?.winner ||
          (!!tokenData?.etNft?.settlementState && tokenData.etNft.settlementState !== "none"));
      // A failed read resolves to null and the panel is simply not rendered — the page never
      // waits on a retry.
      setSettlement(expectsSettlement ? (await fetchSettlement(settlementNftId)).settlement : null);

      // tokenData is already the token object, no need to destructure
      setToken(tokenData);
      if (tokenData?.offerStatus === "opened") {
        setOpenToOffers(true);
      }

      setClaimMintData({
        name: tokenData?.meta?.name || "",
        content: tokenData?.content || "",
        targetDate: moment(tokenData?.etNft?.easementActivated?.contractStart)
          .add(tokenData?.meta?.contractPeriod, TERM_UNIT)
          .toDate(),
      });

      const approvedItems = tokenData?.verifications?.filter((item: any) => item.status === "approved");
      const latestApprovedVerification = approvedItems?.length
        ? approvedItems.sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]
        : null;

      const auctionEnd = tokenData?.etNft?.etAuction?.auctionEnd;
      const verificationsAfterEndAuction = auctionEnd
        ? (tokenData?.verifications ?? []).filter(
            (v: any) => v?.createdAt && new Date(v.createdAt).getTime() > new Date(auctionEnd).getTime()
          )
        : [];
      const latestVerificationAfterAuction = verificationsAfterEndAuction.length
        ? verificationsAfterEndAuction.sort(
            (a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          )[0]
        : null;

      // Fetch on-chain verification counts to gate the next-verification point.
      // Only fetch for sold lands — unsold lands have no active easement contract.
      let onChainTotalVerifications: number | null = null;
      let onChainVerificationsPerformed: number | null = null;
      if (tokenData?.etNft?.status === "sold" && tokenData?.etNft?.id) {
        try {
          const coreContract = getReadOnlyContract("ecofutures-core");
          if (coreContract) {
            const easementResult = await coreContract.getEasement(tokenData.etNft.id);
            const { totalVerifications, verificationsPerformed } = easementResult.toObject();
            onChainTotalVerifications = Number(totalVerifications);
            onChainVerificationsPerformed = Number(verificationsPerformed);
            console.log("[LandPage chart] on-chain easement counts:", {
              onChainTotalVerifications,
              onChainVerificationsPerformed,
            });
          }
        } catch (e) {
          console.error("[LandPage chart] Failed to fetch on-chain easement data:", e);
        }
      }

      const appendNextVerificationPoint = (
        points: {
          date: string;
          ecoScore: number | null;
          ecoScoreVersion?: string;
          createdAt?: string;
          nextVerificationEcoScore?: number | null;
        }[]
      ) => {
        if (!points.length) {
          return points;
        }

        // Only show the next-verification point when there are still verifications
        // outstanding on-chain. If all scheduled verifications are complete, the
        // point would be misleading.
        if (onChainTotalVerifications !== null && onChainVerificationsPerformed !== null) {
          if (onChainVerificationsPerformed >= onChainTotalVerifications) {
            return points;
          }
        }

        // Reference date for the next scheduled verification:
        // - If a verification was recorded after the auction ended, the post-sale chain has begun; roll from it.
        // - Else if the auction has ended (sale just completed), schedule from endAuction.
        // - Else fall back to the latest approved verification (pre-sale).
        let referenceMoment: moment.Moment | null = null;
        if (latestVerificationAfterAuction?.updatedAt) {
          referenceMoment = moment(latestVerificationAfterAuction.updatedAt);
        } else if (auctionEnd) {
          referenceMoment = moment(auctionEnd);
        } else if (latestApprovedVerification?.updatedAt) {
          referenceMoment = moment(latestApprovedVerification.updatedAt);
        }

        if (!referenceMoment || !referenceMoment.isValid()) {
          return points;
        }

        const nextVerificationDate = referenceMoment
          .add(VERIFICATION_INTERVAL_VALUE, VERIFICATION_INTERVAL_UNIT)
          .toISOString();
        const latestEcoScore = Number(latestApprovedVerification?.ecoScore);
        const normalizedLatestEcoScore = Number.isFinite(latestEcoScore) ? latestEcoScore : 0;

        return [
          ...points,
          {
            date: nextVerificationDate,
            // Keep the primary line continuous to the future verification point.
            ecoScore: normalizedLatestEcoScore,
            nextVerificationEcoScore: normalizedLatestEcoScore,
            ecoScoreVersion: latestApprovedVerification?.ecoScoreVersion,
          },
        ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      };

      const contractStart = tokenData?.etNft?.easementActivated?.createdAt;
      const buildContractStartPoint = (
        sortedPoints: { date: string; ecoScore: number | null; ecoScoreVersion?: string }[]
      ) => {
        if (!contractStart || !sortedPoints.length) return null;
        const contractStartDate = new Date(contractStart);
        if (Number.isNaN(contractStartDate.getTime())) return null;
        return {
          date: contractStartDate.toISOString(),
          createdAt: contractStartDate.toISOString(),
          updatedAt: contractStartDate.toISOString(),
          ecoScore: sortedPoints[0].ecoScore,
          contractStartEcoScore: sortedPoints[0].ecoScore,
          ecoScoreVersion: sortedPoints[0].ecoScoreVersion,
        };
      };

      if (approvedItems?.length === 1) {
        const approvedItems = tokenData?.verifications
          .filter((item: any) => item.status === "approved")
          .map((item: any) => ({
            date: item.updatedAt,
            ecoScore: item.ecoScore,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            ecoScoreVersion: item.ecoScoreVersion,
          }))
          .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const contractStartPoint = buildContractStartPoint(approvedItems);
        const pointsWithStart = (contractStartPoint ? [contractStartPoint, ...approvedItems] : approvedItems).sort(
          (a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        const finalPoints = appendNextVerificationPoint(pointsWithStart);
        console.log("[LandPage chart] branch=single approvedItems:", approvedItems);
        console.log("[LandPage chart] branch=single contractStartPoint:", contractStartPoint);
        console.log("[LandPage chart] branch=single pointsWithStart (sorted):", pointsWithStart);
        console.log("[LandPage chart] branch=single finalPoints -> setCharts:", finalPoints);
        setCharts(finalPoints);
      } else if (approvedItems?.length > 1) {
        const approvedItems = tokenData?.verifications
          .filter((item: any) => item.status === "approved")
          .map((item: any) => ({
            date: new Date(new Date(item.updatedAt).getTime() + 1).toISOString(),
            ecoScore: item.ecoScore,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            ecoScoreVersion: item.ecoScoreVersion,
          }))
          .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const contractStartPoint = buildContractStartPoint(approvedItems);
        const pointsWithStart = (contractStartPoint ? [contractStartPoint, ...approvedItems] : approvedItems).sort(
          (a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        const finalPoints = appendNextVerificationPoint(pointsWithStart);
        console.log("[LandPage chart] branch=multi approvedItems:", approvedItems);
        console.log("[LandPage chart] branch=multi contractStartPoint:", contractStartPoint);
        console.log("[LandPage chart] branch=multi pointsWithStart (sorted):", pointsWithStart);
        console.log("[LandPage chart] branch=multi finalPoints -> setCharts:", finalPoints);
        setCharts(finalPoints);
      }

      const etNft = tokenData?.etNft;

      // Role detection must run for pre-mint tokens too — etNft is null until the
      // EFT is minted, so these fall back to the token-level owner/minter/trust
      // addresses. Without this the Guardian/Owner isn't recognised on a
      // verification_required land and the "You are the Guardian" blue box never shows.
      if (etNft?.ownerAddress === user?.address || tokenData?.ownerAddress === user?.address) {
        setCanAcceptOffer(true);
        setLandOwner(true);
      }
      if (tokenData?.trust?.address === user?.address) {
        setLandTrust(true);
      }
      if (etNft?.minterAddress === user?.address || tokenData?.minterAddress === user?.address) {
        setLandGuardian(true);
      }

      if (etNft) {
        if (etNft?.easementActivated) {
          const endContract = moment(etNft.easementActivated.contractStart).add(
            tokenData.meta?.contractPeriod,
            TIME_DISPLAY_UNIT
          );
          if (endContract.isBefore(moment())) {
            setLandActive(false);
            console.log("Land is inactive, contract has ended.");
            // Check if there are remaining trees for expired lands
            const total = formatValue(etNft?.totalTreeForEasement as any, false);
            const claimed = formatValue(etNft?.easementActivated?.rewardClaimed as any, false) || 0;
            const remaining = total - claimed;
            setHasRemainingTrees(remaining > 0);
          } else {
            setLandActive(true);
            setHasRemainingTrees(false);
            console.log("Land is active, contract is still ongoing.");
          }

          const rewardData: RewardData = {
            guardianReward: formatValue(etNft?.easementActivated?.guardianRedeemedReward, false),
            patronReward: formatValue(etNft?.easementActivated?.patronRedeemedReward, false),
            referralReward: formatValue(etNft?.easementActivated?.referralRedeemedReward, false),
          };

          setRewardData(rewardData);
        }
        if (etNft?.easementActivated?.rewardClaimed && etNft?.easementActivated?.treeTotal) {
          console.log("Calculating total and remaining trees for easement activated");

          const total = toReadable(etNft?.easementActivated.treeTotal, true, false) as number;
          const claimed = toReadable(etNft?.easementActivated.rewardClaimed, true, false) as number;
          console.log("Total trees:", total, "Claimed trees:", claimed);
          const remaining = total - claimed;
          setRemainingTrees(remaining > 0 ? remaining : 0);
        }
        document.title = tokenData?.meta?.name + " | " + APP_TITLE;

        let location = tokenData?.meta?.location;
        try {
          if (typeof location === "string") {
            location = JSON.parse(location);
          }
        } catch (e) {
          console.error("Error parsing location", e);
        }

        setToken({
          ...tokenData,
          meta: {
            ...tokenData?.meta,
            location,
          },
        });
      }
    }
    // if (auctionData) {
    // }
    // if (offersData) {
    // }
    // if (transactionsData) {
    // }
  };

  useEffect(() => {
    if (!auctionData?.auctionStart) return;

    const { auctionStart, status, winner, duration: auctionDuration, bids } = auctionData;

    // The opening price until the first bid — endingPrice is "0" until then (auctionCurrentPrice).
    const currentPrice = auctionCurrentPrice(auctionData);
    setAuctionCurrentBid(currentPrice ? formatValue(currentPrice, false) : 0);
    setAuctionHasBids((bids?.length || 0) > 0);

    if (bids?.length > 0) {
      setAuctionHighestBidderAddress(bids[bids.length - 1]?.bidder || "");
    }

    const startedTs = moment(auctionStart).valueOf();
    const endTime = moment(startedTs + auctionDuration);
    setAuctionEndDate(endTime.toDate());

    const isEnded = status === "sold" || status === "ended" || status === "completed" || status === "cancelled";
    setAuctionEnded(isEnded);

    if (token?.etNft?.totalTreeForEasement) {
      const total = formatValue(token.etNft.totalTreeForEasement, false);
      const claimed = formatValue(
        token.etNft?.easementActivated?.rewardClaimed ? token.etNft.easementActivated.rewardClaimed : 0,
        false
      );
      const remaining = total - (claimed || 0) > 0 ? total - (claimed || 0) : 0;
      const rate = appContext?.mintRate;
      if (rate) {
        setAuctionRemainingTrees(formatValue(remaining * rate, true, 2));
        setAuctionTreesValue(`(@ $${formatValue(rate, true, 4)}/TR3)`);
      } else {
        setAuctionRemainingTrees(remaining);
      }
      const etNft = token.etNft;
      const _winner = winner || (etNft?.minterAddress !== etNft?.ownerAddress ? etNft?.ownerAddress : "");
      setAuctionWinnerAddress(_winner || null);
    }

    setHasAuction(true);
    setIsAuctionLoading(false);
  }, [auctionData, token?.etNft, appContext?.mintRate]);

  // An expired auction is settled by two backend workers, each on a one-minute
  // schedule: the first flips `started` -> `completed`, the second sends
  // endAuction on-chain and the event handler writes `ended` (plus the winner
  // and the opening settlement state). Nothing pushes either step to the
  // client, so a page left open across the end of an auction would sit on the
  // stale status until a manual reload — the countdown hits zero and the view
  // never moves on: no winner, no relist action for the guardian, no settlement
  // panel for the buyer. Refresh until the auction reaches a settled status.
  useEffect(() => {
    const status = token?.etNft?.etAuction?.status;
    const endsAt = token?.etNft?.etAuction?.auctionEnd;
    if (status !== "started" && status !== "completed") {
      // Settled, or no auction at all — re-arm the budget for the next one.
      settleGiveUpAtRef.current = null;
      return undefined;
    }

    const interval = setInterval(() => {
      // A live auction has nothing to poll for yet; this only starts doing work
      // once the clock runs out, which happens with no dep here changing.
      if (endsAt && new Date(endsAt).getTime() > Date.now()) {
        return;
      }
      // The budget lives in a ref, not the closure: every refresh replaces
      // `auctionEndDate` with a fresh Date and re-runs this effect, which would
      // otherwise keep resetting the deadline and poll forever.
      if (settleGiveUpAtRef.current === null) {
        settleGiveUpAtRef.current = Date.now() + AUCTION_SETTLE_POLL_TIMEOUT_MS;
      }
      if (Date.now() > settleGiveUpAtRef.current) {
        clearInterval(interval);
        return;
      }
      // Silent: a background refresh must not swap the page for a spinner.
      fetchAllData(true);
    }, AUCTION_SETTLE_POLL_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token?.etNft?.etAuction?.status, token?.etNft?.etAuction?.auctionEnd]);

  // Whether the auction has reached a status that carries its outcome. `completed`
  // deliberately does not count: it only means the auction expired and the backend
  // has yet to send endAuction, so no winner is recorded yet. Reading it as settled
  // makes the price box announce a sold auction as "Not sold" for the minute in
  // between — and the auction is still open on-chain until endAuction lands.
  const auctionOutcomeKnown =
    token?.etNft?.status === "sold" ||
    token?.etNft?.etAuction?.status === "ended" ||
    token?.etNft?.etAuction?.status === "cancelled";

  const colorPalette = ["#000", "#000", "#000"];

  useEffect(() => {
    const initializeData = async () => {
      try {
        await getLandSizeLimits();
      } catch (error) {
        console.error("Failed to initialize data:", error);
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only fetch highest offer after token is loaded and if offers are open
    if (token?.offerStatus === "opened" && token?.id) {
      fetchHighestOffer();
    }

    // Handle auction loading state
    if (token?.etNft?.etAuction === null || token?.etNft === null) {
      setIsAuctionLoading(false);
    }

    // Get the activated easement level to determine which tiers are active
    const activatedLevel = token?.etNft?.easementActivated?.level;
    console.log({ activatedLevel });

    // Set bidding tiers
    if (token?.meta) {
      const sortedEasements = [...(token?.easements || [])].sort(
        (a, b) => Number(a?.level ?? Number.MAX_SAFE_INTEGER) - Number(b?.level ?? Number.MAX_SAFE_INTEGER)
      );

      const currentBidNum = Number(auctionCurrentBid) || 0;
      const isOnAuction = !!token?.etNft?.etAuction && token?.etNft?.etAuction?.status === "started";

      const isTierActive = (tierPrice: string | number | undefined, easementLevel?: number): boolean => {
        // Easement already activated on-chain takes priority
        if (easementLevel !== undefined && activatedLevel !== undefined && activatedLevel >= easementLevel) return true;
        if (easementLevel === undefined && activatedLevel !== undefined && activatedLevel >= 0) return true;
        // During an active auction, mark tier active if current bid meets or exceeds its price.
        // Bids only: before the first one the current bid is the opening price, which is Tier 1's.
        if (isOnAuction && auctionHasBids && currentBidNum > 0 && tierPrice !== undefined) {
          return currentBidNum >= formatValue(tierPrice as string, false);
        }
        return false;
      };

      const nextBiddingTiers = [
        {
          title: "Tier 1",
          description: token?.meta?.bfd_plan,
          perks: token?.meta?.basicPerks,
          price: token?.meta?.startingBid,
          // basicPerks can be undefined on minimal-meta lands — guard before spreading.
          details: [token?.meta?.bfd_plan, ...((token?.meta?.basicPerks as string[]) ?? [])],
          active: isTierActive(token?.meta?.startingBid),
        },
        ...sortedEasements.map((easement) => ({
          title: `Tier ${easement.level + 1}`,
          description: easement.description,
          perks: easement.perks,
          price: easement.activationBid,
          details: [easement?.description, ...(easement.perks ?? [])],
          active: isTierActive(easement.activationBid, easement.level),
        })),
      ];

      setBiddingTiers(nextBiddingTiers);
    }
  }, [token, auctionCurrentBid, auctionHasBids]);

  // Update land features accordion expansion state when screen size changes
  // useEffect(() => {
  //   setLandFeaturesExpanded(!isMobile);
  // }, [isMobile]);

  const fetchHighestOffer = () => {
    try {
      if (!token?.id) {
        console.log("Token ID not available, skipping highest offer fetch");
        return;
      }

      const offers = offersData;
      if (Array.isArray(offers) && offers.length > 0) {
        // Find the offer with the highest amount
        console.log("Highest offer data:", offers);
        const highest = offers.reduce(
          (max, offer) => (Number(offer.amount) > Number(max.amount) ? offer : max),
          offers[0]
        );
        console.log("Calculated highest offer:", highest, Number(highest.amount));
        setHighestOffer(Number(highest.amount));
        setHighestOfferBuyer(highest.buyer ?? null);
      } else {
        setHighestOffer(null);
        setHighestOfferBuyer(null);
      }
    } catch (error) {
      setHighestOffer(null);
      setHighestOfferBuyer(null);
      console.error("Error fetching highest offer:", error);
    }
  };

  useEffect(() => {
    // etNft is null until the EFT is minted; fall back to the token-level owner/
    // minter so the Guardian of a pre-mint (verification_required) land isn't
    // mis-flagged as canOffer=true on their own token.
    const ownerAddress = token?.etNft?.ownerAddress ?? token?.ownerAddress;
    const minterAddress = token?.etNft?.minterAddress ?? token?.minterAddress;
    if (user?.address === ownerAddress) {
      setCanOffer(false);
      setCanAcceptOffer(true);
    } else if (user?.address === minterAddress) {
      setCanOffer(false);
      setCanAcceptOffer(false);
    } else {
      setCanOffer(true);
      setCanAcceptOffer(false);
    }
  }, [user, token]);

  // Define a type for bid value items to allow any string key
  type RewardData = {
    guardianReward: number;
    patronReward: number;
    referralReward: number;
  };

  useEffect(() => {
    if (nextOffer === "") {
      setErrorMessage("");
    }
  }, [nextOffer]);

  const getLandSizeLimits = async () => {
    try {
      // Read via read-only RPC so page mount never prompts MetaMask
      const contract = getReadOnlyContract("tree-token");
      if (!contract) {
        console.log("Contract not available");
        return;
      }

      const max = await contract.maximumEasementLandSize();
      const parcels = Number(max);
      const maxContract = await contract.calculateMaxEasementPeriodForLandSize(parcels);
      console.log("Land size limits fetched:", { parcels, maxContract });
      const resMax = Number(duration(Number(maxContract), "s").as(TERM_UNIT).toFixed(0));
      setContractMax(resMax);
      // setParcelMax(parcels);
    } catch (e: any) {
      console.log("Failed getting land size limits", { error: e });
      consoleError(e);
    }
  };

  // Helper function to derive level from value
  const getLevelFromValue = (value: number): string => {
    if (value <= 0.5) return "barren";
    if (value <= 1.5) return "marginal";
    if (value <= 2.5) return "moderate";
    if (value <= 3.5) return "abundant";
    if (value <= 4.5) return "rich";
    return "pristine";
  };

  const getIndicatorValueDescription = (indicator: any, assessmentValue: number): string => {
    const parameters = Array.isArray(indicator?.parameters)
      ? indicator.parameters.filter((p: any) => Number.isFinite(Number(p?.value)))
      : [];

    if (parameters.length === 0) {
      return indicator?.description || "";
    }

    const exactMatch = parameters.find((p: any) => Number(p.value) === assessmentValue);
    if (exactMatch?.description) {
      return exactMatch.description;
    }

    const nearestParameter = parameters.reduce((closest: any, current: any) => {
      const closestDistance = Math.abs(Number(closest.value) - assessmentValue);
      const currentDistance = Math.abs(Number(current.value) - assessmentValue);
      return currentDistance < closestDistance ? current : closest;
    }, parameters[0]);

    return nearestParameter?.description || indicator?.description || "";
  };

  const getAttributes = () => {
    const attributes: any = [];

    // Check if we have indicators and assessments data
    if (!indicatorsData?.length || !token?.assessments?.length) {
      return <></>;
    }

    console.log({ indicatorsData, assessments: token.assessments });

    // Helper to check if an indicator is a biodiversity type
    const isBiodiversityIndicator = (tag: string) => tag?.toLowerCase().includes("biodiversity");

    // Sort all indicators by orderKey
    const sortedIndicators = [...indicatorsData].sort(
      (a: any, b: any) => parseInt(a.orderKey || "0") - parseInt(b.orderKey || "0")
    );

    // Collect all biodiversity indicators for combined processing
    const biodiversityIndicators = sortedIndicators.filter((ind: any) => isBiodiversityIndicator(ind.tag));

    // Track if we've already added the combined biodiversity attribute
    let biodiversityAdded = false;

    // Process indicators in orderKey order
    sortedIndicators.forEach((indicator: any) => {
      if (isBiodiversityIndicator(indicator.tag)) {
        // When we encounter first biodiversity indicator, add the combined attribute
        if (!biodiversityAdded && biodiversityIndicators.length > 0) {
          biodiversityAdded = true;

          const biodiversityAssessments = biodiversityIndicators
            .map((ind: any) => {
              const assessment = token?.assessments?.find((a: any) => a.indicatorId === ind.id);
              if (assessment) {
                const description = getIndicatorValueDescription(ind, assessment.value);
                // Extract the type name (e.g., "Flora" from "biodiversity flora")
                const typeName = ind.tag?.toLowerCase().replace("biodiversity", "").trim();
                const capitalizedType = typeName.charAt(0).toUpperCase() + typeName.slice(1);
                return {
                  indicator: ind,
                  assessment,
                  description,
                  typeName: capitalizedType,
                  zoneScores: normalizeZoneScores((assessment as { zoneScores?: unknown }).zoneScores),
                };
              }
              return null;
            })
            .filter(Boolean);

          if (biodiversityAssessments.length > 0) {
            // Calculate average value across all biodiversity indicators
            const averageValue =
              biodiversityAssessments.reduce((sum: number, item: any) => sum + item.assessment.value, 0) /
              biodiversityAssessments.length;
            const level = getLevelFromValue(averageValue);

            // Combine descriptions
            const combinedDescription = biodiversityAssessments
              .map((item: any) => `${item.typeName}: ${item.description}`)
              .join("\n \n ");

            const combinedZoneScoreBuckets = biodiversityAssessments.reduce(
              (acc: Record<string, number[]>, item: any) => {
                item.zoneScores.forEach((zoneScore: ZoneScoreMarker) => {
                  if (!acc[zoneScore.zoneName]) {
                    acc[zoneScore.zoneName] = [];
                  }
                  acc[zoneScore.zoneName].push(zoneScore.score);
                });
                return acc;
              },
              {}
            );

            const combinedZoneScores: ZoneScoreMarker[] = Object.entries(combinedZoneScoreBuckets)
              .map(([zoneName, scores]) => ({
                zoneName,
                score:
                  scores.length > 0 ? clampZoneScore(scores.reduce((sum, score) => sum + score, 0) / scores.length) : 0,
              }))
              .sort((a, b) => a.zoneName.localeCompare(b.zoneName));

            const combinedAssessmentsMap = [
              {
                advanceTag: "Biodiversity",
                description: "",
                valueDescription: combinedDescription,
              },
            ];

            attributes.push(
              <Attribute
                key="biodiversity"
                type="biodiversity"
                color={level}
                value={averageValue}
                assessmentsMap={combinedAssessmentsMap}
                zoneScores={combinedZoneScores}
              />
            );
          }
        }
        // Skip individual biodiversity indicators (already handled in combined)
        return;
      }

      // Process non-biodiversity indicators normally
      const assessment = token?.assessments?.find((a: any) => a.indicatorId === indicator.id);

      if (assessment) {
        const value = assessment.value;
        const level = getLevelFromValue(value);

        // Get description from indicator parameters based on value
        const parameterDescription = getIndicatorValueDescription(indicator, value);

        const assessmentsMapEntry = [
          {
            advanceTag: indicator.advance || indicator.tag,
            description: indicator.description || "",
            valueDescription: parameterDescription,
            parameterDescriptions: Array.isArray(indicator?.parameters)
              ? indicator.parameters
                  .map((parameter: any) => ({
                    value: Number(parameter?.value),
                    description: String(parameter?.description || ""),
                  }))
                  .filter(
                    (parameter: { value: number; description: string }) =>
                      Number.isFinite(parameter.value) && parameter.description.length > 0
                  )
              : [],
          },
        ];

        const zoneScores = normalizeZoneScores((assessment as { zoneScores?: unknown }).zoneScores);

        attributes.push(
          <Attribute
            key={indicator.tag}
            type={indicator.tag}
            color={level}
            value={value}
            assessmentsMap={assessmentsMapEntry}
            zoneScores={zoneScores}
          />
        );
      } else {
        console.log(`No assessment found for indicator: ${indicator.tag} (id: ${indicator.id})`);
      }
    });

    return attributes;
  };

  // Embedded opens on a popup tap, so the full fetch is already in flight.
  // Instead of a bare spinner, mirror the loaded two-column geometry: the hero
  // renders immediately from the lightweight map token (FLIP-morphing in from
  // the popup card — see the layout effect above) while the right panel shows
  // the geo-ping + section skeletons until the payload lands. Clicking the
  // surrounding map/backdrop still dismisses back to the popup.
  const previewLocation = (() => {
    const raw = previewToken?.meta?.location;
    if (!raw) {
      return null;
    }
    try {
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
      return null;
    }
  })();
  // Same remaining-trees math as the popup/loaded hero, off the preview fields.
  const previewRemainingTrees = previewToken?.etNft?.easementActivated
    ? Number(formatValue(previewToken.etNft.totalTreeForEasement as any, false)) -
      Number(formatValue(previewToken.etNft.easementActivated.rewardClaimed as any, false) || 0)
    : null;
  // Preview token with the parsed location spliced in, shaped like the rich
  // token so EmbeddedHeroCard renders identically from either.
  const previewHeroToken = previewToken?.content
    ? { ...previewToken, meta: { ...previewToken.meta, location: previewLocation } }
    : null;

  const embeddedLoading = (
    <EmbeddedBoundary embedded={embedded} topOffset={topOffset} onBackdropClick={onClose} scrollRef={embeddedScrollRef}>
      {/* TREE ALIGNMENT — the {false} placeholders mirror the loaded return's
          conditional child slots (outer: standalone spinner / no-data; inner:
          LandMapBackground, draft Attention, mobile RoleBanner) so the column
          Boxes — and the EmbeddedHeroCard inside — sit at the same child
          positions in both states. React then reconciles the loading→loaded
          swap in place instead of remounting, which is what keeps the hero
          <img>/gif alive (no refresh) and its copy from re-running heroTextRise
          when the payload lands. Keep the shapes in sync if either side changes. */}
      {false}
      {false}
      <>
        {false}
        {false}
        {false}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
            alignItems: "flex-start",
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* Left column mirror — same widths/margins as loaded. */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              position: { md: "sticky" },
              top: { md: "var(--layout-header-height, 100px)" },
            }}
          >
            {previewHeroToken ? (
              <EmbeddedHeroCard
                heroToken={previewHeroToken}
                isMobile={isMobile}
                remainingTrees={previewRemainingTrees}
                heroRef={previewHeroRef}
              />
            ) : (
              // Deep link for a land the map dataset doesn't carry — no preview
              // data to paint, so the hero slot is a shimmer block. (The id keeps
              // the backdrop-click ignore list and the reverse morph working.)
              // Same box as EmbeddedHeroCard, so the loaded hero replaces it in
              // place rather than jumping when the payload lands.
              <Box
                id="image-block"
                ref={previewHeroRef}
                sx={{
                  position: "relative",
                  width: { xs: "85%", md: LEFT_COL_CARD_WIDTH_MD },
                  aspectRatio: "1 / 1",
                  mr: { xs: "7.5%", md: "8.5%" },
                  ml: { xs: "7.5%", md: "16.5%" },
                  mt: { xs: "7.5%", md: HERO_TOP_MARGIN_MD },
                  borderRadius: "20px",
                  overflow: "hidden",
                }}
              >
                <SkeletonBlock sx={{ position: "absolute", inset: 0 }} />
              </Box>
            )}
          </Box>

          {/* Right column mirror — rises in ~250ms after the morph starts. Fills
              `backwards` so it holds hidden through the delay but keeps no
              transform once landed (the loaded right column must not carry one —
              see the parallax note there; this is the same DOM node). */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              mr: { md: "6.25%" },
              animation: `${panelIn} 400ms cubic-bezier(0.22, 1, 0.36, 1) 250ms backwards`,
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <EmbeddedLoadLayer />
          </Box>
        </Box>
      </>
    </EmbeddedBoundary>
  );

  // Must be before any early-return so hook order is stable across renders.
  const galleryItems = React.useMemo((): MediaFile[] => {
    if (!token) return [];
    const eftFile: MediaFile = {
      id: `eft-${token.id}`,
      url: token.content || "",
      name: token.meta?.name || "EFT",
      type: "image/x-eft",
    };
    const verificationVideo = (token as any)?.verifications?.find((v: any) => v?.videoUrl);
    const videos: MediaFile[] = verificationVideo?.videoUrl
      ? [
          {
            id: `verification-video-${verificationVideo.id ?? 0}`,
            url: verificationVideo.videoUrl as string,
            name: "Verification video",
          },
        ]
      : [];
    const docTypeOrder: Record<string, number> = { easement_contract: 0, survey_plan: 1, additional: 2 };
    const docs: MediaFile[] = (token.files || [])
      .filter((f) => f.type === "additional" || f.type === "survey_plan" || f.type === "easement_contract")
      .map((f) => ({ id: f.id, url: f.url, name: f.name || "", type: f.type }))
      .sort((a, b) => (docTypeOrder[a.type ?? ""] ?? 99) - (docTypeOrder[b.type ?? ""] ?? 99));
    return [eftFile, ...videos, ...docs];
  }, [token]);

  if (!token) {
    if (embedded) return embeddedLoading;
    return (
      <CenteredContainer>
        <CircularProgress />
      </CenteredContainer>
    );
  }

  const meta = token.meta as TokenMeta;

  if (!meta) {
    if (embedded) return embeddedLoading;
    return (
      <CenteredContainer>
        <CircularProgress />
      </CenteredContainer>
    );
  }

  const shouldShowActiveTime = () => {
    if (token.etNft?.easementActivated?.contractStart) {
      const end = moment(token.etNft.easementActivated.contractStart).add(
        token.meta?.contractPeriod,
        TIME_DISPLAY_UNIT
      );
      return end.isAfter();
    } else {
      return false;
    }
  };

  const isContractExpired = () => {
    if (token.etNft?.easementActivated?.contractStart) {
      const end = moment(token.etNft.easementActivated.contractStart).add(
        token.meta?.contractPeriod,
        TIME_DISPLAY_UNIT
      );
      return end.isBefore();
    }
    return false;
  };

  const getTokenAttributes = () => {
    const list: any[] = [];
    // Un-minted / minimal-meta lands (e.g. status "verification_required") may omit
    // these arrays entirely, so guard each before filtering — `?.` covers a missing
    // `meta` but not a missing array field.
    (token.meta?.biomes ?? []).filter((item) => !!item).forEach((item) => list.push(item));
    (token.meta?.flora ?? []).filter((item) => !!item).forEach((item) => list.push(item));
    (token.meta?.fauna ?? []).filter((item) => !!item).forEach((item) => list.push(item));
    (token.meta?.production ?? []).filter((item) => !!item).forEach((item) => list.push(item));
    (token.meta?.dwellings ?? []).filter((item) => !!item).forEach((item) => list.push(item));
    return (
      <Tags>
        {list.map((item, index) => (
          <button
            style={{ border: "none", background: "none", padding: 0, margin: 0, cursor: "pointer" }}
            onClick={() =>
              embedded && onSearch ? onSearch(item) : navigator(AppRoutes.Explore.path + `?search=${item}`)
            }
            key={index}
          >
            <span key={index}>{item}</span>
          </button>
        ))}
      </Tags>
    );
  };

  const getSurveyFile = () => {
    const file = token.files?.find((item) => item.type === "survey_plan");
    return file || null;
  };

  const getContractFile = () => {
    const file = token.files?.find((item) => item.type === "easement_contract");
    return file || null;
  };

  const placeOffer = async () => {
    setOfferLoading(true);
    setErrorMessage("");
    setInfoMessage("");

    try {
      const signer = await getMetaMaskSigner();
      const NFTContract = getContract("ecofutures-token", signer);
      const ERCContract = getContract("erc20", signer);
      const tokenId = Number(token?.etNft?.id as any);
      const amount = Number(nextOffer);
      if (NFTContract && tokenId && ERCContract) {
        try {
          // Check if user has a previous offer
          let previousOfferAmount = 0;
          let difference = amount;
          if (Array.isArray(offersData) && offersData.length > 0 && user?.address) {
            const userPreviousOffer = offersData.find(
              (offer: any) => offer.buyer?.toLowerCase() === user.address.toLowerCase()
            );

            if (userPreviousOffer) {
              previousOfferAmount = Number(userPreviousOffer.amount);
              difference = amount - previousOfferAmount;

              if (difference > 0) {
                console.log(
                  `Increase allowance: Previous offer ${previousOfferAmount}, New offer ${amount}, Increase amount: ${difference}`
                );
              } else if (difference < 0) {
                console.log(
                  `Decrease allowance: Previous offer ${previousOfferAmount}, New offer ${amount}, Decrease amount: ${Math.abs(
                    difference
                  )}`
                );
              } else {
                console.log(`Same offer amount: ${amount}`);
              }
            } else {
              console.log(`New offer from user: ${amount} (no previous offer found)`);
            }
          }

          // Call the appropriate allowance function based on the difference
          let awaitTREEApproval;
          if (difference > 0) {
            // Increase allowance for new or higher offer
            awaitTREEApproval = await ERCContract.increaseAllowance(
              ecoFuturesTokenContractAddress,
              Math.abs(difference)
            );
          } else if (difference < 0) {
            // Decrease allowance for lower offer
            awaitTREEApproval = await ERCContract.decreaseAllowance(
              ecoFuturesTokenContractAddress,
              Math.abs(difference)
            );
          } else {
            // Same amount, still need to set allowance
            awaitTREEApproval = await ERCContract.increaseAllowance(ecoFuturesTokenContractAddress, amount);
          }

          const r = await checkTransactionConfirmationReceipt(awaitTREEApproval.hash, library);
          const receipt = r as any;
          if (receipt?.status !== "0") {
            setInfoMessage("Offer approved");
            const placeNFTOffer = await NFTContract.placeOfferOnNFT(tokenId, amount, ercContractAddress);
            const r = await checkTransactionConfirmationReceipt(placeNFTOffer.hash, library);
            const receipt = r as any;
            if (receipt?.status !== "0") {
              setInfoMessage("Offer placed");
              setNextOffer("");
              setErrorMessage("");
              setTimeout(() => {
                setOfferModal(false);
                setInfoMessage("");
                setErrorMessage("");
                // Silent, so the new highest offer flips in the price box (DigitFlip); a loud
                // refetch re-emerges the whole page and the flip is lost inside it.
                fetchAllData(true);
                setOfferLoading(false);
              }, 5000);
            }
          }
        } catch (error: any) {
          // Handle MetaMask rejection
          if (error.code === 4001 || error.message?.includes("User rejected") || error.code === "ACTION_REJECTED") {
            setErrorMessage("Transaction was rejected by user");
            return;
          }
          throw error; // Re-throw other errors to be caught by outer catch block
        }
      }
    } catch (error: any) {
      console.log({ error });
      // Extract meaningful error message from error object
      const errorMsg = error?.reason || error?.message || error?.shortMessage || "An unknown error occurred";
      setErrorMessage(errorMsg);
      setOfferLoading(false);
    }
  };

  const handleCloseCancelOffer = () => {
    if (!cancelOfferLoading) {
      setCancelOfferModal(false);
    }
  };

  const handleCancelOffer = async () => {
    // Set loading states before performing any operations
    setOfferLoading(true);
    setCancelOfferLoading(true);
    setErrorMessage("");

    // State updates are asynchronous, so we can't log the state value immediately
    try {
      const signer = await getMetaMaskSigner();
      const NFTContract = getContract("ecofutures-token", signer);
      const tokenId = Number(token?.etNft?.id as any);
      if (NFTContract && tokenId) {
        const result = await NFTContract.cancelListing(tokenId);
        const r = await checkTransactionConfirmationReceipt(result.hash, library);
        const receipt = r as any;
        // The raw JSON-RPC receipt carries `status` as "0x0" / "0x1" (a decoded one as 0 / 1),
        // so the old `!== "0"` compare treated a reverted transaction as a success.
        if (Number(receipt?.status) === 0) {
          throw new Error("The cancel transaction was mined but reverted.");
        }
        setInfoMessage("NFT offer cancelled");
        setCancelOfferModal(false);
        // Not a reload. The backend indexes SaleCancelled a few seconds after the receipt, so
        // reloading right here re-fetched the listing as still open — the button "did nothing".
        // Same wait GuardianPortal gives it; silent, or the whole content subtree unmounts.
        window.setTimeout(() => fetchAllData(true), 3000);
      }
    } catch (error: any) {
      console.log({ error });
      // Names the V10.1 revert (NoActiveListing, OnlyOwnerCanCancelListing) rather than showing
      // its selector; a wallet rejection gets its own line; anything else falls through to the
      // reason / message as before.
      setErrorMessage(
        isUserRejection(error) ? "Cancelled in your wallet — nothing was sent." : describeChainError(error)
      );
    } finally {
      setOfferLoading(false);
      setCancelOfferLoading(false);
    }
  };

  const shouldShowMintRate = () => {
    return (
      token?.status !== EcoTokenStatus.APPROVED &&
      token?.status !== EcoTokenStatus.VERIFICATION_REQUIRED &&
      token?.status !== EcoTokenStatus.DRAFT &&
      token?.status !== EcoTokenStatus.REJECTED &&
      token?.status !== EcoTokenStatus.CLAIMED
    );
  };

  const getMintRate = () => {
    // const total = formatValue(token?.etNft?.totalTreeForEasement as any, false);
    // const days = moment.duration(token.meta?.contractPeriod, TIME_DISPLAY_UNIT).as("days");
    // return formatValue(total / (days - 1), true, 3);

    if (!token.etNft?.totalTreeForEasement) {
      return <span>N/A</span>;
    }
    const total = formatValue(token.etNft.totalTreeForEasement as any, false);
    const claimed = formatValue(token.etNft?.easementActivated?.rewardClaimed as any, false) || 0;
    const remaining = total - claimed;
    return sixDigits(formatValue(remaining, true, 3));
  };

  // const getTotalMint = () => {
  // const total = formatValue(token?.etNft?.totalTreeForEasement as any, false);
  // const days = moment.duration(token.meta?.contractPeriod, TIME_DISPLAY_UNIT).as("days");
  // return formatValue(total / (days - 1), true, 3);

  // if (!token.etNft?.totalTreeForEasement) {
  //   return <span>N/A</span>;
  // }
  // const total = formatValue(token.etNft.totalTreeForEasement as any, false);
  // const claimed = formatValue(token.etNft?.easementActivated?.rewardClaimed as any, false) || 0;
  // const remaining = total - claimed;
  // return sixDigits(formatValue(total, true, 3));
  // };

  const getRemainingTrees = () => {
    if (!token.etNft?.totalTreeForEasement) {
      return <span>N/A</span>;
    }
    const total = formatValue(token.etNft.totalTreeForEasement as any, false);
    const claimed = formatValue(token.etNft?.easementActivated?.rewardClaimed as any, false) || 0;
    const remaining = total - claimed;
    const remainingDecimalPlaces =
      remaining <= 0 ? 0 : remaining > 999999 ? 0 : Math.max(0, 6 - Math.floor(Math.log10(remaining) + 1));
    return (
      <span style={{ display: "flex", alignItems: "center", textDecoration: "none", justifyContent: "end" }}>
        {sixDigits(formatValue(remaining, true, remainingDecimalPlaces))}
        <CurrencyLabel currency={TREE_CURRENCY} sx={{ textDecoration: "none !important", fontSize: "14px" }} />
      </span>
    );
  };

  const getRemainingTreesMobile = () => {
    // const total = formatValue(token.etNft?.totalTreeForEasement as any, false);
    // const claimed = formatValue(token.etNft?.easementActivated?.rewardClaimed as any, false) || 0;
    // const mintRate = appContext.mintRate;
    // const remaining = total - claimed;
    return (
      <>
        {isSmallScreen && (
          <>
            {/*<IconButton
              size="small"
              onClick={() => {
                setPopoverAnchorEl(true as any);
              }}
              sx={{ ml: "2px", padding: 0 }}
            >
              <InfoOutlined sx={{ fontSize: "14px", color: "#999999" }} />
            </IconButton>*/}
            <SwipeableDrawer
              anchor="bottom"
              open={Boolean(popoverAnchorEl)}
              onClose={() => setPopoverAnchorEl(null)}
              onOpen={() => setPopoverAnchorEl(true as any)}
              disableSwipeToOpen={false}
              PaperProps={{
                sx: {
                  borderTopLeftRadius: "16px",
                  borderTopRightRadius: "16px",
                  maxHeight: "70vh",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "auto",
                  height: "auto",
                  flexDirection: "column",
                  alignItems: "start",
                  backgroundColor: "white",
                  padding: "22px 25px",
                  gap: 2,
                }}
              >
                {/* Title Header with Close Icon */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    marginBottom: "16px",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: "18px",
                      color: "#000",
                    }}
                  >
                    Mint Currency
                  </Typography>
                  <IconButton
                    onClick={() => setPopoverAnchorEl(null)}
                    sx={{
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "20px", color: "#666" }} />
                  </IconButton>
                </Box>
                <Grid
                  container
                  gap={"40px"}
                  sx={{ width: "100%", flexDirection: "row", justifyContent: "space-between", marginBottom: "20px" }}
                >
                  <Grid
                    item
                    xs={"auto"}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      minWidth: "112px",
                      justifyContent: "end",
                    }}
                  >
                    <PopUpText>Remaining Mint: {getRemainingTrees()}</PopUpText>
                  </Grid>
                  <Grid
                    item
                    xs={"auto"}
                    sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: "112px" }}
                  >
                    <PopUpText sx={{ textAlign: "left" }}>
                      Total Mint :{" "}
                      <PopUpText>
                        <span style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                          {token.etNft?.totalTreeForEasement
                            ? sixDigits(formatValue(token.etNft.totalTreeForEasement as any, true, 3))
                            : "N/A"}
                          {token.etNft?.totalTreeForEasement && (
                            <CurrencyLabel
                              currency={TREE_CURRENCY}
                              sx={{ textDecoration: "none !important", fontSize: "14px" }}
                            />
                          )}
                        </span>
                      </PopUpText>
                    </PopUpText>
                  </Grid>
                </Grid>
                <Divider sx={{ marginBottom: "15px", width: "100%", backgroundColor: "#E0E0E0" }} />
                <Grid
                  container
                  gap={"8px"}
                  sx={{ width: "100%", flexDirection: "column", alignContent: "end", marginBottom: "20px" }}
                >
                  <Grid
                    item
                    xs={"auto"}
                    sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
                  >
                    <Grid
                      item
                      xs={"auto"}
                      sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
                    >
                      <PopUpText>
                        Patron:{" "}
                        {rewardData?.patronReward ? sixDigits(formatValue(rewardData?.patronReward, true, 3)) : "0"}{" "}
                        <CurrencyLabel
                          currency={TREE_CURRENCY}
                          sx={{ textDecoration: "none !important", fontSize: "14px" }}
                        />
                      </PopUpText>
                    </Grid>
                    <PopUpText>
                      Guardian:{" "}
                      {rewardData?.guardianReward ? sixDigits(formatValue(rewardData?.guardianReward, true, 3)) : "0"}{" "}
                      <CurrencyLabel
                        currency={TREE_CURRENCY}
                        sx={{ textDecoration: "none !important", fontSize: "14px" }}
                      />
                    </PopUpText>
                  </Grid>
                  <Grid
                    item
                    xs={"auto"}
                    sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
                  >
                    <PopUpText>
                      Referer:{" "}
                      {rewardData?.referralReward ? sixDigits(formatValue(rewardData?.referralReward, true, 3)) : "0"}{" "}
                      <CurrencyLabel
                        currency={TREE_CURRENCY}
                        sx={{ textDecoration: "none !important", fontSize: "14px" }}
                      />
                    </PopUpText>
                  </Grid>
                </Grid>
                <Divider
                  sx={{ marginBottom: "15px", borderStyle: "dashed", width: "100%", backgroundColor: "#E0E0E0" }}
                />{" "}
                <Grid container sx={{ width: "100%", flexDirection: "row", justifyContent: "end" }}>
                  <Grid
                    item
                    xs={"auto"}
                    sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
                  >
                    <PopUpText sx={{ display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
                      {shouldShowActiveTime() ? "Active Time" : "Contract Period"}:
                      {isContractExpired() ? (
                        " Expired"
                      ) : shouldShowActiveTime() ? (
                        <CountdownTimer
                          medium
                          targetDate={moment(token?.etNft?.easementActivated?.contractStart)
                            .add(token.meta?.contractPeriod || 1, TERM_UNIT)
                            .toDate()}
                        />
                      ) : (
                        ` ${token?.meta?.contractPeriod || 0} ${
                          (token?.meta?.contractPeriod || 0) > 1 ? TERM_UNIT : TERM_UNIT.slice(0, -1)
                        }`
                      )}
                    </PopUpText>
                  </Grid>
                </Grid>
              </Box>
            </SwipeableDrawer>
          </>
        )}
        {!isSmallScreen && (
          <>
            <Popover
              classes={{ paper: "PopoverPaper" }}
              open={Boolean(popoverAnchorEl)}
              anchorEl={popoverAnchorEl}
              onClose={() => setPopoverAnchorEl(null)}
              disableScrollLock={true}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "auto",
                  height: "auto",
                  flexDirection: "column",
                  alignItems: "start",
                  backgroundColor: "white",
                  padding: "12px",
                  gap: 2,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Text bold medium>
                    Mint Currency
                  </Text>
                  <IconButton size="small" onClick={() => setPopoverAnchorEl(null)}>
                    <Close />
                  </IconButton>
                </div>

                {/* <Typography variant="body2">
                  ${formatValue(remainingInEasement, true, 2)} @ ${formatValue(mintRate, true, 4)}
                </Typography>
                {token.etNft?.status !== "sold" && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {getMintRate()} TREE Per day
                  </Typography>
                )} */}
                {/* <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  {getRemainingTrees()}  Mint Capacity
                  {getMintRate()} TREE Per day
                </Typography> */}
                <Grid
                  container
                  gap={"40px"}
                  sx={{ width: "100%", flexDirection: "row", justifyContent: "space-between", marginBottom: "20px" }}
                >
                  <Grid
                    item
                    xs={"auto"}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      minWidth: "112px",
                      justifyContent: "end",
                    }}
                  >
                    <PopUpText>Remaining Mint: {getRemainingTrees()}</PopUpText>
                  </Grid>
                  <Grid
                    item
                    xs={"auto"}
                    sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: "112px" }}
                  >
                    <PopUpText sx={{ textAlign: "left" }}>
                      Total Mint :{" "}
                      <PopUpText>
                        <span style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                          {token.etNft?.totalTreeForEasement
                            ? sixDigits(formatValue(token.etNft.totalTreeForEasement as any, true, 3))
                            : "N/A"}
                          {token.etNft?.totalTreeForEasement && (
                            <CurrencyLabel
                              currency={TREE_CURRENCY}
                              sx={{ textDecoration: "none !important", fontSize: "14px" }}
                            />
                          )}
                        </span>
                      </PopUpText>
                    </PopUpText>
                  </Grid>
                </Grid>
                <Divider sx={{ marginBottom: "15px", width: "100%", backgroundColor: "#E0E0E0" }} />
                <Grid
                  container
                  gap={"8px"}
                  sx={{ width: "100%", flexDirection: "column", alignContent: "end", marginBottom: "20px" }}
                >
                  <Grid
                    item
                    xs={"auto"}
                    sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
                  >
                    <PopUpText>
                      Patron:{" "}
                      {rewardData?.patronReward ? sixDigits(formatValue(rewardData?.patronReward, true, 3)) : "0"}{" "}
                      <CurrencyLabel
                        currency={TREE_CURRENCY}
                        sx={{ textDecoration: "none !important", fontSize: "14px" }}
                      />
                    </PopUpText>
                  </Grid>
                  <Grid
                    item
                    xs={"auto"}
                    sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
                  >
                    <PopUpText>
                      Guardian:{" "}
                      {rewardData?.guardianReward ? sixDigits(formatValue(rewardData?.guardianReward, true, 3)) : "0"}{" "}
                      <CurrencyLabel
                        currency={TREE_CURRENCY}
                        sx={{ textDecoration: "none !important", fontSize: "14px" }}
                      />
                    </PopUpText>
                  </Grid>

                  <Grid
                    item
                    xs={"auto"}
                    sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
                  >
                    <PopUpText>
                      Referer:{" "}
                      {rewardData?.referralReward ? sixDigits(formatValue(rewardData?.referralReward, true, 3)) : "0"}{" "}
                      <CurrencyLabel
                        currency={TREE_CURRENCY}
                        sx={{ textDecoration: "none !important", fontSize: "14px" }}
                      />
                    </PopUpText>
                  </Grid>
                </Grid>
                <Divider
                  sx={{ marginBottom: "15px", borderStyle: "dashed", width: "100%", backgroundColor: "#E0E0E0" }}
                />
                <Grid container sx={{ width: "100%", flexDirection: "row", justifyContent: "end" }}>
                  <Grid
                    item
                    xs={"auto"}
                    sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
                  >
                    <PopUpText sx={{ display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
                      {shouldShowActiveTime() ? "Active Time" : "Contract Period"}:
                      {isContractExpired() ? (
                        " Expired"
                      ) : shouldShowActiveTime() ? (
                        <CountdownTimer
                          medium
                          targetDate={moment(token?.etNft?.easementActivated?.contractStart)
                            .add(token.meta?.contractPeriod || 1, TERM_UNIT)
                            .toDate()}
                        />
                      ) : (
                        ` ${token?.meta?.contractPeriod || 0} ${
                          (token?.meta?.contractPeriod || 0) > 1 ? TERM_UNIT : TERM_UNIT.slice(0, -1)
                        }`
                      )}
                    </PopUpText>
                  </Grid>
                </Grid>
              </Box>
            </Popover>
          </>
        )}
      </>
    );
  };

  const getRejectionReason = () => {
    const denial = token?.verifications?.find((item) => item.status === EcoTokenStatus.REJECTED && item.trustId);
    console.log("Denial reason", { denial });
    if (denial) {
      return denial.description || "Denial reason is unknown";
    } else {
      return "Denial reason is unknown";
    }
  };

  // const getTokenStatusForDetails = () => {
  //   const resolved = resolveStandardStatus(
  //     {
  //       status: token.status,
  //       etNft: token.etNft,
  //       offerStatus: token.offerStatus,
  //       meta: token.meta,
  //     },
  //     { role: "public" }
  //   );

  //   const text = resolved?.label ?? token.status;
  //   const pulse = resolved?.showPulse ?? false;
  //   const sold = resolved?.pulseSold ?? false;
  //   const color: "primary" | "warning" | "error" | "action.pulse" | "info" =
  //     resolved?.color === "action.pulse" ? "action.pulse" : "primary";

  //   return !isMobile ? (
  //     <Text
  //       numeric
  //       contrast
  //       bold
  //       capital
  //       medium
  //       sx={{
  //         mt: 1.5,
  //         alignItems: "center",
  //         color: color === "action.pulse" ? color : `${color}.main`,
  //         display: "flex",
  //         flexWrap: "wrap",
  //         gap: 1,
  //         width: "100%",
  //         "& > *": {
  //           whiteSpace: "normal",
  //           wordBreak: "break-word",
  //         },
  //       }}
  //     >
  //       {pulse && <PulseCircle sold={sold} />}
  //       {text}
  //     </Text>
  //   ) : (
  //     <Text
  //       numeric
  //       contrast
  //       bold
  //       capital
  //       sx={{
  //         mt: 1.5,
  //         alignItems: "center",
  //         color: color === "action.pulse" ? color : `${color}.main`,
  //         fontSize: "14px",
  //         gap: 1,
  //         width: "100%",
  //       }}
  //     >
  //       {pulse && <PulseCircle sold={sold} />}
  //       {text}
  //     </Text>
  //   );
  // };

  return (
    <EmbeddedBoundary
      embedded={embedded}
      topOffset={topOffset}
      // Both desktop and mobile dismiss by clicking the surrounding map/backdrop
      // (lightbox style) — there's no explicit close button.
      onBackdropClick={onClose}
      scrollRef={embeddedScrollRef}
    >
      {/* Standalone shows a spinner while loading; embedded reaches this return only
          once the rich `token` has loaded (the loading state early-returns above). */}
      {isLoading && !embedded && (
        <CenteredContainer>
          <CircularProgress />
        </CenteredContainer>
      )}

      {!isLoading && !token && !embedded && (
        <CenteredContainer>
          <Text>No token data found</Text>
        </CenteredContainer>
      )}

      {token && (embedded || !isLoading) && (
        <>
          {/* Standalone mounts its own non-interactive map backdrop. Embedded
              mode renders over the host page's live, interactive map instead. */}
          {!embedded &&
            Number.isFinite(Number(token.meta?.location?.lon)) &&
            Number.isFinite(Number(token.meta?.location?.lat)) && (
              <LandMapBackground
                lon={Number(token.meta?.location?.lon)}
                lat={Number(token.meta?.location?.lat)}
                skipFlyTo={cameFromExplore}
                onArrive={(origin) => {
                  setEmergeOrigin(origin);
                  setEmergePlaying(true);
                }}
              />
            )}
          {token.status === EcoTokenStatus.DRAFT && !!token.trust && user?.address === token.ownerAddress && (
            <Attention
              sx={{
                display: "flex",
                justifyContent: "space-between",
                padding: sizing(6),
                backgroundColor: "back.searchComponent",
                borderRadius: "15px",
                position: "relative",
                zIndex: 2,
                mx: { xs: "5%", md: 0 },
                width: { xs: "90%", md: "auto" },
                ...(!emergePlaying ? { opacity: 0, transform: "scale(0)" } : {}),
                ...(emergePlaying
                  ? {
                      transformOrigin: emergeOrigin ? `${emergeOrigin.x}px ${emergeOrigin.y}px` : "center center",
                      animation: `${landContentEmerge} 1500ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                    }
                  : {}),
              }}
            >
              <div>
                <Text numeric>Verification denial reason</Text>
                <Text numeric medium contrast sx={{ color: "text.primary" }}>
                  {getRejectionReason()}
                </Text>
              </div>
              <div>
                <BackButton
                  onClick={() => navigator(`${AppRoutes.Upload.path}?tokenId=${token.id}`)}
                  sx={{ m: 0, "&:hover": { border: "none", outline: "none" } }}
                >
                  Edit
                </BackButton>
              </div>
            </Attention>
          )}

          {/* Blue box — mobile only slot (above everything on xs) */}
          {(() => {
            const _cs = tokenData.etNft?.easementActivated?.contractStart;
            const _cp = tokenData.meta?.contractPeriod;
            const _exp = _cs && moment(_cs).add(_cp, TERM_UNIT).isBefore(moment());
            const _hasActions =
              (!_exp || hasRemainingTrees) &&
              ((tokenData.etNft !== null &&
                (isAuctionLoading ||
                  (tokenData.etNft.etAuction?.status === "started" && user?.role !== UserRoles.TRUST))) ||
                (user && openToOffers && canAcceptOffer) ||
                (openToOffers && canOffer && !isTrust && !isAdmin) ||
                (!landActive &&
                  !openToOffers &&
                  !canOffer &&
                  !isTrust &&
                  !isAdmin &&
                  (landOwner || landGuardian) &&
                  token?.etNft?.etAuction?.status !== "started") ||
                (openToOffers && (isTrust || isAdmin || landGuardian)) ||
                (token?.etNft?.etAuction?.status === "started" && (isTrust || isAdmin)) ||
                (landActive && !openToOffers && landTrust) ||
                (landActive && !openToOffers && (landOwner || landGuardian)) ||
                ((token?.status === EcoTokenStatus.CLAIMED ||
                  token?.status === EcoTokenStatus.RE_VERIFICATION_REQUIRED) &&
                  landTrust));
            if (!(isTrust || isAdmin || landOwner || landGuardian) || !_hasActions) return null;
            return (
              <RoleBanner
                token={token}
                landGuardian={landGuardian}
                landOwner={landOwner}
                landTrust={landTrust}
                isAdmin={isAdmin}
                sx={{ display: { xs: "flex", md: "none" } }}
              />
            );
          })()}
          <Box
            sx={{
              // Embedded only mounts this subtree once `token` exists, so a background
              // refetch (wallet/user/library populating) must not flip it to display:none
              // — that hides valid content and makes the IntersectionObserver strip
              // `is-revealed` from the right-column cards, leaving them invisible.
              display: isLoading && !embedded ? "none" : "flex",
              flexDirection: { xs: "column", md: "row" },
              width: { xs: "100%", md: "100%" },
              mx: { xs: 0, md: 0 },
              alignItems: "flex-start",
              position: "relative",
              zIndex: 2,
              // No persistent will-change: landContentEmerge is a one-shot
              // animation, and the browser auto-promotes during an active
              // transform/opacity animation. A permanent hint on this large
              // wrapper would keep a big GPU layer alive for the whole session.
              //
              // Embedded mode skips the scale-from-origin emerge (it would fight
              // the hero slide-in + staggered card reveal) and just shows; the
              // left/right columns run their own entrance animations instead.
              ...(embedded
                ? { opacity: 1 }
                : !emergePlaying
                ? { opacity: 0, transform: "scale(0)" }
                : {
                    transformOrigin: emergeOrigin ? `${emergeOrigin.x}px ${emergeOrigin.y}px` : "center center",
                    animation: `${landContentEmerge} 1500ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                  }),
            }}
          >
            {/* Left Column */}
            <Box
              ref={parentRef}
              sx={{
                width: { xs: "100%", md: "50%" },
                position: { md: "sticky" },
                top: { md: "var(--layout-header-height, 100px)" },
                // Embedded desktop: hero/left column slides in from the right —
                // unless the preview hero already played the entrance (morph or
                // slide) while loading, in which case this mounts already in place.
                ...(embedded && !isMobile && !heroIntroPlayedRef.current
                  ? {
                      animation: `${heroSlideIn} 900ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                    }
                  : {}),
              }}
            >
              {/* Shared with the embedded loading pass — same component, same tree
                  position, so the hero (and its gif) survives the data swap without
                  remounting. See EmbeddedHeroCard. remainingTrees falls back to the
                  preview's value while the contract read is still in flight, so the
                  TR3 stat doesn't blink to the rounded total for a frame. */}
              <EmbeddedHeroCard
                heroToken={token}
                isMobile={isMobile}
                remainingTrees={remainingTrees ?? (embedded ? previewRemainingTrees : null)}
                onImageClick={() => {
                  if (!token.content) return;
                  if (embedded) {
                    // Embedded view: flip the card in-place to reveal media gallery.
                    setHeroFlipOpen(true);
                  } else {
                    // Standalone view: open the full-screen media gallery modal.
                    setGalleryInitialFileId(`eft-${token.id}`);
                    setGalleryOpen(true);
                  }
                }}
                flipped={embedded ? heroFlipOpen : undefined}
                onFlipClose={embedded ? () => setHeroFlipOpen(false) : undefined}
                flipButtonRef={embedded ? heroCardRef : undefined}
                // Embedded: default delay (and usually the loading pass's instance,
                // already mid-reveal — same value, so it doesn't restart). Standalone:
                // hold until the map lands and landContentEmerge starts.
                textRevealDelayMs={embedded ? undefined : emergePlaying ? HERO_TEXT_STANDALONE_DELAY_MS : null}
                onMint={
                  (landActive || hasRemainingTrees) && (landGuardian || (landOwner && !openToOffers))
                    ? () => setClaimMint(true)
                    : undefined
                }
              />

              {/* Purple Box — only when there is a live/settled auction, an open
                  offer, or a completed sale to price. Gating on `etNft != null`
                  alone was too loose: a minted-but-idle token (owner == minter, no
                  etAuction, offerStatus null) has an etNft yet nothing to show, so
                  the box would fall through to a meaningless "$0 opening bid / Ends
                  in". A pre-mint token has no etNft and is likewise excluded. */}
              {(hasAuction ||
                token?.etNft?.etAuction != null ||
                token?.offerStatus === "opened" ||
                token?.etNft?.status === "sold" ||
                (!!token?.etNft && token?.etNft?.ownerAddress !== token?.etNft?.minterAddress)) && (
                <Box
                  data-detail-keep
                  sx={{
                    width: { xs: "85%", md: LEFT_COL_CARD_WIDTH_MD },
                    ml: { xs: "7.5%", md: "16.5%" },
                    mr: { xs: "7.5%", md: "8.5%" },
                    mt: { xs: 0, md: 0 },
                    mb: { xs: "7.5%", md: 0 },
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: "18px", md: "24px" },
                      width: "100%",
                      backgroundColor: "#BC92F8",
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      gap: { xs: "18px", md: "24px" },
                      flexDirection: "column",
                      // Embedded reveal: one light sweep across the price
                      // box after the section card lands.
                      ...(embedded
                        ? {
                            position: "relative",
                            overflow: "hidden",
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              inset: 0,
                              pointerEvents: "none",
                              background:
                                "linear-gradient(100deg, transparent 30%, rgba(255, 255, 255, 0.45) 50%, transparent 70%)",
                              transform: "translateX(-110%)",
                              animation: `${priceSweep} 900ms cubic-bezier(0.22, 1, 0.36, 1) 750ms both`,
                            },
                            "@media (prefers-reduced-motion: reduce)": {
                              "&::after": { animation: "none" },
                            },
                          }
                        : {}),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                        <Text
                          sx={{
                            color: "#000",
                            fontFamily: "monospace",
                            fontSize: "14px",
                            fontStyle: "normal",
                            fontWeight: 700,
                            lineHeight: "normal",
                            textTransform: "uppercase",
                          }}
                        >
                          {token.offerStatus === "opened" && highestOffer
                            ? "Highest offer"
                            : auctionOutcomeKnown
                            ? token.etNft?.etAuction?.winner || token.etNft?.ownerAddress !== token.etNft?.minterAddress
                              ? "Last sale"
                              : "Not sold"
                            : auctionHasBids
                            ? "Current bid"
                            : "Opening bid"}
                        </Text>
                        {token.offerStatus === "opened" && highestOfferBuyer && (
                          <div style={{ display: "flex", gap: "10px", lineHeight: "18px" }}>
                            <span style={{ lineHeight: "18px" }}>
                              <ArrowForwardSharpIcon
                                sx={{
                                  fontSize: "14px",
                                  color: "#000",
                                  fontWeight: "700",
                                  lineHeight: "18px",
                                }}
                              />
                            </span>
                            <ClickableContainer sx={{ padding: "0px 8px", margin: "0px -8px" }}>
                              <AddressLink
                                type="user"
                                address={highestOfferBuyer}
                                sx={{
                                  width: "100%",
                                  "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                                  "& a.userAddress:hover, & a.trustAddress:hover": {
                                    textDecoration: "none",
                                  },
                                  "& a": {
                                    color: "#000 !important",
                                    fontWeight: "bold",
                                    fontSize: { xs: "14px", sm: "14px" },
                                  },
                                }}
                              />
                            </ClickableContainer>
                          </div>
                        )}
                        {!(token.offerStatus === "opened" && highestOffer) &&
                          auctionHighestBidderAddress &&
                          !auctionWinnerAddress && (
                            <div style={{ display: "flex", gap: "10px", lineHeight: "18px" }}>
                              <span style={{ lineHeight: "18px" }}>
                                <ArrowForwardSharpIcon
                                  sx={{
                                    fontSize: "14px",
                                    color: "#000",
                                    fontWeight: "700",
                                    lineHeight: "18px",
                                  }}
                                />
                              </span>
                              <ClickableContainer sx={{ padding: "0px 8px", margin: "0px -8px" }}>
                                <AddressLink
                                  type="user"
                                  address={auctionHighestBidderAddress}
                                  sx={{
                                    width: "100%",
                                    "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                                    "& a.userAddress:hover, & a.trustAddress:hover": {
                                      textDecoration: "none",
                                    },
                                    "& a": {
                                      color: "#000 !important",
                                      fontWeight: "bold",
                                      fontSize: { xs: "14px", sm: "14px" },
                                    },
                                  }}
                                />
                              </ClickableContainer>
                            </div>
                          )}
                        {!(token.offerStatus === "opened" && highestOffer) &&
                          auctionWinnerAddress &&
                          !(
                            auctionOutcomeKnown &&
                            (token.etNft?.etAuction?.winner || token.etNft?.ownerAddress !== token.etNft?.minterAddress)
                          ) && (
                            <div style={{ display: "flex", gap: "10px" }}>
                              <span style={{ lineHeight: "18px" }}>
                                <ArrowForwardSharpIcon
                                  sx={{
                                    fontSize: "14px",
                                    color: "#000",
                                    fontWeight: "700",
                                    lineHeight: "18px",
                                  }}
                                />
                              </span>
                              <ClickableContainer sx={{ padding: "0px 8px", margin: "0px -8px" }}>
                                <AddressLink
                                  type="user"
                                  address={auctionWinnerAddress}
                                  sx={{
                                    width: "100%",
                                    "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                                    "& a.userAddress:hover, & a.trustAddress:hover": {
                                      textDecoration: "none",
                                    },
                                    "& a": {
                                      color: "#000 !important",
                                      fontWeight: "bold",
                                      fontSize: { xs: "14px", sm: "14px" },
                                    },
                                  }}
                                />
                              </ClickableContainer>
                            </div>
                          )}
                        {/* {!(token.offerStatus === "opened" && highestOffer) &&
                      !auctionWinnerAddress &&
                      auctionHasBids && (
                        <div style={{ display: "flex", gap: "10px" }}>
                          <span style={{ lineHeight: "18px" }}>
                            <ArrowForwardSharpIcon
                              sx={{
                                fontSize: "14px",
                                color: "#000",
                                fontWeight: "700",
                                lineHeight: "18px",
                              }}
                            />
                          </span>
                          <Box
                            sx={{
                              fontSize: "14px",
                              fontFamily: "IBM Mono, monospace",
                              fontWeight: 600,
                              lineHeight: "18px",
                              direction: "ltr",
                              color: "#000 !important",
                              textTransform: "uppercase",
                            }}
                          >
                            {auctionEndTs}
                          </Box>
                        </div>
                      )} */}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          alignSelf: "flex-start",
                          gap: "4px",
                          flexShrink: 0,
                        }}
                      >
                        <Text
                          numeric
                          sx={{
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            textAlign: "right",
                            textTransform: "uppercase",
                            fontSize: "14px",
                            fontWeight: 700,
                          }}
                        >
                          {/* A new bid or offer flips the amount (transitions.dev number pop-in).
                              Offer and bid are separate scopes, so switching between them is a
                              first appearance, not a flip. */}
                          <DigitFlip
                            value={
                              token.offerStatus === "opened" && highestOffer
                                ? `$${formatValue(highestOffer, true, 2)}`
                                : `$${formatValue(auctionCurrentBid, true, 2)}`
                            }
                            trackKey={token.etNft?.id ?? `token-${token.id}`}
                            scope={token.offerStatus === "opened" && highestOffer ? "land-offer" : "land-bid"}
                          />
                        </Text>
                        {!(token.offerStatus === "opened" && highestOffer) && !auctionEnded && (
                          <AuctionCountdown endDate={auctionEndDate} />
                        )}
                      </Box>
                    </Box>
                    {(() => {
                      // EFTActionButton only paints while auction data is loading or a
                      // live auction needs its cancel/ending UI — once the auction has
                      // ended it renders nothing, so mounting it would just reserve an
                      // empty action row under the price box.
                      const showOwnerAction =
                        tokenData.etNft !== null &&
                        !isTrust &&
                        !isAdmin &&
                        landOwner &&
                        (isAuctionLoading || tokenData.etNft.etAuction?.status === "started");
                      const showNonOwnerAction =
                        tokenData.etNft !== null &&
                        !isTrust &&
                        !isAdmin &&
                        !landOwner &&
                        (isAuctionLoading || tokenData.etNft.etAuction?.status === "started");
                      const showCancelListing = !!(user && openToOffers && canAcceptOffer);
                      const showMakeOffer = !!(openToOffers && canOffer && !isTrust && !isAdmin);
                      // Re-auctioning is the Guardian's (minter's) call, and only while the
                      // token is back in their hands: listEasementToAuction requires
                      // ownerOf(tokenId) == the calling guardian, so owner must MATCH minter.
                      // This previously required the two to differ, which inverted the gate —
                      // it offered the button exactly when the guardian could not list (auction
                      // ended with a winner, NFT sitting in EcoBank escrow, ownerAddress set to
                      // the winner) and hid it in the two states where listing actually works:
                      // an auction that ended with no funded bidder, and a settlement unwound by
                      // refundAndRelist. The primary market is still open in both — EcoBank marks
                      // _primaryMarketCompleted only at finalise — so the guardian can relist.
                      const auctionOwnerAddr = token?.etNft?.ownerAddress ?? token?.ownerAddress;
                      const auctionMinterAddr = token?.etNft?.minterAddress ?? token?.minterAddress;
                      const showAuction =
                        !landActive &&
                        !hasRemainingTrees &&
                        !openToOffers &&
                        !isTrust &&
                        !isAdmin &&
                        !!landGuardian &&
                        !!auctionOwnerAddr &&
                        auctionOwnerAddr === auctionMinterAddr &&
                        // A settlement still in flight owns the token even when the addresses
                        // agree; only an idle row is the guardian's to relist.
                        (token?.etNft?.settlementState ?? "none") === "none" &&
                        token?.etNft?.etAuction?.status === "ended";
                      const showListToOffer =
                        !landActive &&
                        !!hasRemainingTrees &&
                        !openToOffers &&
                        !canOffer &&
                        !isTrust &&
                        !isAdmin &&
                        !!landOwner &&
                        token?.etNft?.etAuction?.status !== "started";
                      // Secondary listings and offers revert on-chain until the primary sale
                      // has fully settled (guide §5) — see isSecondaryMarketLocked for why a
                      // bare `primaryMarketCompleted === false` would wrongly lock pre-V9 NFTs.
                      const primaryMarketPending = isSecondaryMarketLocked(token?.etNft);
                      const showVerify =
                        (token?.status === EcoTokenStatus.CLAIMED ||
                          token?.status === EcoTokenStatus.RE_VERIFICATION_REQUIRED) &&
                        !!landTrust;
                      // Post-auction settlement (V9.1) has its own card at the top of the
                      // right column, below the blue role banner — see the SettlementPanel
                      // mount there. It is intentionally not one of these price-box actions.
                      // Covenant badge is informational and reads its own on-chain state —
                      // it renders nothing when the covenant is unset, so gating on the
                      // token being minted is enough.
                      //const showCovenant = token?.etNft?.id !== undefined && token?.etNft?.id !== null;
                      const anyAction =
                        showOwnerAction ||
                        showNonOwnerAction ||
                        showCancelListing ||
                        showMakeOffer ||
                        showAuction ||
                        showListToOffer ||
                        showVerify;
                      // ||
                      // showCovenant;
                      if (!anyAction) return null;
                      return (
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            mt: { xs: "0px", md: "0px" },
                            "& > *": { width: "100%", minHeight: 0 },
                          }}
                        >
                          {/* {showCovenant && <CovenantBadge tokenId={token?.etNft?.id} />} */}
                          {showOwnerAction && (
                            <EFTActionButton
                              setIsAuctionLoading={setIsAuctionLoading}
                              compact
                              token={token}
                              fetchToken={(silent?: boolean) => fetchAllData(silent)}
                              auctionData={auctionData}
                            />
                          )}
                          {showNonOwnerAction && (
                            <EFTActionButton
                              setIsAuctionLoading={setIsAuctionLoading}
                              compact
                              token={token}
                              fetchToken={(silent?: boolean) => fetchAllData(silent)}
                              auctionData={auctionData}
                            />
                          )}
                          {showCancelListing && (
                            <Button
                              variant="contained"
                              color="primary"
                              sx={{
                                width: "100%",
                                borderRadius: "20px",
                                border: "none",
                                textTransform: "none",
                                ":hover": { border: "none", backgroundColor: "#333", color: "white" },
                              }}
                              onClick={() => {
                                // A message left from a previous attempt (or from the offer
                                // popup, which shares the state) must not open with the popup.
                                setErrorMessage("");
                                setCancelOfferModal(true);
                              }}
                            >
                              Cancel listing
                            </Button>
                          )}
                          {showMakeOffer && (
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={isTrust}
                              style={{ width: "100%" }}
                              sx={{
                                width: "100%",
                                borderRadius: "20px",
                                border: "none",
                                textTransform: "none",
                                ":hover": { border: "none", backgroundColor: "#333", color: "white" },
                              }}
                              onClick={() => {
                                if (user?.emailVerified && library) {
                                  setOfferModal(true);
                                } else if (!user) {
                                  window.dispatchEvent(new Event("openWalletDialog"));
                                } else {
                                  navigator(AppRoutes.Profile.subRoutes.Identification.path);
                                }
                              }}
                            >
                              {user?.emailVerified && library ? "Make offer" : user ? "Verify" : "Make offer"}
                            </Button>
                          )}
                          {showAuction && (
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={isTrust}
                              sx={{
                                width: "100%",
                                borderRadius: "20px",
                                border: "none",
                                textTransform: "none",
                                ":hover": { border: "none", backgroundColor: "#333", color: "white" },
                              }}
                              onClick={() => {
                                navigator(generatePath(AppRoutes.AuctionCreation.path, { tokenId: token.id }));
                              }}
                            >
                              Auction
                            </Button>
                          )}
                          {showListToOffer && (
                            <>
                              <Button
                                variant="contained"
                                color="primary"
                                disabled={primaryMarketPending}
                                sx={{
                                  width: "100%",
                                  borderRadius: "20px",
                                  border: "none",
                                  textTransform: "none",
                                  ":hover": { border: "none", backgroundColor: "#333", color: "white" },
                                }}
                                onClick={() => setOfferCreationModalOpen(true)}
                              >
                                List to offer
                              </Button>
                              {primaryMarketPending && (
                                <Text sx={{ fontSize: "13px", color: "#888", textAlign: "center" }}>
                                  Listing opens once the primary sale has settled.
                                </Text>
                              )}
                            </>
                          )}
                          {showVerify && (
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={!landTrust}
                              sx={{
                                width: "100%",
                                borderRadius: "20px",
                                border: "none",
                                textTransform: "none",
                                ":hover": { border: "none", backgroundColor: "#333", color: "white" },
                              }}
                              onClick={() => {
                                navigator(generatePath(AppRoutes.Verification.path, { tokenId: token.id }));
                              }}
                            >
                              Verify
                            </Button>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Right Column - Scrolling */}
            {/*
              Parallax note: avoid applying a transform on this wrapper. The mobile
              sticky-actions bar inside is `position: fixed` and would otherwise be
              promoted into this element's containing block, breaking its screen-fixed
              positioning. Parallax depth is achieved via the background map drift
              and per-card IntersectionObserver entrance animations instead.
            */}
            <Box
              ref={rightColumnRef}
              sx={{
                mr: { md: "6.25%" },
                width: { xs: "100%", md: "50%" },
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
                // Anchors the dissolving load-layer overlay during the
                // skeleton→content handoff below.
                position: "relative",
                "& [data-parallax-card]": {
                  opacity: 0,
                  // Rest below the fold by default (rises up when scrolling down).
                  transform: "translate3d(0, 48px, 0) scale(0.98)",
                  // will-change is applied per-card by the IntersectionObserver only
                  // for the duration of the reveal animation, then cleared — so we
                  // never keep a stack of promoted layers alive for every card.
                  backfaceVisibility: "hidden",
                },
                // Entered from the top (scrolling up): rest hidden above the fold
                // so the reveal drops it down into place.
                "& [data-parallax-card].reveal-from-top": {
                  transform: "translate3d(0, -48px, 0) scale(0.98)",
                },
                "& [data-parallax-card].is-revealed": {
                  animation: `${parallaxCardReveal} 1150ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                },
                "& [data-parallax-card].is-revealed.reveal-from-top": {
                  animation: `${parallaxCardRevealFromTop} 1150ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                },
                "@media (prefers-reduced-motion: reduce)": {
                  "& [data-parallax-card]": {
                    opacity: 1,
                    transform: "none",
                  },
                  "& [data-parallax-card].is-revealed, & [data-parallax-card].is-revealed.reveal-from-top": {
                    animation: "none",
                  },
                },
              }}
            >
              {/* The skeleton layer lingers as an overlay for one beat after the
                  payload lands, dissolving (opacity + blur) over the sections
                  staggering in underneath. */}
              {loadLayerExiting && <EmbeddedLoadLayer exiting />}
              {/* Detail stack — fades when the hero gallery flip is open.
                  Media stack (sibling below) slides in to replace it. */}
              <RightColDetailStack galleryOpen={!!heroFlipOpen}>
                {/* Same clamped top margin as the hero card so the two columns'
                    first cards stay top-aligned on short viewports too. */}
                <BackgroundDiv sx={{ margin: 0, marginTop: { xs: "0px", md: HERO_TOP_MARGIN_MD } }} data-parallax-card>
                  {/* Blue Box — desktop only (mobile version rendered above both columns) */}
                  {(() => {
                    const _cs = tokenData.etNft?.easementActivated?.contractStart;
                    const _cp = tokenData.meta?.contractPeriod;
                    const _exp = _cs && moment(_cs).add(_cp, TERM_UNIT).isBefore(moment());
                    const _hasActions =
                      (!_exp || hasRemainingTrees) &&
                      ((tokenData.etNft !== null &&
                        (isAuctionLoading ||
                          (tokenData.etNft.etAuction?.status === "started" && user?.role !== UserRoles.TRUST))) ||
                        (user && openToOffers && canAcceptOffer) ||
                        (openToOffers && canOffer && !isTrust && !isAdmin) ||
                        (!landActive &&
                          !openToOffers &&
                          !canOffer &&
                          !isTrust &&
                          !isAdmin &&
                          (landOwner || landGuardian) &&
                          token?.etNft?.etAuction?.status !== "started") ||
                        (openToOffers && (isTrust || isAdmin || landGuardian)) ||
                        (token?.etNft?.etAuction?.status === "started" && (isTrust || isAdmin)) ||
                        (landActive && !openToOffers && landTrust) ||
                        (landActive && !openToOffers && (landOwner || landGuardian)) ||
                        ((token?.status === EcoTokenStatus.CLAIMED ||
                          token?.status === EcoTokenStatus.RE_VERIFICATION_REQUIRED) &&
                          landTrust));
                    if (!(isTrust || isAdmin || landOwner || landGuardian) || !_hasActions) return null;
                    return (
                      <RoleBanner
                        token={token}
                        landGuardian={landGuardian}
                        landOwner={landOwner}
                        landTrust={landTrust}
                        isAdmin={isAdmin}
                        sx={{ display: { xs: "none", md: "flex" } }}
                      />
                    );
                  })()}

                  {/* Post-auction settlement (V9.1) — a distinct card at the top of the
                      right column, directly under the role banner. The record was resolved
                      in sortData() before `token` was set, so it is already in hand on this
                      first paint; a null one means either nothing to settle or a failed
                      read, and renders no wrapper so nothing reserves empty space. The panel
                      still self-gates to null when the state is "none" or "finalised".
                      onChanged re-runs the whole page load, which re-resolves this record. */}
                  {settlement && (
                    <SettlementPanel
                      tokenId={token?.etNft?.etAuction?.nftId as number}
                      settlement={settlement}
                      viewerAddress={user?.address}
                      viewerRole={user?.role}
                      boundVerifier={tokenData?.trust?.address ?? token?.trust?.address}
                      onChanged={() => fetchAllData()}
                      // sx={{ mb: { xs: "16px", md: "20px" } }}
                    />
                  )}

                  {/* Covenant-term challenge (V9.1) — the post-settlement half of the story, and
                      deliberately a separate card: it only exists once the settlement is
                      FINALISED, which is exactly where SettlementPanel above stops rendering for
                      everyone but the winner. Self-gates to null unless there is a challenge to
                      show or the viewer is the deed's attestor, so a healthy land page is
                      unchanged. */}
                  {settlement && (
                    <CovenantChallengePanel
                      tokenId={token?.etNft?.etAuction?.nftId as number}
                      landId={token?.id}
                      settlement={settlement}
                      viewerAddress={user?.address}
                      viewerRole={user?.role}
                      boundVerifier={tokenData?.trust?.address ?? token?.trust?.address}
                      onChanged={() => fetchAllData()}
                    />
                  )}

                  <Background>
                    <Box
                      sx={{
                        flexDirection: "column",
                        width: "100%",
                        display: "flex",
                        height: isMobile ? "auto" : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <LandSummaryCardRoot ref={detailsRef} sx={{ position: "relative" }}>
                        <LandSummaryHeader
                          title={`${token.meta?.name} ${token.etNft?.id ? `#${token.etNft.id}` : ""}`}
                          location={
                            token.meta?.location?.name && token.meta?.location?.country
                              ? `${token.meta.location.name}, ${token.meta.location.country}`
                              : undefined
                          }
                          landId={token.id}
                          onMediaClick={
                            (token.files?.filter((item) => item.type === "additional")?.length ?? 0) > 0 ||
                            !!(token as any)?.verifications?.find((v: any) => v?.videoUrl)?.videoUrl
                              ? () => {
                                  setGalleryInitialFileId(undefined);
                                  setGalleryOpen(true);
                                }
                              : undefined
                          }
                          onSurveyClick={
                            getSurveyFile()?.url
                              ? () => {
                                  setGalleryInitialFileId(getSurveyFile()!.id);
                                  setGalleryOpen(true);
                                }
                              : undefined
                          }
                          onContractClick={
                            getContractFile()?.url
                              ? () => {
                                  if (embedded) {
                                    // Flip the hero and scroll to the contract tile in the media stack.
                                    setHeroFlipOpen(true);
                                    const contractId = getContractFile()!.id;
                                    const contractIdx = galleryItems
                                      .slice(1)
                                      .findIndex((item) => item.id === contractId);
                                    if (contractIdx >= 0) {
                                      setTimeout(() => scrollToFlipTile(contractIdx), 350);
                                    }
                                  } else {
                                    setGalleryInitialFileId(getContractFile()!.id);
                                    setGalleryOpen(true);
                                  }
                                }
                              : undefined
                          }
                          isMobile={isMobile}
                          ownerAddress={
                            token.etNft?.ownerAddress &&
                            (token.etNft?.status === "sold" || token.etNft.ownerAddress !== token.etNft.minterAddress)
                              ? token.etNft.ownerAddress
                              : undefined
                          }
                        />
                        <Box
                          ref={tiersRef}
                          sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%" }}
                        >
                          <Grid container columnSpacing={{ xs: 6, sm: 12 }} rowSpacing={{ xs: "18px", sm: "24px" }}>
                            {shouldShowMintRate() && (
                              <Grid
                                item
                                xs={6}
                                sm={6}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  pb: 0,
                                }}
                              >
                                <LandSummaryField label="Mintable">
                                  <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        textDecoration: "none",
                                        whiteSpace: "nowrap",
                                        cursor: "pointer",
                                      }}
                                      onClick={(event) => {
                                        setPopoverAnchorEl(event.currentTarget);
                                      }}
                                    >
                                      <Box>
                                        {getMintRate()} {" TR3"}
                                      </Box>
                                      {(() => {
                                        const total = formatValue(token?.etNft?.totalTreeForEasement as any, false);
                                        const claimed =
                                          formatValue(token?.etNft?.easementActivated?.rewardClaimed as any, false) ||
                                          0;
                                        const remaining = total - claimed;
                                        const remainingPercentage = total > 0 ? (remaining / total) * 100 : 0;
                                        return (
                                          <Text
                                            sx={{
                                              color: "#999",
                                              alignItems: "center",
                                              fontSize: "12px",
                                              fontFamily: "'IBM Plex Mono', monospace",
                                              fontWeight: 500,
                                              textTransform: "uppercase",
                                            }}
                                          >
                                            ({remainingPercentage.toFixed(2)}%)
                                          </Text>
                                        );
                                      })()}
                                    </Box>
                                    {(() => {
                                      const total = formatValue(token?.etNft?.totalTreeForEasement as any, false);
                                      const claimed =
                                        formatValue(token?.etNft?.easementActivated?.rewardClaimed as any, false) || 0;
                                      const remaining = total - claimed;
                                      const computedProgress = total > 0 ? (remaining / total) * 100 : 0;
                                      return (
                                        <HoloProgressBar
                                          value={computedProgress}
                                          aria-label="Mintable TR3 remaining"
                                          // Embedded reveal: the 500ms delay lands the fill as
                                          // the first card's 1150ms rise settles (~90% there),
                                          // a beat before the price sweep (750ms).
                                          fillIn={embedded}
                                          fillDelayMs={500}
                                          sx={{ mt: "10px" }}
                                        />
                                      );
                                    })()}
                                    {getRemainingTreesMobile()}
                                  </Box>
                                </LandSummaryField>
                              </Grid>
                            )}

                            {/* {shouldShowMintRate() && (
                            <Grid
                              item
                              xs={6}
                              sm={6}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                pb: 0,
                              }}
                            >
                              <LandSummaryField label="Mint Capacity">
                                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                  <ClickableContainer>
                                    <Box
                                      onClick={(event) => {
                                        setPopoverAnchorEl(event.currentTarget);
                                      }}
                                      sx={{ cursor: "pointer", textDecoration: "none", whiteSpace: "nowrap" }}
                                    >
                                      {getTotalMint()} {" TR3"}
                                    </Box>
                                  </ClickableContainer>
                                  {getRemainingTreesMobile()}
                                </Box>
                              </LandSummaryField>
                            </Grid>
                          )} */}

                            {!details && token.etNft?.etAuction && hasAuction && token.etNft && (
                              <>
                                {/* <Grid item xs={6} sm={6} sx={{ display: "flex", flexDirection: "column", pb: 0 }}>
                                {token.offerStatus === "opened" && highestOffer ? (
                                  <LandSummaryField label="Highest offer">
                                    ${formatValue(highestOffer, true, 2)}
                                  </LandSummaryField>
                                ) : (
                                  <LandSummaryField
                                    label={
                                      token.etNft?.status === "sold" ||
                                      token.etNft?.etAuction.status === "completed" ||
                                      token.etNft?.etAuction.status === "ended" ||
                                      token.etNft?.etAuction.status === "cancelled"
                                        ? token.etNft.etAuction.winner ||
                                          token.etNft.ownerAddress !== token.etNft.minterAddress
                                          ? "Sold for"
                                          : "Not sold"
                                        : auctionHasBids
                                        ? "Current bid"
                                        : "Opening bid"
                                    }
                                  >
                                    ${formatValue(auctionCurrentBid, true, 2)}
                                  </LandSummaryField>
                                )}
                                {token.offerStatus === "opened" && highestOfferBuyer && (
                                  <div style={{ display: "flex", gap: "10px", lineHeight: "18px" }}>
                                    <Text
                                      numeric
                                      bold
                                      contrast
                                      sx={{ fontSize: "14px", fontWeight: 700, textTransform: "uppercase" }}
                                    >
                                      by
                                    </Text>
                                    <ClickableContainer sx={{ padding: "0px 8px", margin: "0px -8px" }}>
                                      <AddressLink
                                        type="user"
                                        address={highestOfferBuyer}
                                        sx={{
                                          width: "100%",
                                          "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                                          "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
                                          "& a": {
                                            color: "#000 !important",
                                            fontWeight: "bold",
                                            fontSize: { xs: "14px", sm: "14px" },
                                          },
                                        }}
                                      />
                                    </ClickableContainer>
                                  </div>
                                )}
                                {!(token.offerStatus === "opened" && highestOffer) &&
                                  auctionHighestBidderAddress &&
                                  !auctionWinnerAddress && (
                                    <div style={{ display: "flex", gap: "10px", lineHeight: "18px" }}>
                                      <Text
                                        numeric
                                        bold
                                        contrast
                                        sx={{ fontSize: "14px", fontWeight: 700, textTransform: "uppercase" }}
                                      >
                                        by
                                      </Text>
                                      <ClickableContainer sx={{ padding: "0px 8px", margin: "0px -8px" }}>
                                        <AddressLink
                                          type="user"
                                          address={auctionHighestBidderAddress}
                                          sx={{
                                            width: "100%",
                                            "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                                            "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
                                            "& a": {
                                              color: "#000 !important",
                                              fontWeight: "bold",
                                              fontSize: { xs: "14px", sm: "14px" },
                                            },
                                          }}
                                        />
                                      </ClickableContainer>
                                    </div>
                                  )}
                                {!(token.offerStatus === "opened" && highestOffer) &&
                                  auctionWinnerAddress &&
                                  !(
                                    (token.etNft?.status === "sold" ||
                                      token.etNft?.etAuction.status === "completed" ||
                                      token.etNft?.etAuction.status === "ended" ||
                                      token.etNft?.etAuction.status === "cancelled") &&
                                    (token.etNft.etAuction.winner ||
                                      token.etNft.ownerAddress !== token.etNft.minterAddress)
                                  ) && (
                                    <div style={{ display: "flex", gap: "10px" }}>
                                      <span style={{ lineHeight: "18px" }}>
                                        <ArrowForwardSharpIcon
                                          sx={{
                                            fontSize: "14px",
                                            color: "#999",
                                            fontWeight: "700",
                                            lineHeight: "18px",
                                          }}
                                        />
                                      </span>
                                      <ClickableContainer sx={{ padding: "0px 8px", margin: "0px -8px" }}>
                                        <AddressLink
                                          type="user"
                                          address={auctionWinnerAddress}
                                          sx={{
                                            width: "100%",
                                            "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                                            "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
                                            "& a": {
                                              color: "#000 !important",
                                              fontWeight: "bold",
                                              fontSize: { xs: "14px", sm: "14px" },
                                            },
                                          }}
                                        />
                                      </ClickableContainer>
                                    </div>
                                  )}
                                {!(token.offerStatus === "opened" && highestOffer) && !auctionWinnerAddress && (
                                  <div style={{ display: "flex", gap: "10px" }}>
                                    <span style={{ lineHeight: "18px" }}>
                                      <ArrowForwardSharpIcon
                                        sx={{
                                          fontSize: "14px",
                                          color: "#999",
                                          fontWeight: "700",
                                          lineHeight: "18px",
                                        }}
                                      />
                                    </span>
                                    <Box
                                      sx={{
                                        fontSize: "14px",
                                        fontFamily: "IBM Mono, monospace",
                                        fontWeight: 600,
                                        lineHeight: "18px",
                                        direction: "ltr",
                                        color: "#000 !important",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      {auctionEndTs}
                                    </Box>
                                  </div>
                                )}
                              </Grid> */}

                                {token.etNft.totalTreeForEasement && (
                                  <Grid item xs={6} sm={6} sx={{ display: "flex", flexDirection: "column", pb: 0 }}>
                                    <LandSummaryField
                                      //label={auctionWinnerAddress ? "Value" : auctionEnded ? "Value" : "Auction ends in"}
                                      label="Value"
                                    >
                                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Text sx={{ fontSize: "14px", fontWeight: 700 }}>
                                          ${sixDigits(auctionRemainingTrees)}
                                        </Text>
                                        <Text sx={{ fontSize: "14px", fontWeight: 700, color: "#999" }}>
                                          {auctionTreesValue}
                                        </Text>
                                        {/* {auctionWinnerAddress && (
                                        <>
                                          <Text sx={{ fontSize: "14px", fontWeight: 700 }}>
                                            ${sixDigits(auctionRemainingTrees)}
                                          </Text>
                                          <Text sx={{ fontSize: "14px", fontWeight: 700, color: "#999" }}>
                                            {auctionTreesValue}
                                          </Text>
                                        </>
                                      )} */}
                                        {/* {!auctionEnded && (
                                      <CountdownTimer
                                        sx={{
                                          "& .timerItem_over span": {
                                            fontFamily: "'IBM Plex Mono', monospace !important",
                                            fontSize: "14px !important",
                                            color: "black !important",
                                            fontWeight: "700 !important",
                                            lineHeight: "18px !important",
                                            textTransform: "uppercase",
                                          },
                                        }}
                                        targetDate={auctionEndDate}
                                      />
                                    )}
                                    {auctionEnded && !auctionWinnerAddress && (
                                      <>
                                        <Text sx={{ fontSize: "14px", fontWeight: 700 }}>
                                          ${sixDigits(auctionRemainingTrees)}
                                        </Text>
                                        <Text sx={{ fontSize: "14px", fontWeight: 700, color: "#999" }}>
                                          {auctionTreesValue}
                                        </Text>
                                      </>
                                    )} */}
                                      </Box>
                                    </LandSummaryField>
                                  </Grid>
                                )}
                              </>
                            )}

                            {!!tokenData?.trust?.address && (
                              <Grid
                                item
                                xs={6}
                                sm={6}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  pb: 0,
                                }}
                              >
                                <LandSummaryField label="Trustee">
                                  <ClickableContainer>
                                    <AddressLink
                                      type="user"
                                      address={tokenData?.trust?.address}
                                      sx={{
                                        width: "100%",
                                        "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                                        "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
                                        "& a": {
                                          fontFamily: "'IBM Plex Mono', monospace",
                                          fontWeight: 700,
                                          fontSize: "14px",
                                        },
                                      }}
                                    />
                                  </ClickableContainer>
                                </LandSummaryField>
                              </Grid>
                            )}

                            <Grid
                              item
                              xs={6}
                              sm={6}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                pb: 0,
                              }}
                            >
                              <LandSummaryField label="Guardian">
                                <ClickableContainer>
                                  <AddressLink
                                    type="user"
                                    address={token.minterAddress}
                                    sx={{
                                      width: "100%",
                                      "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                                      "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
                                      "& a": {
                                        fontFamily: "'IBM Plex Mono', monospace",
                                        fontWeight: 700,
                                        fontSize: "14px",
                                      },
                                    }}
                                  />
                                </ClickableContainer>
                              </LandSummaryField>
                            </Grid>
                          </Grid>
                        </Box>
                      </LandSummaryCardRoot>
                    </Box>
                  </Background>
                </BackgroundDiv>
                <Box data-parallax-card sx={{ width: "100%" }}>
                  <LandDescriptionTiersCard tiers={biddingTiers} contractPeriod={token.meta?.contractPeriod} />
                </Box>
                {token.etNft?.etAuction && (
                  <Block data-parallax-card sx={{ display: "flex", padding: sizing(0), mt: 0, width: "100%" }}>
                    {
                      <Auction
                        token={token}
                        onlyProvenance={true}
                        isOwner={canAcceptOffer}
                        isAuctionLoading={isAuctionLoading}
                        setIsAuctionLoading={setIsAuctionLoading}
                        auctionData={auctionData}
                        offersData={offersData}
                        transactionsData={transactionsData}
                        transactionsCountData={transactionsCountData}
                      />
                    }
                  </Block>
                )}

                <Box
                  data-parallax-card
                  onMouseLeave={!isMobile ? () => setHoveredEcoScore(null) : undefined}
                  onTouchEnd={isMobile ? () => setHoveredEcoScore(null) : undefined}
                  onTouchCancel={isMobile ? () => setHoveredEcoScore(null) : undefined}
                  sx={{
                    width: "100%",
                    margin: "auto",
                    height: { xs: "200px", md: "167px" },
                    backgroundColor: "back.table",
                    p: { xs: "18px", md: "24px" },
                    borderRadius: { xs: "20px", md: "20px" },
                  }}
                >
                  <Box sx={{ padding: "0px 20px 0px 0px" }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      marginTop={{ xs: "12px", md: "0px" }}
                    >
                      <Typography
                        fontSize={"14px"}
                        fontFamily={"monospace"}
                        textTransform={"uppercase"}
                        fontWeight={700}
                      >
                        Verification history
                      </Typography>
                    </Box>
                  </Box>
                  <LineChart
                    colors={colorPalette}
                    dataset={charts}
                    xAxis={[
                      {
                        id: String("history" as AxisId),
                        dataKey: "date",
                        disableLine: true,
                        disableTicks: true,
                        min: 0,
                        scaleType: "point",
                        tickLabelStyle: {
                          transform: "translateY(10px)",
                          color: "black",
                          fontWeight: "bold",
                          fontSize: "14px",
                          textTransform: "uppercase",
                        },
                        valueFormatter: (value) => moment(value).format("MMM YY"),
                      },
                    ]}
                    yAxis={[
                      {
                        dataKey: "ecoScore",
                        min: 0,
                        max: 1,
                        disableLine: true,
                        tickInterval: [1],
                        // disableTicks: charts.length <= 2 ? false : true,
                        disableTicks: true,
                        tickLabelStyle: {
                          transform: "translateX(-10px)",
                          color: "black",
                          fontWeight: "bold",
                          fontSize: "14px",
                          fontFamily: "IBM Mono, monospace",
                          paddingRight: "10px",
                        },
                        valueFormatter: (value) => {
                          return value ? (value * 100).toFixed() + "%" : "";
                        },
                      },
                    ]}
                    series={[
                      {
                        dataKey: "ecoScore",
                        curve: "linear",
                        area: true,
                        showMark: true,
                        valueFormatter: (value) => {
                          return value ? (value * 100).toFixed() : "0";
                        },
                      },
                      {
                        id: "nextVerification",
                        dataKey: "nextVerificationEcoScore",
                        curve: "linear",
                        showMark: true,
                        valueFormatter: (value) => {
                          return value ? (value * 100).toFixed() : "0";
                        },
                      },
                      {
                        id: "contractStart",
                        dataKey: "contractStartEcoScore",
                        curve: "linear",
                        showMark: true,
                        valueFormatter: (value) => {
                          return value ? (value * 100).toFixed() : "0";
                        },
                      },
                    ]}
                    slotProps={{ legend: { hidden: true } }}
                    height={isMobile ? 180 : 150}
                    grid={{ vertical: false, horizontal: false }}
                    tooltip={{
                      trigger: "axis",
                      axisContent: (value) => {
                        const dataIndex = value?.dataIndex;
                        const chartPoint = dataIndex !== null && dataIndex !== undefined ? charts[dataIndex] : null;
                        const ecoScoreData =
                          dataIndex !== null && dataIndex !== undefined ? value?.series[0]?.data[dataIndex] : null;
                        const nextVerificationData =
                          dataIndex !== null && dataIndex !== undefined ? value?.series[1]?.data[dataIndex] : null;
                        const data = ecoScoreData ?? nextVerificationData;
                        const ecoScoreVersion =
                          dataIndex !== null && dataIndex !== undefined ? charts[dataIndex]?.ecoScoreVersion : null;
                        const timestampSource = chartPoint?.updatedAt ?? value?.axisData?.x?.value;
                        const date = moment(timestampSource).format("DD MMM YYYY, HH:mm");
                        const numericData = Number(data);
                        const displayEcoScore = Number.isFinite(numericData) ? (numericData * 100).toFixed() : "0";
                        const isFutureVerificationPoint = Boolean(
                          chartPoint &&
                            chartPoint.nextVerificationEcoScore !== null &&
                            chartPoint.nextVerificationEcoScore !== undefined
                        );
                        const hoveredValue = Number(data);
                        if (Number.isFinite(hoveredValue) && hoveredValue !== hoveredEcoScore) {
                          setHoveredEcoScore(hoveredValue);
                        }

                        if (isFutureVerificationPoint) {
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                width: "auto",
                                height: "auto",
                                flexDirection: "column",
                                alignItems: "start",
                                backgroundColor: "white",
                                borderRadius: "8px",
                                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                                border: "1px solid rgba(0, 0, 0, 0.12)",
                                padding: isMobile ? "10px 14px" : "12px 25px",
                                transform: isMobile ? "translateY(-75px)" : "none",
                              }}
                            >
                              <PopUpText sx={{ color: "#959A80", whiteSpace: "nowrap" }}>
                                Next Verification Due on : {date}
                              </PopUpText>
                            </Box>
                          );
                        }

                        const isContractStartPoint = Boolean(
                          chartPoint &&
                            chartPoint.contractStartEcoScore !== null &&
                            chartPoint.contractStartEcoScore !== undefined
                        );

                        if (isContractStartPoint) {
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                width: "auto",
                                height: "auto",
                                flexDirection: "column",
                                alignItems: "start",
                                backgroundColor: "white",
                                borderRadius: "8px",
                                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                                border: "1px solid rgba(0, 0, 0, 0.12)",
                                padding: isMobile ? "10px 14px" : "12px 25px",
                                transform: isMobile ? "translateY(-75px)" : "none",
                              }}
                            >
                              <PopUpText sx={{ color: "#959A80", whiteSpace: "nowrap" }}>
                                Contract Start Date : {date}
                              </PopUpText>
                            </Box>
                          );
                        }

                        return (
                          <Box
                            sx={{
                              display: "flex",
                              width: "auto",
                              height: "auto",
                              flexDirection: "column",
                              alignItems: "start",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                              border: "1px solid rgba(0, 0, 0, 0.12)",
                              padding: isMobile ? "10px 14px 14px 14px" : "12px 25px 22px 25px",
                              gap: 2,
                              transform: isMobile ? "translateY(-75px)" : "none",
                            }}
                          >
                            <Grid container sx={{ width: "100%", flexDirection: "row", justifyContent: "start" }}>
                              <Grid
                                item
                                xs={"auto"}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "start",
                                  minWidth: "112px",
                                }}
                              >
                                <PopUpText sx={{ color: "#959A80" }}>{date}</PopUpText>
                              </Grid>
                            </Grid>

                            <Grid
                              container
                              gap={"40px"}
                              sx={{ width: "100%", flexDirection: "row", justifyContent: "space-between" }}
                            >
                              <Grid
                                item
                                xs={"auto"}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  minWidth: "112px",
                                }}
                              >
                                <PopUpText
                                  sx={{ display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}
                                >
                                  Eco Score: <PopUpText>{displayEcoScore}</PopUpText>
                                </PopUpText>
                              </Grid>
                              <Grid
                                item
                                xs={"auto"}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  minWidth: "112px",
                                }}
                              >
                                <PopUpText
                                  sx={{ display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}
                                >
                                  Eco Matrix:
                                  <PopUpText>{ecoScoreVersion || "v1.0"}</PopUpText>
                                </PopUpText>
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      },
                    }}
                    sx={{
                      position: "relative",
                      bottom: "30px",
                      height: "100%",
                      [`& .${markElementClasses.root}:not(.MuiMarkElement-series-nextVerification):not([data-series='nextVerification']):not(.MuiMarkElement-series-contractStart):not([data-series='contractStart'])`]:
                        {
                          stroke: "transparent",
                          r: 2, // Modify the circle radius
                          fill: "#000000",
                          strokeWidth: 2,
                        },
                      [`& .${lineElementClasses.root}`]: {
                        stroke: "#BC92F8",
                        strokeWidth: 3,
                      },
                      "& .MuiLineElement-series-nextVerification": {
                        stroke: "transparent",
                        strokeWidth: 0,
                      },
                      "& .MuiLineElement-series-contractStart": {
                        stroke: "transparent",
                        strokeWidth: 0,
                      },
                      "& .MuiMarkElement-root.MuiMarkElement-series-contractStart, & .MuiMarkElement-root[data-series='contractStart']":
                        {
                          fill: "#4CAF50",
                          stroke: "#4CAF50",
                          strokeWidth: 2,
                          r: 4,
                        },
                      "& .MuiMarkElement-root.MuiMarkElement-series-nextVerification, & .MuiMarkElement-root[data-series='nextVerification']":
                        {
                          fill: "#8E8E8E",
                          stroke: "#8E8E8E",
                          strokeWidth: 2,
                          r: 4,
                          // Compositor-only attention pulse: animate opacity alone.
                          // The mark is a react-spring <path> positioned via an inline
                          // `transform`, so animating transform here would clobber its
                          // position; `r` is inert on a <path>. The old keyframes
                          // animated drop-shadow blur + fill/stroke every frame (paint
                          // churn + continuous blur). Opacity composites cheaply and
                          // leaves positioning untouched.
                          animation: "nextVerificationPulse 2s ease-in-out infinite",
                        },
                      "@keyframes nextVerificationPulse": {
                        "0%": { opacity: 0.4 },
                        "50%": { opacity: 1 },
                        "100%": { opacity: 0.4 },
                      },
                      [`& .${chartsGridClasses.line}`]: {
                        strokeOpacity: 0.1,
                        stroke: "#BC92F8",
                        strokeWidth: 1,
                        strokeDasharray: "none !important",
                      },
                      // "& .MuiChartsAxis-root.MuiChartsAxis-directionX.MuiChartsAxis-bottom": {
                      //   transform: "translate(0px,280px)"
                      // },

                      "& .css-wke9ls-MuiChartsAxis-root-MuiChartsYAxis-root": {
                        transform: "translateX(50px)",
                      },
                      [`& .${areaElementClasses.root}`]: {
                        fill: `url(#${gradientId})  !important`,
                        fillOpacity: 1,
                      },
                      // "& .MuiChartsAxis-label": {
                      //   transform: "translate(-20px, 10px)",
                      // },
                      "& .MuiChartsYAxis-tickLabel": {
                        paddingRight: "10px",
                      },
                    }}
                  >
                    <ChartsReferenceLine
                      y={1}
                      label=""
                      lineStyle={{ stroke: "#000", strokeDasharray: "6 6", strokeWidth: 2 }}
                    />
                    {/* {hoveredEcoScore !== null && (
                      <ChartsReferenceLine
                        y={hoveredEcoScore}
                        label=""
                        lineStyle={{ stroke: "#000", strokeDasharray: "6 6", strokeWidth: 2 }}
                      />
                    )} */}
                    <AreaGradient color="#BC92F8" id={gradientId} />
                  </LineChart>
                </Box>
                {/* </Box> */}
                <Details sx={{ display: isLoading ? "none" : "block" }}>
                  <BlockCol>
                    <BackgroundDiv data-parallax-card>
                      <Block
                        sx={{
                          p: { xs: "18px", md: "24px" },
                          display: indicatorsData?.length && token?.assessments?.length ? "block" : "none",
                          backgroundColor: "back.explorerGreen",
                          borderRadius: "20px",
                          minHeight: isMobile ? "auto" : "263px",
                          maxHeight: isMobile ? "fit-content" : "fit-content",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "row", md: "row" },
                            justifyContent: "space-between",
                            mb: 2,
                            paddingBottom: "20px",
                            gap: "20px",
                            borderBottom: "1px solid white",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              alignContent: "center",
                            }}
                          >
                            <Text
                              numeric
                              sx={{
                                fontSize: { xs: "26px", md: "26px" },
                                color: "#FFF",
                                fontWeight: 700,
                                textAlign: "center",
                                display: "flex",
                                alignItems: "center",
                                gap: "20px",
                                justifyContent: "start",
                              }}
                            >
                              {/* {!isMobile && <span>ECOSCORE:</span>} */}
                              <span>{(token.ecoScore * 100).toFixed()}</span>
                            </Text>
                          </Box>

                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Button
                              variant="outlined"
                              disableFocusRipple
                              disableRipple
                              disableTouchRipple
                              disableElevation
                              style={{
                                width: "auto",
                                height: "auto",
                              }}
                              sx={{
                                mr: "12px",
                                color: selectedView === "ecoscore" ? "#2b3501" : "white",
                                border: "1px solid white",
                                textTransform: "none",
                                backgroundColor: selectedView === "ecoscore" ? "#FFF" : "transparent",
                                borderRadius: "20px",
                                "&:hover": {
                                  border: "1px solid white",
                                  animation: "none",
                                },
                              }}
                              onClick={() => setSelectedView("ecoscore")}
                            >
                              {isMobile ? "Score" : "Ecoscore"}
                            </Button>
                            <Button
                              variant="outlined"
                              disableFocusRipple
                              disableRipple
                              disableTouchRipple
                              disableElevation
                              style={{
                                width: "auto",
                                height: "auto",
                              }}
                              sx={{
                                color: selectedView === "ecosystem" ? "#2b3501" : "white",
                                border: "1px solid white",
                                textTransform: "none",
                                backgroundColor: selectedView === "ecosystem" ? "#FFF" : "transparent",
                                borderRadius: "20px",
                                "&:hover": {
                                  border: "1px solid white",
                                  animation: "none",
                                },
                              }}
                              onClick={() => setSelectedView("ecosystem")}
                            >
                              {isMobile ? "Biome" : "Biome"}
                            </Button>
                          </Box>
                        </Box>

                        {selectedView === "ecoscore" ? (
                          <Attributes>{getAttributes()}</Attributes>
                        ) : (
                          <Grid container spacing={{ xs: 2, md: "40px" }}>
                            <Grid item xs={12} md={4} my={"20px"}>
                              <Typography sx={{ color: "white", fontSize: "16px", fontWeight: "bold" }}>
                                {token.meta?.ecoSystemType
                                  ? ecoSystemSubTypeName(token.meta?.ecoSystemType.split(",")[1])
                                  : "No data"}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8} my={"20px"}>
                              <Typography sx={{ color: "white", fontSize: "16px" }}>
                                {token.meta?.ecoSystemType
                                  ? ecoSystemTypes.find(
                                      (type) =>
                                        type.name === ecoSystemSubTypeName(token?.meta?.ecoSystemType.split(",")[1])
                                    )?.description1 ||
                                    "A property with a plantation system emphasizes large-scale, single-crop farming like tea or rubber, maximizing economic returns. Such systems are efficient, supporting local economies and providing steady yields for the linked Ecofutures Token (EFT)."
                                  : "No description available"}
                              </Typography>
                            </Grid>
                          </Grid>
                        )}
                      </Block>
                    </BackgroundDiv>

                    {/* Land Features Section */}
                    <BackgroundDiv data-parallax-card>
                      <Block
                        sx={{
                          p: { xs: "18px", md: "24px" },
                          borderRadius: "20px",
                          backgroundColor: "#ffffff",
                        }}
                      >
                        {/* Tab switcher */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            mb: "24px",
                          }}
                        >
                          <Box sx={{ display: "flex", gap: "12px", alignItems: "center", justifyContent: "end" }}>
                            <Button
                              variant="outlined"
                              disableFocusRipple
                              disableRipple
                              disableTouchRipple
                              disableElevation
                              style={{
                                width: "auto",
                                height: "auto",
                              }}
                              sx={{
                                color: landFeaturesTab === "features" ? "#ffffff" : "#2b3501",
                                border: "1px solid #2b3501",
                                textTransform: "none",
                                backgroundColor: landFeaturesTab === "features" ? "#2b3501" : "transparent",
                                borderRadius: "20px",
                                "&:hover": {
                                  border: "1px solid #2b3501",
                                  animation: "none",
                                },
                              }}
                              onClick={() => setLandFeaturesTab("features")}
                            >
                              Features
                            </Button>
                            <Button
                              variant="outlined"
                              disableFocusRipple
                              disableRipple
                              disableTouchRipple
                              disableElevation
                              style={{
                                width: "auto",
                                height: "auto",
                              }}
                              sx={{
                                color: landFeaturesTab === "elevation" ? "#ffffff" : "#2b3501",
                                border: "1px solid #2b3501",
                                textTransform: "none",
                                backgroundColor: landFeaturesTab === "elevation" ? "#2b3501" : "transparent",
                                borderRadius: "20px",
                                "&:hover": {
                                  border: "1px solid #2b3501",
                                  animation: "none",
                                },
                              }}
                              onClick={() => setLandFeaturesTab("elevation")}
                            >
                              Scale
                            </Button>
                          </Box>
                          <Divider sx={{ borderColor: "#2b3501", borderBottomWidth: "1px", opacity: 1, mt: "8px" }} />
                        </Box>

                        {/* Tab content */}
                        {landFeaturesTab === "features" ? (
                          <Box>{getTokenAttributes()}</Box>
                        ) : (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: "0px" }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                py: "14px",
                                borderBottom: "0.5px solid #e0e0e0",
                              }}
                            >
                              <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: "Inter, sans-serif" }}>
                                Land size
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontFamily: "'IBM Plex Mono', monospace",
                                  color: "#2b3501",
                                  textTransform: "uppercase",
                                  textAlign: "right",
                                }}
                              >
                                {toHectares(effectiveTokenSize).toUpperCase()}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                py: "14px",
                              }}
                            >
                              <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: "Inter, sans-serif" }}>
                                Elevation
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontFamily: "'IBM Plex Mono', monospace",
                                  color: "#2b3501",
                                  textTransform: "uppercase",
                                  textAlign: "right",
                                }}
                              >
                                {meta.elevationMin}m - {meta.elevationMax}m
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Block>
                    </BackgroundDiv>
                    <Box data-parallax-card sx={{ width: "100%" }}>
                      <LandDescriptionCard description={token.meta?.description} />
                    </Box>
                  </BlockCol>
                </Details>
              </RightColDetailStack>
              {/* Media stack — slides in when the hero card flips open.
                  Tiles for every media item except the EFT image itself (index 0),
                  which the flip back-face already shows. */}
              <RightColMediaStack galleryOpen={!!heroFlipOpen} data-detail-keep>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { sm: "flex-start" },
                    gap: { xs: "8px", md: "24px" },
                  }}
                >
                  {/* Tiles column */}
                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "0px", minWidth: 0 }}>
                    {galleryItems.slice(1).map((item, idx) => (
                      <GalleryMediaTile
                        key={`${item.id}-${heroFlipOpen ? "open" : "closed"}`}
                        item={item}
                        flipIdx={idx}
                        index={idx + 1}
                        galleryOpen={heroFlipOpen}
                        onOpen={() => {
                          setActiveFlipIdx(idx);
                          setGalleryInitialFileId(item.id);
                          setGalleryOpen(true);
                        }}
                      />
                    ))}
                    {galleryItems.length <= 1 && (
                      <div
                        style={{
                          color: "rgba(255,255,255,0.45)",
                          fontFamily: "Inter, sans-serif",
                          fontSize: 13,
                          padding: "24px 0",
                        }}
                      >
                        No additional media
                      </div>
                    )}
                  </Box>
                  {/* Vertical thumb rail — right side on desktop, bottom strip on mobile */}
                  {heroFlipOpen && galleryItems.slice(1).length > 1 && (
                    <FlipThumbRail>
                      {galleryItems.slice(1).map((item, idx) => (
                        <FlipThumbItem
                          key={String(item.id)}
                          active={activeFlipIdx === idx}
                          onClick={() => scrollToFlipTile(idx)}
                          title={item.name || ""}
                          aria-label={`Go to ${item.name || "media item"}`}
                        >
                          <FlipThumbIcon>{renderFlipRailThumb(item)}</FlipThumbIcon>
                        </FlipThumbItem>
                      ))}
                    </FlipThumbRail>
                  )}
                </Box>
              </RightColMediaStack>
            </Box>
          </Box>

          <MediaGalleryModal
            open={galleryOpen}
            onClose={() => setGalleryOpen(false)}
            initialFileId={galleryInitialFileId}
            eftImage={
              token.content ? { id: `eft-${token.id}`, url: token.content, name: token.meta?.name || "EFT" } : undefined
            }
            files={[
              ...(() => {
                const verification = (token as any)?.verifications?.find((v: any) => v?.videoUrl);
                return verification?.videoUrl
                  ? [
                      {
                        id: `verification-video-${verification.id ?? 0}`,
                        url: verification.videoUrl as string,
                        name: "Verification video",
                      },
                    ]
                  : [];
              })(),
              ...(token.files || [])
                .filter(
                  (file) =>
                    file.type === "additional" || file.type === "survey_plan" || file.type === "easement_contract"
                )
                .map((file) => ({ id: file.id, url: file.url, name: file.name, type: file.type })),
            ]}
          />

          {claimMint && claimMintData && (
            <TreeClaimModalNew
              token={token}
              guardian={landGuardian ? true : false}
              id={token.etNft?.id as number}
              setId={setClaimMint}
              name={claimMintData.name}
              content={claimMintData.content}
              // Silent: a loud fetch unmounts this subtree (popup included) before the popup
              // clears claimMint, so it would reopen once the refetch lands.
              refresh={() => fetchAllData(true)}
              targetDate={claimMintData.targetDate}
            />
          )}

          {/* Offer Creation Modal */}
          {tokenData && (
            <OfferCreationModal
              token={tokenData}
              open={offerCreationModalOpen}
              setOpen={setOfferCreationModalOpen}
              onSuccess={() => {
                setOfferCreationModalOpen(false);
                fetchAllData();
              }}
            />
          )}

          {/* EFT Offer Popup — V9 Figma (node 3001:4019). The mobile/desktop split and the
              land card now live in LandActionPopup; this supplies only the panel body. */}
          <LandActionPopup
            open={offerModal}
            onClose={() => {
              setOfferModal(false);
              setNextOffer("");
              setErrorMessage("");
              setInfoMessage("");
            }}
            token={token}
            statLabel="HIGHEST OFFER"
            statValue={`$${highestOffer !== null ? formatValue(highestOffer, true, 2) : "0"}`}
            statAddress={highestOfferBuyer}
          >
            <PopupValuePill
              value={nextOffer ? `$${Number(nextOffer).toLocaleString("en-US")}` : ""}
              onChange={handleOfferAmountChange}
              variant="outlined"
              disabled={offerLoading}
              // Only flag the field once something has been typed (see the bid popup).
              error={!!errorMessage && !!nextOffer}
              placeholder="$0.00"
              inputMode="decimal"
            />
            <PopupCTA onClick={placeOffer} variant="contained" disabled={!nextOfferValid || offerLoading}>
              {offerLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Make offer"}
            </PopupCTA>
          </LandActionPopup>

          {/* Cancel Offer Confirmation Popup — the same shell with no amount field: the standing
              high offer as the heading and one CTA, the composition the mint popup keeps too.
              No detail rows here by design. */}
          <LandActionPopup
            open={cancelOfferModal}
            onClose={() => {
              if (!cancelOfferLoading) {
                setErrorMessage("");
                handleCloseCancelOffer();
              }
            }}
            token={token}
            statLabel="HIGHEST OFFER"
            statValue={`$${highestOffer !== null ? formatValue(highestOffer, true, 2) : "0"}`}
            statAddress={highestOfferBuyer}
          >
            <PopupCTA disabled={cancelOfferLoading} onClick={() => handleCancelOffer()} variant="contained">
              {cancelOfferLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Cancel listing"}
            </PopupCTA>
            {/* The strict-Figma panel has no field to carry an error ring, so a failed cancel
                (a revert, a wallet rejection) used to vanish here — the spinner stopped and
                nothing else changed. The one line under the CTA is the surface. */}
            {errorMessage && <PopupFootnote tone="error">{errorMessage}</PopupFootnote>}
          </LandActionPopup>
        </>
      )}
    </EmbeddedBoundary>
  );
};

const IdleIcon = createSvgIcon(
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">
    <path d="M140-440q-25 0-42.5-17.5T80-500q0-25 17.5-42.5T140-560h240q25 0 42.5 17.5T440-500q0 25-17.5 42.5T380-440H140Zm440 0q-25 0-42.5-17.5T520-500q0-25 17.5-42.5T580-560h240q25 0 42.5 17.5T880-500q0 25-17.5 42.5T820-440H580Z" />
  </svg>,
  "Idle"
);

// const TrusteeMiniIcon = createSvgIcon(
//   <svg width="16" height="19" viewBox="0 0 16 19" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <path
//       d="M6.91519 12.1197H9.20277V9.93969H11.4905V7.75969H9.20277V5.57969H6.91519V7.75969H4.62748V9.93969H6.91519V12.1197ZM8.05898 18.1097C5.91824 17.6097 4.14484 16.4497 2.73866 14.6397C1.33249 12.8297 0.629395 10.8097 0.629395 8.59969V3.39969L8.05898 0.679688L15.4886 3.39969V8.59969C15.4886 10.8097 14.7855 12.8297 13.3793 14.6397C11.9731 16.4497 10.1997 17.6097 8.05898 18.1097Z"
//       fill="black"
//     />
//   </svg>,
//   "TrusteeMini"
// );
const Attributes = styled("div")`
  display: grid;
  column-gap: 10px;
  row-gap: 16px;
  grid-template-columns: repeat(2, 1fr);

  @media (max-width: 560px) {
    grid-template-columns: repeat(1, 1fr);
    row-gap: 0;

    & > div:last-child > div {
      padding-bottom: 0 !important;
      border-bottom: none !important;
    }
  }
`;

const AttributeWrap = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: start;
  color: white;
  &:first-of-type {
    padding-left: 0;
  }
  div {
    text-transform: capitalize;
  }
`;

const Attribute = ({
  type,
  color,
  value,
  assessmentsMap,
  zoneScores,
}: {
  type: AdvanceType | string;
  color: string;
  value: any;
  assessmentsMap: any;
  zoneScores?: ZoneScoreMarker[];
}) => {
  // InfoPopup state
  const [infoPopupOpen, setInfoPopupOpen] = useState(false);
  const [infoPopupContent, setInfoPopupContent] = useState<{
    type: string;
    level: string;
    description: string;
    parameterDescriptions?: IndicatorParameterDescription[];
    zoneScores: ZoneScoreMarker[];
  } | null>(null);
  const navigator = useNavigate();
  // 0-0.5 Barren = 0
  // 0.6-1.5 Marginal = 1
  // 1.6-2.5 Moderate = 2
  // 2.6-3.5 Abundant = 3
  // 3.6-4.5 Rich = 4
  // 4.6-5 Pristine = 5

  const method = (up: boolean) => (up ? Math.floor : Math.ceil);
  const _val = method(value < 4.6)(value);

  const [activeButton, setActiveButton] = useState<string | null>(null);

  const handleClick = (type: string, color: string) => () => {
    // For assessmentsMap, we should use the first item since it's already filtered
    const assessment = assessmentsMap.length > 0 ? assessmentsMap[0] : null;
    console.log("Attribute click - type:", type, "assessment:", assessment);
    setInfoPopupContent({
      type,
      level: color,
      description: assessment ? assessment.valueDescription || "" : "",
      parameterDescriptions: assessment?.parameterDescriptions,
      zoneScores: zoneScores || [],
    });
    setInfoPopupOpen(true);
    setActiveButton(type);
  };

  return (
    <div>
      <AttributeWrap
        sx={{
          pt: { xs: "18px", md: "24px" },
          display: "flex",
          flexDirection: { xs: "row", sm: "row" },
          alignItems: { xs: "center", sm: "flex-start" },
          width: "100%",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          "@media (max-width:540px)": {
            paddingBottom: "18px",
            borderBottom: "1px solid #999999",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            justifyContent: "space-between",
            width: "100%",
            textAlign: { xs: "center", sm: "left" },
            marginLeft: { xs: "0px", sm: "0px" },
            flex: 1,
            minHeight: 0,
            alignSelf: "stretch",
          }}
        >
          <Box>
            <Text bold sx={{ textTransform: "capitalize", fontSize: "14px" }}>
              {type}
            </Text>
          </Box>
          <Box>
            <Button
              onClick={handleClick(type, color)}
              sx={{
                p: "15px 20px",
                borderRadius: "24px",
                width: "120px",
                minWidth: "120px",
                whiteSpace: "nowrap",
                border: "1px solid #ffffff",
                color: activeButton === type ? "transparent" : "#ffffff",
                backgroundColor: activeButton === type ? "#ffffff" : "transparent",
                "&:hover": {
                  border: "1px solid #ffffff",
                  backgroundColor: "#ffffff",
                  color: "back.explorerGreen",
                  "& .MuiTypography-root": {
                    color: "back.explorerGreen",
                  },
                },
              }}
            >
              <Text
                sx={{
                  fontSize: "12px",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: activeButton === type ? "back.explorerGreen" : "inherit",
                  "&:hover": {
                    color: "back.explorerGreen",
                  },
                }}
              >
                {color}
              </Text>
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "end", sm: "center" },
            width: "100%",
            overflow: "visible",
            marginRight: { xs: "20px", sm: "0px" },
          }}
        >
          <EcoscoreIcon
            score={_val as 0 | 1 | 2 | 3 | 4 | 5}
            category={type as "soil" | "air" | "water" | "succession" | "biodiversity" | "structure" | "forest"}
            sx={{
              width: { xs: "90px", sm: "80px", md: "115px" },
              height: { xs: "90px", sm: "80px", md: "120px" },
              overflow: "visible",
            }}
          />
          {/* <IconButton
            onClick={handleClick(type, color)}
            sx={{
              p: 0,
              width: { xs: "24px", sm: "24px" },
              height: { xs: "24px", sm: "24px" },
            }}
          >
            <InfoIcon sx={{ fontSize: 24, color: "white" }} />
          </IconButton> */}
        </Box>
      </AttributeWrap>
      {/* InfoPopup rendered here */}
      {infoPopupContent && (
        <InfoPopup
          value={_val as 0 | 1 | 2 | 3 | 4 | 5}
          type={infoPopupContent.type}
          level={infoPopupContent.level}
          description={infoPopupContent.description}
          parameterDescriptions={infoPopupContent.parameterDescriptions}
          zoneScores={infoPopupContent.zoneScores}
          open={infoPopupOpen}
          onClose={() => {
            setInfoPopupOpen(false);
            setActiveButton(null);
          }}
          navigator={navigator}
        />
      )}
    </div>
  );
};

// ——————————————————————————————————————————————————————
// Hero card gallery flip — styled components
// ——————————————————————————————————————————————————————

// The inner rotating element. Must be a plain styled div (NOT a MUI Box) so
// that no sx transform intercepts the rotateY — MUI Box can compose its own
// transform that clobbers preserve-3d children.
const HeroFlipInner = styled("div")<{ flipped: boolean }>(({ flipped }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  transformStyle: "preserve-3d",
  transition: `transform ${HERO_FLIP_MS}ms ${HERO_FLIP_EASE}`,
  // perspective() embedded in the transform rather than as a CSS property on
  // BackgroundDiv: sticky positioning causes Chrome to compute the CSS
  // `perspective` property in original layout coordinates, not the visual
  // (sticky-scrolled) position, making the flip appear stretched when scrolled.
  // perspective() in the transform chain always uses the element's own center.
  transform: flipped ? "perspective(1400px) rotateY(180deg)" : "perspective(1400px) rotateY(0deg)",
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
}));

// Front face sits in normal flow (drives the height); back face is absolute.
// `backface-visibility: hidden` is unreliable on mobile — after the flip
// completes we also drive opacity to 0 (with a delay matching the transition)
// so the image can't bleed through on devices that don't honour backface-visibility.
const HeroFlipFront = styled("div")<{ flipped: boolean }>(({ flipped }) => ({
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
  position: "absolute",
  inset: 0,
  opacity: flipped ? 0 : 1,
  // Delay the hide until after the flip finishes; reveal instantly when returning.
  transition: flipped ? `opacity 0s ${HERO_FLIP_MS}ms` : "opacity 0s 0ms",
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
}));

// The overlay layer (badge, chip, name, stats) that fades out before the card
// rotates far enough for text to appear mirrored.
const HeroFlipFrontOverlays = styled("div")<{ flipped: boolean }>(({ flipped }) => ({
  position: "absolute",
  inset: 0,
  opacity: flipped ? 0 : 1,
  // When opening: fade out quickly so overlays are gone before mid-rotation.
  // When closing: fade back in after the card has cleared 90° on its way back.
  transition: flipped ? `opacity 250ms ease` : `opacity 350ms ease ${HERO_OVERLAY_RETURN_DELAY_MS}ms`,
  pointerEvents: flipped ? "none" : "auto",
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    opacity: flipped ? 0 : 1,
  },
}));

// Back face: rotated 180° so it faces the camera when HeroFlipInner is at 180°.
// Position absolute over the same area as the front face.
// On mobile the front face Box carries marginY: 7.5%, so we match that offset
// here so both faces occupy identical visual space.
//
// `pointer-events: none` when not flipped is critical: backface-visibility:hidden
// hides the element visually but does NOT reliably block pointer events in Chrome.
// Without this, clicking the front face to open the flip also triggers the back
// face onClick (which calls onFlipClose), causing an immediate open→close bounce.
const HeroFlipBack = styled("div")<{ flipped: boolean }>(({ flipped }) => ({
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
  position: "absolute",
  inset: 0,
  transform: "rotateY(180deg)",
  overflow: "hidden",
  borderRadius: "20px",
  pointerEvents: flipped ? "auto" : "none",
  "@media (prefers-reduced-motion: reduce)": {},
}));

// ——————————————————————————————————————————————————————
// Hero card pointer tilt — styled components
// ——————————————————————————————————————————————————————
// transitions.dev "Card hover tilt", in Emotion. The pointer is tracked on the
// OUTER BackgroundDiv (flat, never transforms — see hooks/useCardTilt) and this
// is the element that rotates. It wraps the gallery flip so the whole flipping
// card leans; the two rotations live on separate elements so the 850ms flip
// and the 400ms tilt follow never fight over one `transform` transition.
//
// Deliberately NOT `overflow: hidden` / `preserve-3d` like the reference: under
// perspective the near edge of the mid-flip card extends past its own box, and
// a clip here would shear it off. Staying at the default `transform-style:
// flat` (which the reference's overflow:hidden forced anyway) also keeps the
// glare painting above both faces by DOM order rather than by 3D depth. The
// glare clips itself to the card radius instead (HeroTiltGlareClip).
const HeroTiltCard = styled("div")({
  position: "relative",
  width: "100%",
  height: "100%",
  // Rest pose. The hook writes the live pose inline with the same three
  // functions (perspective / rotateX / rotateY) so the return interpolates
  // pairwise; it clears the inline value on leave to ease back to this.
  transform: "perspective(var(--tilt-perspective)) rotateX(0deg) rotateY(0deg)",
  // Slow return to flat on leave; while the pointer moves the hook sets
  // [data-tilting], which swaps in the short follow so the lean tracks 1:1.
  transition: "transform var(--tilt-return) var(--tilt-return-ease)",
  "&[data-tilting]": {
    transition: "transform var(--tilt-follow) var(--tilt-follow-ease)",
  },
  "@media (prefers-reduced-motion: reduce)": {
    transform: "none !important",
    transition: "none !important",
  },
});

// Clips the oversized glare sheet to the card's rounded corners. Sits after
// HeroFlipInner in the DOM so it paints above both faces; pointer-events off so
// it never takes the hover away from the wrapper.
const HeroTiltGlareClip = styled("div")({
  position: "absolute",
  inset: 0,
  borderRadius: "20px",
  overflow: "hidden",
  pointerEvents: "none",
});

// Cursor-tracked glare: layered soft circles that add like light (screen
// blend) at the pointer. The reference moves the gradient centre through
// `background`, which repaints the whole sheet on every pointermove; here the
// gradients are painted ONCE at the centre of a sheet twice the card's size and
// the sheet is translated under the pointer (inline `transform`, written by the
// hook) — compositor-only (transform + opacity), as the project's motion rules
// require. 2D translate rather than translate3d on purpose: a 3D transform
// would pin a permanent compositor layer the size of two cards (on mobile too,
// where the glare never shows); the hook promotes it with will-change only for
// the length of a hover.
const HeroTiltGlare = styled("div")({
  position: "absolute",
  top: "-50%",
  left: "-50%",
  width: "200%",
  height: "200%",
  opacity: 0,
  mixBlendMode: "screen",
  background: [
    "radial-gradient(circle 95px at 50% 50%, rgba(255,255,255,0.48), rgba(255,255,255,0.06) 52%, rgba(255,255,255,0) 84%)",
    "radial-gradient(circle 200px at 50% 50%, rgba(255,255,255,0.22), rgba(255,255,255,0.04) 58%, rgba(255,255,255,0) 78%)",
    "radial-gradient(circle 360px at 50% 50%, rgba(255,255,255,0.10), rgba(255,255,255,0) 88%)",
  ].join(", "),
  transition: "opacity var(--tilt-glare-fade) var(--tilt-glare-ease)",
  "[data-hover] &": {
    opacity: "var(--tilt-glare-opacity)",
  },
  // The hook never starts a hover under reduced motion; this just makes the
  // sheet inert if the preference flips mid-session.
  "@media (prefers-reduced-motion: reduce)": {
    display: "none",
  },
});

// ——————————————————————————————————————————————————————
// Right column detail / media stack swap
// ——————————————————————————————————————————————————————

// Wraps the existing detail cards. Fades out (opacity only — no transform, to
// avoid creating a new containing block for any position:fixed descendants).
// After fade-out, height collapses to 0 so the invisible cards don't inflate
// scroll height while the media stack is showing.
const RightColDetailStack = styled("div")<{ galleryOpen: boolean }>(({ galleryOpen }) => ({
  opacity: galleryOpen ? 0 : 1,
  pointerEvents: galleryOpen ? "none" : "auto",
  overflow: galleryOpen ? "hidden" : "visible",
  height: galleryOpen ? 0 : "auto",
  // Collapse after fade-out completes (300ms); restore instantly on close.
  transition: galleryOpen ? `opacity 300ms ease, height 0s 300ms, overflow 0s 300ms` : `opacity 350ms ease 200ms`,
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    opacity: galleryOpen ? 0 : 1,
    height: galleryOpen ? 0 : "auto",
    overflow: galleryOpen ? "hidden" : "visible",
  },
}));

// Media tiles. In normal flow so it drives the right column's scroll height
// when the gallery is open; collapses to height:0 when closed so it doesn't
// add hidden space behind the detail stack.
const RightColMediaStack = styled("div")<{ galleryOpen: boolean }>(({ galleryOpen, theme }) => ({
  height: galleryOpen ? "auto" : 0,
  overflow: galleryOpen ? "visible" : "hidden",
  paddingTop: galleryOpen ? "12.5%" : 0,
  // Desktop: same clamped top offset as the hero card so the first media tile
  // stays top-aligned with the flipped hero.
  [theme.breakpoints.up("md")]: {
    paddingTop: galleryOpen ? HERO_TOP_MARGIN_MD : 0,
  },
  paddingBottom: galleryOpen ? "5%" : 0,
  display: "flex",
  flexDirection: "column",
  opacity: galleryOpen ? 1 : 0,
  transform: galleryOpen ? "none" : "translate3d(0, 18px, 0)",
  pointerEvents: galleryOpen ? "auto" : "none",
  transition: galleryOpen
    ? `opacity 400ms ease 350ms, transform 500ms ${HERO_FLIP_EASE} 350ms`
    : `opacity 300ms ease, transform 400ms ${HERO_FLIP_EASE}`,
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    opacity: galleryOpen ? 1 : 0,
    transform: "none",
    height: galleryOpen ? "auto" : 0,
    overflow: galleryOpen ? "visible" : "hidden",
  },
}));

// ——————————————————————————————————————————————————————
// Gallery chrome: fixed title bar + thumbnail strip
// ——————————————————————————————————————————————————————

// Vertical thumbnail rail shown alongside the flip media stack on the right
// side. Sticks within the embedded scroll overlay as the user scrolls the
// tiles. On mobile (< sm) it collapses to a horizontal dot strip at the top.
const FlipThumbRail = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flexShrink: 0,
  width: 52,
  position: "sticky",
  top: "50%",
  transform: "translateY(-50%)",
  alignSelf: "flex-start",
  [theme.breakpoints.down("sm")]: {
    // Float the strip above the tiles on mobile. The rail is rendered after the
    // tiles column in the DOM (so it sits on the right on desktop); order:-1
    // pulls it to the top once the parent stacks into a column.
    order: -1,
    flexDirection: "row",
    width: "100%",
    position: "static",
    justifyContent: "center",
    paddingBottom: "8px",
    gap: "6px",
  },
}));

const FlipThumbItem = styled("button")<{ active: boolean }>(({ active, theme }) => ({
  flexShrink: 0,
  width: 44,
  height: 44,
  borderRadius: 10,
  border: active ? "2px solid rgba(255,255,255,0.9)" : "2px solid rgba(255,255,255,0.22)",
  backgroundColor: active ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.38)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  overflow: "hidden",
  cursor: "pointer",
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(255,255,255,0.72)",
  transition: "border-color 0.15s, background-color 0.15s",
  "&:hover": {
    border: "2px solid rgba(255,255,255,0.6)",
    backgroundColor: "rgba(255,255,255,0.25)",
  },
  [theme.breakpoints.down("sm")]: {
    width: 36,
    height: 36,
    borderRadius: 8,
  },
}));

const FlipThumbIcon = styled("div")({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& img": { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  "& svg": { fontSize: "20px !important", color: "rgba(255,255,255,0.8)" },
});

// The title bar: ✕ | asset name | "n OF m". Fixed so it sits above the
// EmbeddedBoundary scroll container without being clipped by it.

// Card-style media item — matches the visual weight of LandSummaryCardRoot.
const MediaCardRoot = styled("div")({
  width: "100%",
  backgroundColor: "#f5f5f3",
  borderRadius: "20px",
  overflow: "hidden",
  cursor: "pointer",
  transform: "scale(1)",
  transition:
    "transform 550ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 550ms cubic-bezier(0.16, 1, 0.3, 1), filter 550ms cubic-bezier(0.16, 1, 0.3, 1)",
  "&:hover": {
    transform: "scale(1.015)",
    boxShadow: "0 10px 32px rgba(0,0,0,0.15)",
    filter: "brightness(0.93)",
  },
  "&:focus-visible": { outline: "2px solid #8cc63f", outlineOffset: 2 },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

// Full-width 16:9 preview area used for video items.
const MediaCardVideoPreview = styled("div")({
  width: "100%",
  aspectRatio: "16/9",
  backgroundColor: "#1a2510",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(255,255,255,0.7)",
  "& img": { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" },
  "& svg": { position: "relative", zIndex: 1, fontSize: 48, filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.5))" },
});

// Full-width image preview — lets the image define its own height so nothing
// is cropped, stretched, or squeezed.
const MediaCardImagePreview = styled("div")({
  width: "100%",
  overflow: "hidden",
  "& img": { display: "block", width: "100%", height: "auto" },
});

// Body row shared by both video and doc cards.
const MediaCardBody = styled("div")({
  padding: "18px",
  display: "flex",
  alignItems: "center",
  gap: "14px",
  fontFamily: "Inter, sans-serif",
});

// Rounded icon tile for doc cards (survey plan / contract / PDF).
const MediaCardDocIcon = styled("div")({
  width: 44,
  height: 44,
  flexShrink: 0,
  backgroundColor: "#e6ede0",
  borderRadius: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#555",
  "& svg": { fontSize: 22 },
});

const MediaCardInfo = styled("div")({
  flex: 1,
  minWidth: 0,
});

const MediaCardName = styled("div")({
  fontFamily: "monospace",
  textTransform: "uppercase",
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "18px",
  color: "#000",
});

// const MediaCardType = styled("div")({
//   fontFamily: "'IBM Plex Mono', monospace",
//   fontSize: 10,
//   letterSpacing: "0.08em",
//   color: "#999",
//   textTransform: "uppercase",
//   marginTop: 3,
// });

const MediaCardArrow = styled("div")({
  flexShrink: 0,
  fontSize: 16,
  color: "#aaa",
  paddingLeft: 4,
});

const getMediaCardType = (item: MediaFile): string => {
  const isPdf = /\.pdf(\?.*)?$/i.test(item.url) || item.name?.toLowerCase().endsWith(".pdf");
  const isVideo =
    /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)(\?.*)?$/i.test(item.url) ||
    /(?:youtube\.com|youtu\.be|vimeo\.com)/i.test(item.url);
  if (item.type === "survey_plan") return "Survey plan";
  if (item.type === "easement_contract") return "Ecological covenant";
  if (isVideo) return "Verification Video";
  if (isPdf) return "Additional Document";
  return "Additional Media";
};

const isVideoUrl = (url: string) =>
  /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)(\?.*)?$/i.test(url) || /(?:youtube\.com|youtu\.be|vimeo\.com)/i.test(url);

function renderFlipRailThumb(file: MediaFile) {
  const isPdfFile = /\.pdf(\?.*)?$/i.test(file.url) || file.name?.toLowerCase().endsWith(".pdf");
  const isVidFile =
    /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)(\?.*)?$/i.test(file.url) ||
    /(?:youtube\.com|youtu\.be|vimeo\.com)/i.test(file.url);
  const isImgFile =
    /\.(png|jpe?g|gif|webp|avif)(\?.*)?$/i.test(file.url) || /\.(png|jpe?g|gif|webp|avif)$/i.test(file.name || "");
  if (isImgFile) {
    return (
      <img
        src={file.url}
        alt={file.name || ""}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    );
  }
  if (isPdfFile) {
    if (file.type === "survey_plan") return <SurveyIcon />;
    if (file.type === "easement_contract") return <ContractIcon />;
    return <PictureAsPdfIcon sx={{ fontSize: 20 }} />;
  }
  if (isVidFile) {
    return <PlayCircleOutlineIcon sx={{ fontSize: 20 }} />;
  }
  return null;
}

// Renders the back face of the hero flip card. When `item` is a video it shows
const GalleryMediaTile: React.FC<{
  item: MediaFile;
  index: number;
  flipIdx?: number;
  galleryOpen: boolean;
  onOpen: () => void;
}> = ({ item, galleryOpen, onOpen, flipIdx }) => {
  const [playing, setPlaying] = useState(false);

  // Reset the player when the gallery closes so the tile starts from the
  // thumbnail on every re-open. An active iframe creates a compositing layer
  // that breaks the tile's opacity animation when the flip replays.
  useEffect(() => {
    if (!galleryOpen) setPlaying(false);
  }, [galleryOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPdf = /\.pdf(\?.*)?$/i.test(item.url) || item.name?.toLowerCase().endsWith(".pdf");
  const isVideo = isVideoUrl(item.url);
  const isImage =
    /\.(png|jpe?g|gif|webp|avif)(\?.*)?$/i.test(item.url) || /\.(png|jpe?g|gif|webp|avif)$/i.test(item.name || "");
  const ytMatch = item.url.match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
  );
  const isDirectVideo = /\.(mp4|webm|ogg)(\?.*)?$/i.test(item.url);

  const docIcon = () => {
    if (item.type === "survey_plan") return <SurveyIcon />;
    if (item.type === "easement_contract") return <ContractIcon />;
    return <PictureAsPdfIcon />;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isVideo) {
      e.stopPropagation();
      setPlaying(true);
    } else if (!isPdf) {
      onOpen();
    }
  };

  return (
    <MediaCardRoot
      data-parallax-card
      data-flip-tile={flipIdx !== undefined ? String(flipIdx) : undefined}
      role={isPdf ? undefined : "button"}
      tabIndex={galleryOpen && !isPdf ? 0 : -1}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (isVideo) setPlaying(true);
          else if (!isPdf) onOpen();
        }
      }}
      style={{
        cursor: isPdf || playing ? "default" : "pointer",
      }}
    >
      <MediaCardBody>
        {!isVideo && !isPdf && !isImage && <MediaCardDocIcon>{docIcon()}</MediaCardDocIcon>}
        <MediaCardInfo>
          {/* <MediaCardName>{item.name || "Untitled"}</MediaCardName> */}
          <MediaCardName>{getMediaCardType(item)}</MediaCardName>
        </MediaCardInfo>
        {!playing && !isPdf && <MediaCardArrow>↗</MediaCardArrow>}
      </MediaCardBody>
      {isImage && (
        <MediaCardImagePreview>
          <img src={item.url} alt={item.name || ""} />
        </MediaCardImagePreview>
      )}
      {isPdf && (
        // Flatten the PdfPreview's own border-radius so it sits flush inside
        // MediaCardRoot's rounded corners without a visible gap before the label.
        <Box sx={{ "& > *": { borderRadius: "0 0 20px 20px !important", padding: "0px" } }}>
          <PdfPreview fileUrl={item.url} scrollable />
        </Box>
      )}
      {isVideo && (
        <MediaCardVideoPreview>
          {playing ? (
            ytMatch ? (
              <iframe
                title={item.name || "Video"}
                src={`https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
              />
            ) : isDirectVideo ? (
              <video
                src={item.url}
                autoPlay
                controls
                playsInline
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : null
          ) : (
            <>
              {ytMatch && <img src={`https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`} alt={item.name || ""} />}
              <PlayCircleOutlineIcon />
            </>
          )}
        </MediaCardVideoPreview>
      )}
    </MediaCardRoot>
  );
};

const Background = styled("div")`
  position: relative;
  display: flex;
  width: 100%;
  border-radius: 20px;
  justify-content: space-around;
  align-items: stretch;
  overflow: hidden;
  border-bottom: none;
  max-height: auto;

  @media (max-width: 768px) {
    max-height: unset;
    height: auto;
    flex-direction: column;
  }
`;

const Image = styled("img")`
  width: 50%;
  display: block;
  object-fit: cover;
  object-position: center;
  @media (max-width: 560px) {
    width: 100%;
    height: 320px;
    object-fit: cover;
    object-position: center;
  }
`;

const Details = styled("div")`
  display: flex;
  flex-direction: column;
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: 600;
  }
  blockquote {
    font-style: italic;
    margin: 0 0 20px;
    background: rgba(255, 255, 255, 0.1);
    padding: 12px 24px;
    border-radius: 8px;
  }
`;
// const AdditionalFiles = styled("div")`
//   display: flex;
//   gap: 6px;
//   flex-wrap: wrap;

//   @media (max-width: 768px) {
//     display: grid;
//     grid-template-columns: 1fr 1fr;
//     gap: 8px;
//   }
// `;

// const Section = styled("div")`
//   border: 1px solid ${({ theme }) => theme.palette.border.dark};
//   display: flex;
//   flex-direction: column-reverse;
//   justify-content: space-between;
//   color: black;
//   background-color: ${({ theme }) => theme.palette.back.green};

//   @media (min-width: 992px) {
//     &:not(:last-of-type) {
//       border-right: 0;
//     }
//   }
//   @media (max-width: 992px) {
//     border-left: 0;
//     border-right: 0;
//     &:not(:first-of-type) {
//       border-top: 0;
//     }
//   }
// `;

const Block = styled("div")`
  padding: 24px;
  white-space: pre-line;
`;

export const CenteredContainer = styled("div")`
  width: 100%;
  display: flex;
  z-index: 1000;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

export const PopUpText = styled("div")`
  color: #000;
  text-align: right;
  font-family: "monospace", monospace;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-transform: uppercase;
`;

// const ContractTicker = styled("div")`
//   * {
//     font-size: 16px !important;
//     margin-left: 4px;
//     font-family: inherit !important;
//   }

//   @media (max-width: 768px) {

//      margin-left: 4px;
//     font-size: 14px !important;
//     display: block;
//     width: 100%;
//     // white-space: normal;
//     word-wrap: break-word;
//     overflow-wrap: break-word;
//   }
// `;
const BlockCol = styled("div")`
  display: flex;
  flex-direction: column;

  @media (max-width: 1200px) {
    //flex-direction: row;
  }
`;

export const People = styled("div")`
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(auto-fill, 32%);
  grid-column-gap: 6px;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;
export const Person = styled("div")`
  border: 1px solid #3d3d3d;
  border-radius: 16px;
  padding: 6px 12px;

  @media (max-width: 900px) {
    padding: 10px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  h6 {
    display: flex;
    align-items: center;

    svg {
      margin-right: 6px;
    }
  }
`;

const Tags = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 24px;

  span {
    display: flex;
    background-color: ${({ theme }) =>
      theme.palette.mode === "dark" ? theme.palette.text.disabled : theme.palette.background.paper};
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 14px;
    padding: 12px;
    border-radius: 16px;
    text-transform: capitalize;
    transition: all 0.3s ease;

    &:hover {
      color: white;
      background-color: ${({ theme }) => theme.palette.back.explorerGreen};
    }
  }
  @media (max-width: 560px) {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    span {
      width: auto;
    }
  }

  @media (max-width: 425px) {
    span {
      min-width: auto;
      padding: 6px 12px;
    }
  }
`;

// const arrowStyles = {
//   position: "absolute",
//   zIndex: 2,
//   top: "calc(50% - 15px)",
//   width: 30,
//   height: 30,
//   cursor: "pointer",
//   backgroundColor: "rgba(255, 255, 255, 0.8)",
//   border: "none",
//   borderRadius: "50%",
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "center",
// };

export default LandPage;
